<template>
  <div class="container-fluid mt-3 master-report-page">

    <!-- Page Header -->
    <div class="body-title mb-3">
      <b-row class="no-gutters align-items-center">
        <b-col>
          <h5 class="dash-heading">Master Report</h5>
        </b-col>
      </b-row>
    </div>

    <!-- Filter Card -->
    <b-card class="mb-3 report-filter-card" no-body>
      <div class="filter-card-header d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <i class="fas fa-sliders-h text-primary mr-2"></i>
          <span class="font-weight-bold text-dark">Search & Filter Criteria</span>
        </div>
        <span class="badge badge-light border text-muted font-weight-normal py-1 px-2">
          {{ activeFilterCount }} Active Filter{{ activeFilterCount === 1 ? '' : 's' }}
        </span>
      </div>

      <b-card-body class="p-3">
        <b-row>
          <!-- Primary Selection Row -->
          <b-col md="4">
            <b-form-group label="Report Type" label-size="sm" class="filter-group">
              <b-form-select
                v-model="selectedReportType"
                :options="reportTypeOptions"
                value-field="value"
                text-field="text"
                size="sm"
                class="modern-input"
              ></b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="8" v-if="reportTypeConfig.showSubCompany">
            <b-form-group label="Sub Company (उपकंपनी)" label-size="sm" class="filter-group">
              <b-form-select
                v-model="selectedCompanyID"
                :options="subCompanies"
                value-field="id"
                text-field="name"
                size="sm"
                class="modern-input"
              >
                <template v-slot:first>
                  <b-form-select-option :value="null">All Companies</b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="4" v-if="reportTypeConfig.showLoanType">
            <b-form-group label="Loan Type" label-size="sm" class="filter-group">
              <b-form-select
                v-model="selectedLoanType"
                :options="loanTypeOptions"
                value-field="value"
                text-field="text"
                size="sm"
                class="modern-input"
              ></b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="4" v-if="reportTypeConfig.showLoanType">
            <b-form-group label="Scheme" label-size="sm" class="filter-group">
              <b-form-select
                v-model="selectedSchemeName"
                :options="schemeFilterOptions"
                value-field="value"
                text-field="text"
                size="sm"
                class="modern-input"
              ></b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="4" v-if="reportTypeConfig.showRegion">
            <b-form-group label="Region (विभाग)" label-size="sm" class="filter-group">
              <b-form-select v-model="selectedRegion" :options="regionOptions" size="sm" class="modern-input"></b-form-select>
            </b-form-group>
          </b-col>

          <!-- District Multi-Select Dropdown -->
          <b-col md="4" v-if="reportTypeConfig.showDistrict">
            <b-form-group label="District" label-size="sm" class="filter-group">
              <b-dropdown
                size="sm"
                variant="outline-secondary"
                class="w-100 modern-filter-dropdown"
                menu-class="p-2 shadow-lg modern-filter-menu"
                no-flip
                no-caret
              >
                <template #button-content>
                  <div class="d-flex align-items-center justify-content-between w-100 text-left">
                    <span class="text-truncate filter-display-val">
                      <b-badge v-if="selectedDistrictIDs.length > 0" variant="primary" class="mr-1 pill-count">{{ selectedDistrictIDs.length }}</b-badge>
                      {{ districtButtonLabel }}
                    </span>
                    <i class="fas fa-chevron-down text-muted ml-2 chevron-icon"></i>
                  </div>
                </template>

                <div class="dropdown-search-box mb-2" @click.stop>
                  <b-input-group size="sm">
                    <b-input-group-prepend is-text class="search-prep">
                      <i class="fas fa-search text-muted small"></i>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model.trim="districtSearch"
                      placeholder="Search district..."
                      class="filter-search-input"
                    ></b-form-input>
                    <b-input-group-append v-if="districtSearch">
                      <b-button size="sm" variant="light" class="btn-clear-search" @click="districtSearch = ''">×</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>

                <div class="d-flex justify-content-between align-items-center px-1 mb-2 filter-quick-bar" @click.stop>
                  <div>
                    <button type="button" class="btn btn-xs btn-outline-primary py-0 px-2 mr-1" @click="selectAllDistricts">All</button>
                    <button type="button" class="btn btn-xs btn-outline-secondary py-0 px-2" @click="clearSelectedDistricts">Clear</button>
                  </div>
                  <small class="text-muted">{{ filteredDistrictOptions.length }} items</small>
                </div>

                <div class="filter-dropdown-scroll" @click.stop>
                  <div
                    v-for="opt in filteredDistrictOptions"
                    :key="opt.value"
                    class="filter-item-row"
                    :class="{ 'row-active': selectedDistrictIDs.includes(opt.value) }"
                  >
                    <b-form-checkbox
                      v-model="selectedDistrictIDs"
                      :value="opt.value"
                      @change="onDistrictChanged"
                      class="m-0 custom-checkbox-sm"
                    >
                      <span class="filter-item-text" :title="opt.text">{{ opt.text }}</span>
                    </b-form-checkbox>
                  </div>
                  <div v-if="filteredDistrictOptions.length === 0" class="text-muted text-center py-3 small">
                    No districts found
                  </div>
                </div>
              </b-dropdown>
            </b-form-group>
          </b-col>

          <!-- Bank Multi-Select Dropdown -->
          <b-col md="4" v-if="reportTypeConfig.showBank">
            <b-form-group label="Bank" label-size="sm" class="filter-group">
              <b-dropdown
                size="sm"
                variant="outline-secondary"
                class="w-100 modern-filter-dropdown"
                menu-class="p-2 shadow-lg modern-filter-menu"
                no-flip
                no-caret
              >
                <template #button-content>
                  <div class="d-flex align-items-center justify-content-between w-100 text-left">
                    <span class="text-truncate filter-display-val">
                      <b-badge v-if="selectedBankIDs.length > 0" variant="primary" class="mr-1 pill-count">{{ selectedBankIDs.length }}</b-badge>
                      {{ bankButtonLabel }}
                    </span>
                    <i class="fas fa-chevron-down text-muted ml-2 chevron-icon"></i>
                  </div>
                </template>

                <div class="dropdown-search-box mb-2" @click.stop>
                  <b-input-group size="sm">
                    <b-input-group-prepend is-text class="search-prep">
                      <i class="fas fa-search text-muted small"></i>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model.trim="bankSearch"
                      placeholder="Search bank..."
                      class="filter-search-input"
                    ></b-form-input>
                    <b-input-group-append v-if="bankSearch">
                      <b-button size="sm" variant="light" class="btn-clear-search" @click="bankSearch = ''">×</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>

                <div class="d-flex justify-content-between align-items-center px-1 mb-2 filter-quick-bar" @click.stop>
                  <div>
                    <button type="button" class="btn btn-xs btn-outline-primary py-0 px-2 mr-1" @click="selectAllBanks">All</button>
                    <button type="button" class="btn btn-xs btn-outline-secondary py-0 px-2" @click="clearSelectedBanks">Clear</button>
                  </div>
                  <small class="text-muted">{{ filteredBankOptions.length }} items</small>
                </div>

                <div class="filter-dropdown-scroll" @click.stop>
                  <div
                    v-for="opt in filteredBankOptions"
                    :key="opt.value"
                    class="filter-item-row"
                    :class="{ 'row-active': selectedBankIDs.includes(opt.value) }"
                  >
                    <b-form-checkbox
                      v-model="selectedBankIDs"
                      :value="opt.value"
                      class="m-0 custom-checkbox-sm"
                    >
                      <span class="filter-item-text" :title="opt.text">{{ opt.text }}</span>
                    </b-form-checkbox>
                  </div>
                  <div v-if="filteredBankOptions.length === 0" class="text-muted text-center py-3 small">
                    No banks found
                  </div>
                </div>
              </b-dropdown>
            </b-form-group>
          </b-col>

          <!-- Status Multi-Select Dropdown -->
          <b-col md="4" v-if="reportTypeConfig.showStatus">
            <b-form-group label="Status" label-size="sm" class="filter-group">
              <b-dropdown
                size="sm"
                variant="outline-secondary"
                class="w-100 modern-filter-dropdown"
                menu-class="p-2 shadow-lg modern-filter-menu"
                no-flip
                no-caret
              >
                <template #button-content>
                  <div class="d-flex align-items-center justify-content-between w-100 text-left">
                    <span class="text-truncate filter-display-val">
                      <b-badge v-if="selectedStatusIds.length > 0" variant="primary" class="mr-1 pill-count">{{ selectedStatusIds.length }}</b-badge>
                      {{ statusButtonLabel }}
                    </span>
                    <i class="fas fa-chevron-down text-muted ml-2 chevron-icon"></i>
                  </div>
                </template>

                <div class="dropdown-search-box mb-2" @click.stop>
                  <b-input-group size="sm">
                    <b-input-group-prepend is-text class="search-prep">
                      <i class="fas fa-search text-muted small"></i>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model.trim="statusSearch"
                      placeholder="Search status..."
                      class="filter-search-input"
                    ></b-form-input>
                    <b-input-group-append v-if="statusSearch">
                      <b-button size="sm" variant="light" class="btn-clear-search" @click="statusSearch = ''">×</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>

                <div class="d-flex justify-content-between align-items-center px-1 mb-2 filter-quick-bar" @click.stop>
                  <div>
                    <button type="button" class="btn btn-xs btn-outline-primary py-0 px-2 mr-1" @click="selectAllStatuses">All</button>
                    <button type="button" class="btn btn-xs btn-outline-secondary py-0 px-2" @click="clearSelectedStatuses">Clear</button>
                  </div>
                  <small class="text-muted">{{ filteredStatusOptions.length }} items</small>
                </div>

                <div class="filter-dropdown-scroll" @click.stop>
                  <div
                    v-for="opt in filteredStatusOptions"
                    :key="opt.value"
                    class="filter-item-row"
                    :class="{ 'row-active': selectedStatusIds.includes(opt.value) }"
                  >
                    <b-form-checkbox
                      v-model="selectedStatusIds"
                      :value="opt.value"
                      class="m-0 custom-checkbox-sm"
                    >
                      <span class="filter-item-text" :title="opt.text">{{ opt.text }}</span>
                    </b-form-checkbox>
                  </div>
                  <div v-if="filteredStatusOptions.length === 0" class="text-muted text-center py-3 small">
                    No statuses found
                  </div>
                </div>
              </b-dropdown>
            </b-form-group>
          </b-col>

          <!-- Caste Multi-Select Dropdown -->
          <b-col md="4" v-if="reportTypeConfig.showCaste">
            <b-form-group label="Caste" label-size="sm" class="filter-group">
              <b-dropdown
                size="sm"
                variant="outline-secondary"
                class="w-100 modern-filter-dropdown"
                menu-class="p-2 shadow-lg modern-filter-menu"
                no-flip
                no-caret
              >
                <template #button-content>
                  <div class="d-flex align-items-center justify-content-between w-100 text-left">
                    <span class="text-truncate filter-display-val">
                      <b-badge v-if="selectedCasteIDs.length > 0" variant="primary" class="mr-1 pill-count">{{ selectedCasteIDs.length }}</b-badge>
                      {{ casteButtonLabel }}
                    </span>
                    <i class="fas fa-chevron-down text-muted ml-2 chevron-icon"></i>
                  </div>
                </template>

                <div class="dropdown-search-box mb-2" @click.stop>
                  <b-input-group size="sm">
                    <b-input-group-prepend is-text class="search-prep">
                      <i class="fas fa-search text-muted small"></i>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model.trim="casteSearch"
                      placeholder="Search caste..."
                      class="filter-search-input"
                    ></b-form-input>
                    <b-input-group-append v-if="casteSearch">
                      <b-button size="sm" variant="light" class="btn-clear-search" @click="casteSearch = ''">×</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>

                <div class="d-flex justify-content-between align-items-center px-1 mb-2 filter-quick-bar" @click.stop>
                  <div>
                    <button type="button" class="btn btn-xs btn-outline-primary py-0 px-2 mr-1" @click="selectAllCastes">All</button>
                    <button type="button" class="btn btn-xs btn-outline-secondary py-0 px-2" @click="clearSelectedCastes">Clear</button>
                  </div>
                  <small class="text-muted">{{ filteredCasteOptions.length }} items</small>
                </div>

                <div class="filter-dropdown-scroll" @click.stop>
                  <div
                    v-for="opt in filteredCasteOptions"
                    :key="opt.value"
                    class="filter-item-row"
                    :class="{ 'row-active': selectedCasteIDs.includes(opt.value) }"
                  >
                    <b-form-checkbox
                      v-model="selectedCasteIDs"
                      :value="opt.value"
                      class="m-0 custom-checkbox-sm"
                    >
                      <span class="filter-item-text" :title="opt.text">{{ opt.text }}</span>
                    </b-form-checkbox>
                  </div>
                  <div v-if="filteredCasteOptions.length === 0" class="text-muted text-center py-3 small">
                    No castes found
                  </div>
                </div>
              </b-dropdown>
            </b-form-group>
          </b-col>

          <!-- Demographics & Details -->
          <b-col md="4" v-if="reportTypeConfig.showGender">
            <b-form-group label="Gender" label-size="sm" class="filter-group">
              <b-form-select v-model="selectedGender" :options="genderOptions" size="sm" class="modern-input"></b-form-select>
            </b-form-group>
          </b-col>

          <b-col md="4" v-if="reportTypeConfig.showAge">
            <b-form-group label="Age Range" label-size="sm" class="filter-group">
              <b-input-group size="sm">
                <b-form-input v-model.trim="ageMin" placeholder="Min Age" type="number" class="modern-input"></b-form-input>
                <b-input-group-text class="px-2 bg-light border-left-0 border-right-0">-</b-input-group-text>
                <b-form-input v-model.trim="ageMax" placeholder="Max Age" type="number" class="modern-input"></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>

          <b-col md="4" v-if="reportTypeConfig.showClaimPaymentStatus">
            <b-form-group label="Claim Payment Status" label-size="sm" class="filter-group">
              <b-form-select
                v-model="selectedClaimPaymentStatus"
                :options="claimPaymentStatusOptions"
                value-field="value"
                text-field="text"
                size="sm"
                class="modern-input"
              ></b-form-select>
            </b-form-group>
          </b-col>

          <!-- Identifiers Row -->
          <b-col md="3" v-if="reportTypeConfig.showApplicationNo">
            <b-form-group label="Application No." label-size="sm" class="filter-group">
              <b-form-input v-model.trim="applicationNoFilter" size="sm" placeholder="e.g. APP/2024/001" class="modern-input"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="3" v-if="reportTypeConfig.showApplicantName">
            <b-form-group label="Applicant Name" label-size="sm" class="filter-group">
              <b-form-input v-model.trim="applicantNameFilter" size="sm" placeholder="Search applicant name..." class="modern-input"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="3" v-if="reportTypeConfig.showLoiNo">
            <b-form-group label="LOI No." label-size="sm" class="filter-group">
              <b-form-input v-model.trim="loiNumberFilter" size="sm" placeholder="Enter LOI number..." class="modern-input"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="3" v-if="reportTypeConfig.showMobile">
            <b-form-group label="Mobile Number" label-size="sm" class="filter-group">
              <b-form-input v-model.trim="mobileFilter" size="sm" placeholder="10-digit mobile..." class="modern-input"></b-form-input>
            </b-form-group>
          </b-col>

          <!-- Date Filters -->
          <b-col md="3" v-if="reportTypeConfig.showDates">
            <b-form-group label="From Date" label-size="sm" class="filter-group">
              <b-form-input v-model="dateFrom" type="date" size="sm" class="modern-input"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="3" v-if="reportTypeConfig.showDates">
            <b-form-group label="To Date" label-size="sm" class="filter-group">
              <b-form-input v-model="dateTo" type="date" size="sm" class="modern-input"></b-form-input>
            </b-form-group>
          </b-col>

          <!-- Action Buttons Bar -->
          <b-col md="12" class="mt-2 pt-3 border-top d-flex justify-content-between align-items-center flex-wrap filter-action-bar">
            <div class="text-muted small">
              <i class="fas fa-info-circle mr-1 text-primary"></i>
              Select filters and click <strong>Generate Report</strong> to fetch live records.
            </div>
            <div class="d-flex align-items-center mt-2 mt-md-0">
              <b-button variant="outline-secondary" size="sm" class="action-btn-reset mr-2" @click="resetFilters">
                <i class="fas fa-undo-alt mr-1"></i> Reset Filters
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                class="action-btn-search px-4"
                :disabled="(!canSearchWithoutCompany && !selectedCompany) || (reportTypeConfig.showDates && (!dateFrom || !dateTo)) || isLoading"
                @click="fetchReport"
              >
                <span v-if="isLoading"><b-spinner small class="mr-1"></b-spinner> Loading...</span>
                <span v-else><i class="fas fa-search mr-1"></i> Generate Report</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Always-visible LOI Summary -->
    <b-card v-if="showLoiSummary && (selectedCompanyID !== null && selectedCompanyID !== undefined) && dateFrom && dateTo" class="mb-3" body-class="py-2 px-3" style="background:#f8fafc;border:1px solid #e0e0e0;">
      <div v-if="loiSummaryLoading" class="text-center py-2">
        <b-spinner small></b-spinner> Loading summary...
      </div>
      <div v-else>
        <span class="mr-4"><strong>Total LOI Issued:</strong> {{ totalLOI }}</span>
        <span><strong>Total Amount Approved:</strong> {{ formatAmount(sumAmountApproved) }}</span>
        <div class="mt-1" style="font-size:12px;color:#4b5f7e;">
          Effective LOI Date Range: {{ effectiveDateRangeText }}
        </div>
      </div>
    </b-card>

    <b-card v-if="searched && showStatisticalSplit" class="mb-3 report-stats-card" body-class="p-3">
      <div class="stats-header">
        <div>
          <div class="stats-title">Statistical Split</div>
          <div class="stats-subtitle">{{ selectedCompany ? selectedCompany.name : "All Companies" }}</div>
        </div>
        <div class="stats-kpis">
          <div v-if="!isConsolidationSummary && (showStatPanel('application_status') || showStatPanel('scheme'))" class="kpi-chip">
            <div class="kpi-label">Applications</div>
            <div class="kpi-value">{{ totalApplications }}</div>
          </div>
          <div v-if="!isConsolidationSummary && showStatPanel('scheme')" class="kpi-chip">
            <div class="kpi-label">LOI</div>
            <div class="kpi-value">{{ totalLOI }}</div>
          </div>
          <div v-if="showStatPanel('claim_split')" class="kpi-chip">
            <div class="kpi-label">Claim Beneficiaries</div>
            <div class="kpi-value">{{ totalClaim }}</div>
          </div>
          <div v-if="isConsolidationSummary" class="kpi-chip">
            <div class="kpi-label">Total Beneficiaries</div>
            <div class="kpi-value">{{ consolidationTotal.Beneficiaries }}</div>
          </div>
          <div v-if="isConsolidationSummary" class="kpi-chip">
            <div class="kpi-label">Paid Amount</div>
            <div class="kpi-value">{{ formatAmount(consolidationTotal.PaidAmt) }}</div>
          </div>
        </div>
      </div>

      <div class="stats-grid">
        <div v-if="showStatPanel('scheme')" class="stat-panel">
          <div class="stat-panel-title">Scheme-wise</div>
          <div v-for="row in topStatRows(stats.schemeWise, 8)" :key="'scheme-' + row.label" class="stat-row2">
            <div class="stat-row2-head">
              <span class="stat-row2-label">{{ row.label }}</span>
              <span class="stat-row2-value">{{ row.count }}</span>
            </div>
            <div class="stat-row2-bar"><span :style="{ width: statPercent(row.count, stats.schemeWise) + '%' }"></span></div>
          </div>
        </div>

        <div v-if="showStatPanel('application_status')" class="stat-panel">
          <div class="stat-panel-title">Application Status-wise</div>
          <div v-for="row in topStatRows(stats.applicationStatusWise, 8)" :key="'status-' + row.label" class="stat-row2">
            <div class="stat-row2-head">
              <span class="stat-row2-label">{{ row.label }}</span>
              <span class="stat-row2-value">{{ row.count }}</span>
            </div>
            <div class="stat-row2-bar"><span :style="{ width: statPercent(row.count, stats.applicationStatusWise) + '%' }"></span></div>
          </div>
        </div>

        <div v-if="showStatPanel('category')" class="stat-panel">
          <div class="stat-panel-title">Category-wise</div>
          <div v-for="row in topStatRows(stats.categoryWise, 8)" :key="'category-' + row.label" class="stat-row2">
            <div class="stat-row2-head">
              <span class="stat-row2-label">{{ row.label }}</span>
              <span class="stat-row2-value">{{ row.count }}</span>
            </div>
            <div class="stat-row2-bar"><span :style="{ width: statPercent(row.count, stats.categoryWise) + '%' }"></span></div>
          </div>
        </div>

        <div v-if="showStatPanel('caste')" class="stat-panel">
          <div class="stat-panel-title">Caste-wise (Top 5)</div>
          <div v-for="row in topStatRows(stats.casteWise, 5)" :key="'caste-' + row.label" class="stat-row2">
            <div class="stat-row2-head">
              <span class="stat-row2-label">{{ row.label }}</span>
              <span class="stat-row2-value">{{ row.count }}</span>
            </div>
            <div class="stat-row2-bar"><span :style="{ width: statPercent(row.count, stats.casteWise) + '%' }"></span></div>
          </div>
        </div>

        <div v-if="showStatPanel('gender')" class="stat-panel">
          <div class="stat-panel-title">Gender-wise</div>
          <div v-for="row in topStatRows(stats.genderWise, 3)" :key="'gender-' + row.label" class="stat-row2">
            <div class="stat-row2-head">
              <span class="stat-row2-label">{{ row.label }}</span>
              <span class="stat-row2-value">{{ row.count }}</span>
            </div>
            <div class="stat-row2-bar"><span :style="{ width: statPercent(row.count, stats.genderWise) + '%' }"></span></div>
          </div>
        </div>

        <div v-if="showStatPanel('year')" class="stat-panel">
          <div class="stat-panel-title">Year-wise</div>
          <div v-for="row in topStatRows(stats.yearWise, 6)" :key="'year-' + row.label" class="stat-row2">
            <div class="stat-row2-head">
              <span class="stat-row2-label">{{ row.label }}</span>
              <span class="stat-row2-value">{{ row.count }}</span>
            </div>
            <div class="stat-row2-bar"><span :style="{ width: statPercent(row.count, stats.yearWise) + '%' }"></span></div>
          </div>
        </div>

        <div v-if="showStatPanel('location')" class="stat-panel">
          <div class="stat-panel-title">{{ locationSplitTitle }}</div>
          <div v-for="row in topStatRows(stats.districtWise, 12)" :key="'district-' + row.label" class="stat-row2">
            <div class="stat-row2-head">
              <span class="stat-row2-label">{{ row.label }}</span>
              <span class="stat-row2-value">{{ row.count }}</span>
            </div>
            <div class="stat-row2-bar"><span :style="{ width: statPercent(row.count, stats.districtWise) + '%' }"></span></div>
          </div>
        </div>

        <div v-if="showStatPanel('beneficiary')" class="stat-panel">
          <div class="stat-panel-title">Old/New Beneficiary</div>
          <div class="stat-row2 simple-row">
            <span class="stat-row2-label">Old Beneficiary</span>
            <span class="stat-row2-value">{{ stats.beneficiarySplit.oldCount }}</span>
          </div>
          <div class="stat-row2 simple-row">
            <span class="stat-row2-label">New Beneficiary</span>
            <span class="stat-row2-value">{{ stats.beneficiarySplit.newCount }}</span>
          </div>
        </div>

        <div v-if="showStatPanel('claim_split')" class="stat-panel">
          <div class="stat-panel-title">Old/New Claim Beneficiaries</div>
          <div class="stat-row2 simple-row">
            <span class="stat-row2-label">Old Beneficiaries</span>
            <span class="stat-row2-value">{{ stats.claimSplit.oldCount }} ({{ stats.claimSplit.oldAmount.toLocaleString('en-IN', {minimumFractionDigits:2}) }})</span>
          </div>
          <div class="stat-row2 simple-row">
            <span class="stat-row2-label">New Beneficiaries</span>
            <span class="stat-row2-value">{{ stats.claimSplit.newCount }} ({{ stats.claimSplit.newAmount.toLocaleString('en-IN', {minimumFractionDigits:2}) }})</span>
          </div>
        </div>
      </div>
    </b-card>

    <b-card v-if="searched && showSchemeSummary" class="mb-3" body-class="py-2 px-3" style="background:#f7fbff;border:1px solid #d8e7fb;">
      <div style="font-size:13px;font-weight:600;margin-bottom:8px;">
        Scheme-wise Detailed Summary
      </div>
      <div class="overflow-auto">
        <b-table
          small
          striped
          bordered
          hover
          head-variant="light"
          :items="schemeDetailRows"
          :fields="displaySchemeDetailFields"
          style="font-size:12px"
          empty-text="No scheme detail available for the selected filters."
        ></b-table>
      </div>
    </b-card>

    <!-- Results Card -->
    <b-card v-if="reportData.length > 0 || searched" header-bg-variant="primary" header-text-variant="white">
      <template v-slot:header>
        <b-row class="no-gutters align-items-center">
          <b-col>
            <strong v-if="isConsolidationSummary">Consolidation Summary Claim Paid — {{ effectiveDateRangeText }}</strong>
            <strong v-else-if="selectedCompany">{{ selectedCompany.name }} — {{ effectiveDateRangeText }}</strong>
            <strong v-else-if="selectedReportType === 'company_wise'">Company Wise Report — {{ effectiveDateRangeText }}</strong>
          </b-col>
          <b-col class="text-right">
            <!-- Quick LOI Summary button removed: summary is always visible now -->
            <b-badge variant="light" class="mr-3" style="color:#333;font-size:13px;">
              Total: {{ totalRows }}
            </b-badge>
            <b-button
              variant="success"
              size="sm"
              :disabled="reportData.length === 0"
              @click="exportToExcel"
            >
              <i class="fa fa-file-excel-o"></i> Export Excel
            </b-button>
          </b-col>
        </b-row>
      </template>

      <!-- No data message -->
      <b-alert show variant="warning" v-if="searched && reportData.length === 0">
        No records found for the selected filters.
      </b-alert>

      <!-- Table -->
      <div v-if="reportData.length > 0">
            <!-- Quick LOI Summary Modal removed: summary is always visible now -->
        <!-- Per page + filter row -->
        <b-row class="mb-2">
          <b-col md="3">
            <b-input-group size="sm">
              <b-form-input v-model="tableFilter" placeholder="Search in table..." />
              <b-input-group-append>
                <b-btn :disabled="!tableFilter" @click="tableFilter=''" size="sm">Clear</b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col md="3" offset-md="6">
            <b-form-group horizontal label="Per page" class="mb-0" label-size="sm" label-cols="4">
              <b-form-select :options="pageOptions" v-model="perPage" size="sm" />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="overflow-auto">
          <b-table
            show-empty
            head-variant="dark"
            bordered
            striped
            small
            hover
            style="font-size:12px"
            :items="reportData"
            :fields="tableFields"
            :current-page="currentPage"
            :per-page="perPage"
            :filter="tableFilter"
            @filtered="onFiltered"
            empty-text="No records available."
          >
            <template v-slot:cell(Sr)="row">{{ (currentPage - 1) * perPage + row.index + 1 }}</template>
          </b-table>
        </div>

        <!-- Totals Row -->
        <div v-if="isConsolidationSummary" class="mt-2 text-right" style="font-size:13px;">
          <strong>Total Beneficiaries:</strong> {{ consolidationTotal.Beneficiaries }} &nbsp; | &nbsp;
          <strong>Total Paid Amount:</strong> {{ formatAmount(consolidationTotal.PaidAmt) }}
        </div>
        <div v-else class="overflow-auto mt-2">
          <table class="table table-bordered table-sm" style="font-size:12px;background:#f0f7ff;">
            <tr style="font-weight:bold;">
              <td colspan="8" class="text-right">TOTAL:</td>
              <td>{{ formatAmount(sumAmountDemanded) }}</td>
              <td colspan="2"></td>
              <td>{{ formatAmount(sumAmountApproved) }}</td>
              <td v-if="showFinancialYearPaidMetrics">{{ formatWholeAmount(sumPaidAmt) }}</td>
              <td colspan="4"></td>
              <td v-if="showClaimMetrics">{{ formatAmount(sumDemand) }}</td>
              <template v-if="showClaimMetrics">
                <td>{{ formatAmount(sumClaimAmount) }}</td>
                <td></td>
                <td>{{ formatAmount(sumPaidAmt) }}</td>
                <td></td>
                <td></td>
              </template>
            </tr>
          </table>
          <div class="mt-1" style="font-size:13px;">
            <strong>Total Applications:</strong> {{ totalApplications }} &nbsp; | &nbsp;
            <strong>Total LOI:</strong> {{ totalLOI }}
            <template v-if="showClaimMetrics"> &nbsp; | &nbsp;
              <strong>Total Claims:</strong> {{ totalClaim }}
            </template>
          </div>
        </div>

        <!-- Pagination -->
        <b-row class="mt-2">
          <b-col md="5">
            <span style="font-size:0.875rem">Total Rows: {{ totalRows }}</span>
          </b-col>
          <b-col md="7">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              size="sm"
              style="float:right"
              limit="10"
            ></b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Initial placeholder -->
    <b-card v-if="!searched && reportData.length === 0" class="text-center py-5">
      <p class="text-muted mb-0">Select filters, then click <strong>Search</strong> to view the Master Report.</p>
    </b-card>

  </div>
</template>

<script>
import apis from "@/apis";
import XLSX from "xlsx";
import logger from "@/utils/logger";
import { normalizeResponseArray } from "@/utils/responseNormalizer";
import { detectTenant, filterCasteDropdown } from "@/tenantConfig";
import educationHistoricalPayments from "@/data/educationHistoricalPayments.json";

const educationPaymentByClaimId = (educationHistoricalPayments || []).reduce((result, payment) => {
  const claimId = String(payment && payment.ClaimEMIID || "").trim().toLowerCase();
  if (claimId) result[claimId] = payment;
  return result;
}, {});

export default {
  name: "SubCompanyReport",
  data() {
    return {
      selectedCompanyID: null,
      selectedYear: null,
      selectedReportType: "applications",
      selectedLoanType: 0,
      selectedSchemeName: "All",
      isLoading: false,
      searched: false,
      reportData: [],
      reportBaseRows: [],
      tableFilter: "",
      currentPage: 1,
      perPage: 50,
      totalRows: 0,
      pageOptions: [50, 100, 200, 500],
      loanTypeOptions: [
        { value: 0, text: "All" },
        { value: 1, text: "Individual Loan" },
        { value: 2, text: "Group Loan" },
        { value: 3, text: "Education Loan" },
      ],
      schemeFilterOptions: [{ value: "All", text: "All" }],
      allSchemeFilterOptions: [],
      reportTypeOptions: [
        { value: "applications", text: "Applications" },
        { value: "loi", text: "LOI" },
        { value: "sanction_letters", text: "Sanction Letters" },
        { value: "disbursements", text: "Disbursements" },
        { value: "claims", text: "Claims" },
        { value: "claim_applicant_wise", text: "Claims Applicant Wise" },
        { value: "consolidation_summary", text: "Consolidation Summary Claim Paid" },
        { value: "financial_year_wise", text: "Financial Year Wise" },
        { value: "gender_wise", text: "Gender Wise" },
        { value: "caste_wise", text: "Caste Wise" },
        { value: "district_wise", text: "District Wise" },
        { value: "division_wise", text: "Division Wise" },
        { value: "company_wise", text: "Company Wise" },
      ],
      regionOptions: [
        "All Regions",
        "Konkan",
        "Pune",
        "Nashik",
        "Chhatrapati Sambhajinagar",
        "Amravati",
        "Nagpur",
      ],
      selectedRegion: "All Regions",
      statusOptions: [{ value: 0, text: "All" }],
      statusTextById: {},
      selectedStatusId: 0,
      selectedStatusIds: [],
      statusSearch: "",
      selectedClaimPaymentStatus: "All",
      claimPaymentStatusOptions: [
        { value: "All", text: "All" },
        { value: "Paid", text: "Paid" },
        { value: "Unpaid", text: "Unpaid" },
        { value: "Pending", text: "Pending" },
      ],
      selectedBankID: "00000000-0000-0000-0000-000000000000",
      selectedBankIDs: [],
      bankSearch: "",
      bankOptions: [{ BankID: "00000000-0000-0000-0000-000000000000", BankName: "All" }],
      genderOptions: ["All", "Male", "Female"],
      selectedGender: "All",
      ageMin: "",
      ageMax: "",
      selectedCasteIDs: [],
      casteOptions: [],
      casteSearch: "",
      districtSearch: "",
      selectedDistrictIDs: [],
      applicationNoFilter: "",
      applicantNameFilter: "",
      loiNumberFilter: "",
      mobileFilter: "",
      tableFields: [
        { key: "Sr",                label: "Sr.No.",              thStyle: { width: "60px" } },
        { key: "CasteMarathi",      label: "CasteMarathi",        sortable: true },
        { key: "BusinessType",      label: "BusinessType",        sortable: true },
        { key: "SchemeType",        label: "SchemeType",          sortable: true },
        { key: "DistrictName",      label: "District",            sortable: true },
        { key: "LoanApplicationNo", label: "LoanApplicationNo",   sortable: true },
        { key: "ApplicationDate",   label: "ApplicationDate",     sortable: true },
        { key: "AmountDemanded",    label: "AmountDemanded",      sortable: true },
        { key: "ApplicationStatus", label: "ApplicationStatus",   sortable: true },
        { key: "ApplicationStatus1",label: "ApplicationStatus1",  sortable: true },
        { key: "ClaimNo",           label: "Claim ID",            sortable: true },
        { key: "AmountApproved",    label: "AmountApproved",      sortable: true },
        { key: "LOINumber",         label: "LOINumber",           sortable: true },
        { key: "LOIDate",           label: "LOIDate",             sortable: true },
        { key: "BankName",          label: "BankName",            sortable: true },
        { key: "Demand",            label: "Demand",              sortable: true },
        { key: "ClaimAmount",       label: "Claim Amount",        sortable: true },
        { key: "AppliedForClaimDtd",label: "Applied for Claim dtd", sortable: true },
        { key: "PaidAmt",           label: "Paid Amt",            sortable: true },
        { key: "Claim",             label: "Claim",               sortable: true },
        { key: "PaidDt",            label: "Paid dt",             sortable: true },
      ],
        loiSummaryLoading: false,
        loiSummary: { count: 0, amount: 0 },
      allSubCompanies: [
        {
          id: 0,
          tenantId: "msobcfdc",
          url: "msobcfdc.in",
          name: "महाराष्ट्र राज्य इतर मागासवर्गीय वित्त आणि विकास महामंडळ मर्यादित",
          nameEnglish: "MAHARASHTRA STATE OBC FINANCE & DEVELOPMENT CORPORATION LTD",
          category: "Universal - Parent Company",
          type: "Universal",
          casteTenantIds: ["", "0", "1"],
          castes: [],
        },
        {
          id: 1,
          tenantId: "spkemavm",
          url: "spkemavm.msobcfdc.in",
          name: "शामराव पेजे कोकण इतर मागासवर्ग आर्थिक विकास महामंडळ",
          nameEnglish: "Shamrao Peje Konkan OBC Economic Development Corporation",
          category: "Universal - सर्व इमाव जाती",
          type: "Universal",
          casteTenantIds: ["", "0", "1"],
          castes: [],
        },
        {
          id: 2,
          tenantId: "skgyavm",
          url: "skgyavm.msobcfdc.in",
          name: "संत काशिबा गुरव युवा आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Kashiba Gurav Youth Economic Development Corporation",
          category: "गुरव",
          type: "Specialized",
          casteTenantId: 2,
          castes: [
            { casteCode: 43, casteName: "गुरव" },
          ],
        },
        {
          id: 3,
          tenantId: "jmbavm",
          url: "jmbavm.msobcfdc.in",
          name: "जगद्ज्योती महात्मा बसवेश्वर आर्थिक विकास महामंडळ",
          nameEnglish: "Jagadjyoti Mahatma Basaveshwar Economic Development Corporation",
          category: "वीरशैव - लिंगायत",
          type: "Specialized",
          casteTenantId: 3,
          castes: [
            { casteCode: 43,  casteName: "लिंगायत गुरव" },
            { casteCode: 58,  casteName: "जंगम, मालाजंगम (विरभद्र) लिंगायत जंगम" },
            { casteCode: 82,  casteName: "लिंगायत कुंभार" },
            { casteCode: 108, casteName: "लिंगायत न्हावी" },
            { casteCode: 125, casteName: "लिंगायत परीट, लिंगायत धोबी" },
            { casteCode: 127, casteName: "लिंगायत फुलारी" },
            { casteCode: 174, casteName: "लिंगायत सुतार" },
            { casteCode: 181, casteName: "लिंगायत तेली" },
            { casteCode: 182, casteName: "लिंगायत माळी" },
            { casteCode: 190, casteName: "लिंगायत वाणी व लिंगायत तांबोळी" },
            { casteCode: 338, casteName: "लिंगायत कुल्लेकडगी" },
          ],
        },
        {
          id: 4,
          tenantId: "ssmkm",
          url: "ssmkm.msobcfdc.in",
          name: "संत सेनाजी महाराज केशशिल्पी महामंडळ",
          nameEnglish: "Sant Senaji Maharaj Hairdresser Corporation",
          category: "न्हावी / केशशिल्पी",
          type: "Specialized",
          casteTenantId: 4,
          castes: [
            { casteCode: 99, casteName: "महली" },
            { casteCode: 101, casteName: "म्हाली" },
            { casteCode: 108, casteName: "नावी" },
            { casteCode: 108, casteName: "न्हावी" },
            { casteCode: 108, casteName: "सलमानी हजाम" },
            { casteCode: 108, casteName: "हजाम" },
            { casteCode: 108, casteName: "वारिक" },
            { casteCode: 108, casteName: "नाभिक" },
            { casteCode: 108, casteName: "नापित" },
            { casteCode: 108, casteName: "म्हाली" },
            { casteCode: 108, casteName: "वालंद" },
            { casteCode: 108, casteName: "हडपद" },
            { casteCode: 108, casteName: "हज्जाम" },
            { casteCode: 108, casteName: "नावीसेन" },
            { casteCode: 108, casteName: "सलमानियॉ" },
            { casteCode: 276, casteName: "हजाम" },
            { casteCode: 276, casteName: "कालसेरु" },
            { casteCode: 276, casteName: "नावलिगा" },
            { casteCode: 276, casteName: "कान्शी" },
            { casteCode: 276, casteName: "नाभिक" },
            { casteCode: 108, casteName: "नाई" },
            { casteCode: 276, casteName: "नाई" },
            { casteCode: 276, casteName: "वालंद" },
            { casteCode: 276, casteName: "सलमानी" },
            { casteCode: 348, casteName: "मारवाडी न्हावी" },
          ],
        },
        {
          id: 5,
          tenantId: "snmavmu",
          url: "snmavmu.msobcfdc.in",
          name: "संत नरहरी महाराज आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Narhari Maharaj Economic Development Corporation",
          category: "सोनार",
          type: "Specialized",
          casteTenantId: 5,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-46/Mahamandal",
          castes: [
            { casteCode: 154, casteName: "सोनार" },
            { casteCode: 154, casteName: "मारवाडी सोनार" },
            { casteCode: 154, casteName: "मारवाडी सुनार" },
            { casteCode: 154, casteName: "मारवाडी स्वर्णकार" },
          ],
        },
        {
          id: 6,
          tenantId: "sssmavmu",
          url: "sssmavmu.msobcfdc.in",
          name: "संत श्री संताजी महाराज आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Shri Santaji Maharaj Economic Development Corporation",
          category: "तेली",
          type: "Specialized",
          casteTenantId: 9,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-67/Mahamandal",
          castes: [
            { casteCode: 181, casteName: "तेली" },
            { casteCode: 181, casteName: "तिळवण तेली" },
            { casteCode: 181, casteName: "मराठा तेली" },
            { casteCode: 181, casteName: "तराणे तेली" },
            { casteCode: 181, casteName: "देशकर तेली" },
            { casteCode: 181, casteName: "एरंडेल तेली" },
            { casteCode: 181, casteName: "लिंगायत तेली" },
            { casteCode: 181, casteName: "एकबैल तेली" },
            { casteCode: 181, casteName: "दोन बैल तेली" },
            { casteCode: 181, casteName: "दोन बैल तेली सावतेली" },
            { casteCode: 181, casteName: "एक बहिया तेली" },
            { casteCode: 181, casteName: "घाँची" },
            { casteCode: 291, casteName: "सावतेली" },
          ],
        },
        {
          id: 7,
          tenantId: "ssrmavmu",
          url: "ssrmavmu.msobcfdc.in",
          name: "संत श्री. रुपलाल महाराज आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Shri Rupalal Maharaj Economic Development Corporation",
          category: "बारी",
          type: "Specialized",
          casteTenantId: 10,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-40/Mahamandal",
          castes: [
            { casteCode: 13, casteName: "बारी" },
            { casteCode: 13, casteName: "बारई" },
          ],
        },
        {
          id: 8,
          tenantId: "svrdlavmu",
          url: "svrdlavmu.msobcfdc.in",
          name: "स्व. विष्णूपंत रामचंद्र दादरे (लोणारी) आर्थिक विकास महामंडळ",
          nameEnglish: "Late Vishnupant Ramchandra Dadare (Lonari) Economic Development Corporation",
          category: "लोणारी",
          type: "Specialized",
          casteTenantId: 8,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-70/Mahamandal",
          castes: [
            { casteCode: 183, casteName: "लोणारी" },
          ],
        },
        {
          id: 9,
          tenantId: "skavmu",
          url: "skavmu.msobcfdc.in",
          name: "सोळा कुलस्वामिनी आर्थिक विकास महामंडळ",
          nameEnglish: "Sola Kulswamini Economic Development Corporation",
          category: "वाणी",
          type: "Specialized",
          casteTenantId: 7,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-72/Mahamandal",
          castes: [
            { casteCode: 190, casteName: "लाडशाखीय वाणी" },
            { casteCode: 190, casteName: "वाणी" },
            { casteCode: 190, casteName: "काथार" },
            { casteCode: 190, casteName: "काथार वाणी" },
            { casteCode: 190, casteName: "कंठहार वाणी" },
            { casteCode: 190, casteName: "वैश्यवाणी" },
            { casteCode: 190, casteName: "वैश्य-वाणी" },
            { casteCode: 190, casteName: "वै.वाणी" },
            { casteCode: 190, casteName: "वै. वाणी" },
            { casteCode: 190, casteName: "वैश्य वाणी" },
            { casteCode: 190, casteName: "V.Wani" },
            { casteCode: 190, casteName: "पानारी" },
            { casteCode: 190, casteName: "कुलवंत वाणी" },
            { casteCode: 190, casteName: "कु.वाणी" },
            { casteCode: 190, casteName: "वाणी (कुलवंत)" },
            { casteCode: 190, casteName: "कुलवंत-वाणी" },
            { casteCode: 190, casteName: "कुलवंत – वाणी" },
            { casteCode: 190, casteName: "कुणबी वाणी" },
            { casteCode: 190, casteName: "नेवी" },
            { casteCode: 190, casteName: "धाकड" },
            { casteCode: 190, casteName: "मिटकरीवाणी" },
            { casteCode: 190, casteName: "बोरळ" },
            { casteCode: 190, casteName: "बोराळ" },
            { casteCode: 190, casteName: "बोरुळ" },
            { casteCode: 190, casteName: "बोरूळ" },
            { casteCode: 190, casteName: "बोरड" },
            { casteCode: 190, casteName: "तांबोळी" },
          ],
        },
        {
          id: 10,
          tenantId: "sssavmu",
          url: "sssavmu.msobcfdc.in",
          name: "श्री.संत नामदेव महाराज शिंपी समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Shri Sant Namdev Maharaj Shimpi Samaj Economic Development Corporation",
          category: "शिंपी",
          type: "Specialized",
          casteTenantId: 6,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-62/Mahamandal",
          castes: [
            { casteCode: 153, casteName: "शिंपी" },
            { casteCode: 153, casteName: "इद्रिसी / दर्जी" },
            { casteCode: 153, casteName: "साईसुतार" },
            { casteCode: 153, casteName: "जैन शिंपी" },
            { casteCode: 153, casteName: "श्रावक" },
            { casteCode: 153, casteName: "श्रावक शिंपी" },
            { casteCode: 153, casteName: "शेतवाळ" },
            { casteCode: 153, casteName: "शेतवाल" },
            { casteCode: 153, casteName: "सैतवाल" },
            { casteCode: 153, casteName: "सैतवाळ" },
            { casteCode: 153, casteName: "मेरु शिंपी" },
            { casteCode: 153, casteName: "मेरु क्षत्रिय शिंपी" },
            { casteCode: 153, casteName: "मेरू शिंपी" },
            { casteCode: 153, casteName: "मेरू क्षत्रिय शिंपी" },
            { casteCode: 153, casteName: "मेरू शिंपी / मेरू क्षत्रिय शिंपी" },
            { casteCode: 153, casteName: "तेलगु दर्जी" },
            { casteCode: 153, casteName: "तेलगु शिंपी" },
            { casteCode: 153, casteName: "नामदेव शिंपी" },
            { casteCode: 170, casteName: "भावसार क्षत्रिय शिंपी" },
            { casteCode: 293, casteName: "भावगर" },
            { casteCode: 293, casteName: "शिव शिंपी" },
            { casteCode: 293, casteName: "नामदेव" },
          ],
        },
        {
          id: 11,
          tenantId: "sbkssavmu",
          url: "sbkssavmu.msobcfdc.in",
          name: "संत भोजलिंग काका सुतार समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Bhojlinga Kaka Sutar Samaj Economic Development Corporation",
          category: "सुतार",
          type: "Specialized",
          casteTenantId: 11,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-25/Mahamandal",
          castes: [
            { casteCode: 121, casteName: "पांचाळ" },
            { casteCode: 174, casteName: "सुतार" },
            { casteCode: 174, casteName: "सुथार" },
            { casteCode: 174, casteName: "वाढई" },
            { casteCode: 174, casteName: "बाढी" },
            { casteCode: 174, casteName: "वढई" },
            { casteCode: 174, casteName: "बाढई" },
            { casteCode: 174, casteName: "बढई" },
            { casteCode: 174, casteName: "वाढी" },
            { casteCode: 174, casteName: "वाडी" },
            { casteCode: 174, casteName: "वधाई" },
            { casteCode: 174, casteName: "झाडे" },
            { casteCode: 174, casteName: "झाडे सुतार" },
            { casteCode: 174, casteName: "पांचाळ सुतार" },
            { casteCode: 266, casteName: "पांचाळ" },
            { casteCode: 266, casteName: "पंचाल" },
          ],
        },
        {
          id: 12,
          tenantId: "lpsavmu",
          url: "lpsavmu.msobcfdc.in",
          name: "लेवा पाटीदार समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Leva Patidar Samaj Economic Development Corporation",
          category: "लेवा पटीदार",
          type: "Specialized",
          casteTenantId: 12,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-78/Mahamandal",
          castes: [
            { casteCode: 83, casteName: "लेवा पाटीदार" },
            { casteCode: 83, casteName: "लेवा कुणबी" },
            { casteCode: 83, casteName: "लेवा पाटील" },
          ],
        },
        {
          id: 13,
          tenantId: "svpgsavmu",
          url: "svpgsavmu.msobcfdc.in",
          name: "सरदार वल्लभभाई पटेल गुजर समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Sardar Vallabhbhai Patel Gujar Samaj Economic Development Corporation",
          category: "गुजर",
          type: "Specialized",
          casteTenantId: 14,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-77/Mahamandal",
          castes: [
            { casteCode: 326, casteName: "गुजर" },
            { casteCode: 326, casteName: "दोडे गुजर" },
            { casteCode: 326, casteName: "गुर्जर" },
            { casteCode: 326, casteName: "लेवा गुजर" },
            { casteCode: 326, casteName: "लेवे गुजर" },
            { casteCode: 326, casteName: "रेवे गुजर" },
            { casteCode: 326, casteName: "रेवा गुजर" },
            { casteCode: 326, casteName: "सुर्यवंशी गुजर" },
            { casteCode: 326, casteName: "बडगुजर" },
            { casteCode: 326, casteName: "लोंढारी" },
            { casteCode: 326, casteName: "पेंढारी" },
            { casteCode: 326, casteName: "बडगुजर तत्सम जाती लोंढारी" },
            { casteCode: 326, casteName: "बडगुजर तत्सम जाती पेंढारी" },
          ],
        },
        {
          id: 14,
          tenantId: "asavmu",
          url: "asavmu.msobcfdc.in",
          name: "आगरी समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Agari Samaj Economic Development Corporation",
          category: "आगरी",
          type: "Specialized",
          casteTenantId: 13,
          establishmentDate: "2025-03-04",
          governmentResolution: "Maham-2024/CR-2/Mahamandal",
          castes: [
            { casteCode: 169, casteName: "आगरी" },
            { casteCode: 169, casteName: "आगळे" },
            { casteCode: 169, casteName: "काळण" },
          ],
        },
      ],
      subCompanies: [],
      currentTenantId: null,
      financialYears: [],
      dateFrom: "",
      dateTo: "",
      claimFilter: "",
      officeFilter: "",
      districtFilter: "",
      allDistrictRows: [],
      districtOptions: [{ DistID: "00000000-0000-0000-0000-000000000000", DistName: "All" }],
      selectedDistrictID: "00000000-0000-0000-0000-000000000000",
      officeOptions: [{ value: "", text: "All" }],
      selectedOffice: "",
      amountFromFilter: "",
      amountToFilter: "",
      schemeNameMap: {},
      schemeDetailRows: [],
      schemeDetailFields: [
        { key: "Scheme", label: "Scheme", sortable: true },
        { key: "Applications", label: "Applications", sortable: true },
        { key: "LOIIssued", label: "LOI Issued", sortable: true },
        { key: "Claims", label: "Claims", sortable: true },
        { key: "DemandedAmount", label: "Demanded Amount", sortable: true },
        { key: "ApprovedAmount", label: "Approved Amount", sortable: true },
        { key: "PaidAmount", label: "Paid Amount", sortable: true },
      ],
      stats: {
        schemeWise: [],
        applicationStatusWise: [],
        categoryWise: [],
        casteWise: [],
        genderWise: [],
        yearWise: [],
        districtWise: [],
        beneficiarySplit: { oldCount: 0, newCount: 0 },
        claimSplit: { oldCount: 0, newCount: 0, oldAmount: 0, newAmount: 0 },
      },
      supportsCompanyStatsApi: true,
      appListApiDisabled: false,
      appListApiDisabledUntil: 0,
      supportsBeneficiaryReportApi: false,
      supportsSanctionQueryApi: true,
      sanctionApiDisabledUntil: 0,
      statsApiDisabled: false,
      enableProfileEnrichment: false,
      maxProfileEnrichmentRows: 120,
      enableClaimEnrichmentForApplications: false,
      apiTimeoutMs: 45000,
      beneficiaryReportLoading: false,
      reportRequestId: 0,
      beneficiaryReportRows: [],
      loiStatusRows: [],
      loiStatusSummary: { generated: 0, pending: 0, total: 0 },
      loiStatusFields: [
        { key: "SrNo", label: "Sr.No.", sortable: true },
        { key: "LoanApplicationNo", label: "Application No.", sortable: true },
        { key: "ApplicantName", label: "Applicant", sortable: true },
        { key: "DistrictName", label: "District", sortable: true },
        { key: "OfficeName", label: "Office", sortable: true },
        { key: "SchemeType", label: "Scheme", sortable: true },
        { key: "LOINumber", label: "LOI No.", sortable: true },
        { key: "LOIDate", label: "LOI Date", sortable: true },
        { key: "Status", label: "Status", sortable: true },
      ],
      userGenderCache: {},
      userProfileCache: {},
      beneficiaryReportFields: [
        { key: "DistrictName", label: "District", sortable: true },
        { key: "LoanApplicationNo", label: "Application No.", sortable: true },
        { key: "Reason", label: "Reason", sortable: true },
        { key: "OldBeneficiaryName", label: "Old Beneficiary", sortable: true },
        { key: "NewBeneficiaryName", label: "New Beneficiary", sortable: true },
        { key: "OldLOINumber", label: "Old LOI No.", sortable: true },
        { key: "NewLOINumber", label: "New LOI No.", sortable: true },
        { key: "RegeneratedOn", label: "Regenerated On", sortable: true },
      ],
    };
  },
  computed: {
    reportTypeConfig() {
      const t = String(this.selectedReportType || "applications").toLowerCase();
      if (t === "consolidation_summary") {
        return {
          showRegion: false,
          showDistrict: false,
          showCaste: true,
          showApplicationNo: false,
          showApplicantName: false,
          showDates: true,
          showSubCompany: true,
          showLoanType: true,
          showBank: false,
          showGender: false,
          showAge: false,
          showStatus: false,
          showClaimPaymentStatus: false,
          showLoiNo: false,
          showMobile: false,
        };
      }
      if (t === "company_wise") {
        return {
          showRegion: true,
          showDistrict: true,
          showCaste: true,
          showApplicationNo: false,
          showApplicantName: false,
          showDates: true,
          showSubCompany: false,
          showLoanType: true,
          showBank: false,
          showGender: false,
          showAge: false,
          showStatus: true,
          showClaimPaymentStatus: false,
          showLoiNo: false,
          showMobile: false,
        };
      }
      return {
        showRegion: t !== "financial_year_wise",
        showDistrict: t !== "financial_year_wise",
        showCaste: t !== "financial_year_wise",
        showApplicationNo: t !== "financial_year_wise",
        showApplicantName: t === "claims" || t === "claim_applicant_wise",
        showDates: true,
        showSubCompany: t === "applications" || t === "loi" || t === "sanction_letters" || t === "disbursements" || t === "claims" || t === "claim_applicant_wise" || t === "gender_wise" || t === "caste_wise" || t === "district_wise" || t === "division_wise",
        showLoanType: t === "applications" || t === "loi" || t === "sanction_letters" || t === "disbursements" || t === "claims" || t === "claim_applicant_wise" || t === "financial_year_wise" || t === "gender_wise" || t === "caste_wise" || t === "district_wise" || t === "division_wise",
        showBank: t === "applications" || t === "loi" || t === "sanction_letters" || t === "disbursements" || t === "claims" || t === "claim_applicant_wise",
        showGender: t === "applications" || t === "claims" || t === "caste_wise",
        showAge: t === "applications" || t === "claims" || t === "claim_applicant_wise",
        showStatus: t === "applications" || t === "loi" || t === "sanction_letters" || t === "disbursements" || t === "claims" || t === "claim_applicant_wise" || t === "district_wise" || t === "gender_wise" || t === "caste_wise" || t === "division_wise",
        showClaimPaymentStatus: t === "claims" || t === "claim_applicant_wise",
        showLoiNo: t === "loi" || t === "sanction_letters" || t === "disbursements" || t === "claims" || t === "claim_applicant_wise",
        showMobile: t === "applications",
      };
    },
    canSearchWithoutCompany() {
      // "All Companies" is a valid search scope for every report type.
      return true;
    },
    scopedCasteOptions() {
      const selectedCompany = this.selectedCompany;
      const allOptions = this.casteOptions || [];

      // Universal companies (parent, Shamrao Peje) own every caste not tagged to a subsidiary.
      if (selectedCompany && Array.isArray(selectedCompany.casteTenantIds) && selectedCompany.casteTenantIds.length > 0) {
        const untaggedIds = {};
        selectedCompany.casteTenantIds.forEach((id) => {
          untaggedIds[String(id === null || id === undefined ? "" : id).trim()] = true;
        });

        const scoped = allOptions.filter((option) => {
          return untaggedIds[String(option && option.casteTenantId || "").trim()] === true;
        });

        // Master data may not carry the tenant id column yet.
        return scoped.length > 0 ? scoped : allOptions;
      }

      if (!selectedCompany || !Array.isArray(selectedCompany.castes) || selectedCompany.castes.length === 0) {
        return allOptions;
      }

      const companyType = this.normalizeCompanyType(selectedCompany);
      if (companyType === "specialized") {
        const allowedNameSet = this.getAllowedCompanyCasteNameSet(selectedCompany);
        const seenKeys = {};
        const options = [];

        // Real master-data castes first so a selection maps to actual rows.
        allOptions.forEach((option) => {
          const tokens = this.splitCasteTokens(option.text);
          if (!tokens.some((token) => allowedNameSet[token] === true)) {
            return;
          }
          const key = this.casteDisplayKey(option.text);
          if (key) {
            if (seenKeys[key]) return;
            seenKeys[key] = true;
          }
          options.push(option);
        });

        // Then every configured caste that isn't already represented above.
        selectedCompany.castes.forEach((caste, index) => {
          const code = String(caste && caste.casteCode || "").trim();
          const name = String(caste && caste.casteName || "").trim();
          if (!name) return;

          const key = this.casteDisplayKey(name);
          if (key) {
            if (seenKeys[key]) return;
            seenKeys[key] = true;
          }

          options.push({
            value: selectedCompany.tenantId + "_caste_" + (code || "no_code") + "_" + index,
            text: code ? code + "-" + name : name,
            casteCode: code,
            casteName: name,
            syntheticCompanyCaste: true
          });
        });

        return options;
      }

      const allowedCodes = {};
      const allowedNames = {};
      selectedCompany.castes.forEach((c) => {
        const code = String(c.casteCode || "").trim();
        const name = this.normalizeCasteText(c.casteName || "");
        if (code) allowedCodes[code] = true;
        if (name) allowedNames[name] = true;
      });

      return allOptions.filter((option) => {
        const code = String(option.value || "").trim();
        const name = this.normalizeCasteText(option.text || "");
        if (companyType === "specialized") {
          return allowedNames[name] === true;
        }
        return allowedCodes[code] === true || allowedNames[name] === true;
      });
    },
    filteredCasteOptions() {
      const search = String(this.casteSearch || "").trim().toLowerCase();
      const selectedLookup = {};
      (this.selectedCasteIDs || []).forEach((id) => {
        selectedLookup[String(id)] = true;
      });

      const filtered = (!search ? (this.scopedCasteOptions || []) : (this.scopedCasteOptions || []).filter((option) => {
        return String(option.text || "").toLowerCase().indexOf(search) !== -1;
      }));

      // Keep selected castes visible at top while searching/browsing.
      return filtered.slice().sort((a, b) => {
        const aSel = selectedLookup[String(a.value)] === true ? 1 : 0;
        const bSel = selectedLookup[String(b.value)] === true ? 1 : 0;
        if (aSel !== bSel) {
          return bSel - aSel;
        }
        return String(a.text || "").localeCompare(String(b.text || ""));
      });
    },
    filteredDistrictOptions() {
      const search = String(this.districtSearch || "").trim().toLowerCase();
      const selectedLookup = {};
      (this.selectedDistrictIDs || []).forEach((id) => {
        selectedLookup[String(id)] = true;
      });

      // Filter out the "All" entry (guid 0000...) for multiselect checkbox list
      const actualDistricts = (this.districtOptions || [])
        .filter((d) => d && String(d.DistID) !== "00000000-0000-0000-0000-000000000000" && String(d.DistName).toLowerCase() !== "all")
        .map((d) => ({ value: String(d.DistID), text: String(d.DistName || "").trim() }));

      const filtered = !search ? actualDistricts : actualDistricts.filter((d) => d.text.toLowerCase().indexOf(search) !== -1);

      return filtered.slice().sort((a, b) => {
        const aSel = selectedLookup[String(a.value)] === true ? 1 : 0;
        const bSel = selectedLookup[String(b.value)] === true ? 1 : 0;
        if (aSel !== bSel) return bSel - aSel;
        return a.text.localeCompare(b.text);
      });
    },
    filteredBankOptions() {
      const search = String(this.bankSearch || "").trim().toLowerCase();
      const selectedLookup = {};
      (this.selectedBankIDs || []).forEach((id) => {
        selectedLookup[String(id)] = true;
      });

      const actualBanks = (this.bankOptions || [])
        .filter((b) => b && String(b.BankID) !== "00000000-0000-0000-0000-000000000000" && String(b.BankName).toLowerCase() !== "all")
        .map((b) => ({ value: String(b.BankID), text: String(b.BankName || "").trim() }));

      const filtered = !search ? actualBanks : actualBanks.filter((b) => b.text.toLowerCase().indexOf(search) !== -1);

      return filtered.slice().sort((a, b) => {
        const aSel = selectedLookup[String(a.value)] === true ? 1 : 0;
        const bSel = selectedLookup[String(b.value)] === true ? 1 : 0;
        if (aSel !== bSel) return bSel - aSel;
        return a.text.localeCompare(b.text);
      });
    },
    filteredStatusOptions() {
      const search = String(this.statusSearch || "").trim().toLowerCase();
      const selectedLookup = {};
      (this.selectedStatusIds || []).forEach((id) => {
        selectedLookup[String(id)] = true;
      });

      const actualStatuses = (this.statusOptions || [])
        .filter((s) => s && s.value !== 0 && String(s.text).toLowerCase() !== "all")
        .map((s) => ({ value: String(s.value), text: String(s.text || "").trim() }));

      const filtered = !search ? actualStatuses : actualStatuses.filter((s) => s.text.toLowerCase().indexOf(search) !== -1);

      return filtered.slice().sort((a, b) => {
        const aSel = selectedLookup[String(a.value)] === true ? 1 : 0;
        const bSel = selectedLookup[String(b.value)] === true ? 1 : 0;
        if (aSel !== bSel) return bSel - aSel;
        return a.text.localeCompare(b.text);
      });
    },
    districtButtonLabel() {
      const len = this.selectedDistrictIDs.length;
      if (len === 0) return "All Districts";
      if (len === 1) {
        const match = (this.districtOptions || []).find((d) => String(d.DistID) === String(this.selectedDistrictIDs[0]));
        return (match && match.DistName) ? match.DistName : "1 District Selected";
      }
      return `${len} Districts Selected`;
    },
    casteButtonLabel() {
      const len = this.selectedCasteIDs.length;
      if (len === 0) return "All Castes";
      if (len === 1) {
        const match = (this.casteOptions || []).find((c) => String(c.value) === String(this.selectedCasteIDs[0]));
        return (match && match.text) ? match.text : "1 Caste Selected";
      }
      return `${len} Castes Selected`;
    },
    bankButtonLabel() {
      const len = this.selectedBankIDs.length;
      if (len === 0) return "All Banks";
      if (len === 1) {
        const match = (this.bankOptions || []).find((b) => String(b.BankID) === String(this.selectedBankIDs[0]));
        return (match && match.BankName) ? match.BankName : "1 Bank Selected";
      }
      return `${len} Banks Selected`;
    },
    statusButtonLabel() {
      const len = this.selectedStatusIds.length;
      if (len === 0) return "All Statuses";
      if (len === 1) {
        const match = (this.statusOptions || []).find((s) => String(s.value) === String(this.selectedStatusIds[0]));
        return (match && match.text) ? match.text : "1 Status Selected";
      }
      return `${len} Statuses Selected`;
    },
    activeFilterCount() {
      let count = 0;
      if (this.selectedCompanyID !== null && this.selectedCompanyID !== undefined) count++;
      if (this.selectedRegion && this.selectedRegion !== "All Regions") count++;
      if (this.selectedDistrictIDs.length > 0) count++;
      if (this.selectedLoanType && Number(this.selectedLoanType) > 0) count++;
      if (this.selectedSchemeName && this.selectedSchemeName !== "All") count++;
      if (this.selectedBankIDs.length > 0) count++;
      if (this.selectedStatusIds.length > 0) count++;
      if (this.selectedCasteIDs.length > 0) count++;
      if (this.selectedGender && this.selectedGender !== "All") count++;
      if (this.ageMin || this.ageMax) count++;
      if (this.selectedClaimPaymentStatus && this.selectedClaimPaymentStatus !== "All") count++;
      if (this.applicationNoFilter) count++;
      if (this.applicantNameFilter) count++;
      if (this.loiNumberFilter) count++;
      if (this.mobileFilter) count++;
      if (this.dateFrom || this.dateTo) count++;
      return count;
    },
    selectedCompany() {
      if (this.selectedCompanyID === null || this.selectedCompanyID === undefined) return null;
      return this.subCompanies.find((c) => c.id === this.selectedCompanyID) || null;
    },
    sumAmountDemanded() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.AmountDemanded) || 0), 0).toFixed(2);
    },
    sumAmountApproved() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.AmountApproved) || 0), 0).toFixed(2);
    },
    sumDemand() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.Demand) || 0), 0).toFixed(2);
    },
    sumClaimAmount() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.ClaimAmount) || 0), 0).toFixed(2);
    },
    sumPaidAmt() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.PaidAmt) || 0), 0).toFixed(2);
    },
    sumReportedPaidAmt() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.ReportedPaidAmt) || 0), 0).toFixed(2);
    },
    totalApplications() {
      const sourceRows = this.isGroupedReportType(this.selectedReportType) ? this.reportBaseRows : this.reportData;
      if (String(this.selectedReportType || "").toLowerCase() === "financial_year_wise") {
        return (sourceRows || []).reduce((total, row) => total + (Number(row.Applications) || 0), 0);
      }
      return (sourceRows || []).length;
    },
    totalLOI() {
      const sourceRows = this.isGroupedReportType(this.selectedReportType) ? this.reportBaseRows : this.reportData;
      if (String(this.selectedReportType || "").toLowerCase() === "financial_year_wise") {
        return (sourceRows || []).reduce((total, row) => total + (Number(row.LOICount) || 0), 0);
      }
      return (sourceRows || []).filter((r) => this.shouldCountAsLoi(r)).length;
    },
    totalClaim() {
      if (String(this.selectedReportType || "").toLowerCase() === "financial_year_wise") {
        return (this.reportData || []).reduce((total, row) => total + (Number(row.ClaimCount || row.Claims) || 0), 0);
      }
      const sourceRows = this.isGroupedReportType(this.selectedReportType) ? this.reportBaseRows : this.reportData;
      const beneficiaries = {};
      (sourceRows || []).forEach((row) => {
        if ((parseFloat(row.ClaimAmount) || 0) <= 0) return;
        const beneficiaryKey = String(row.UserID || row.LoginID || row.AadhaarNo || row.LoanApplicationNo || row.ClaimNo || "").trim();
        if (beneficiaryKey) beneficiaries[beneficiaryKey] = true;
      });
      return Object.keys(beneficiaries).length;
    },
    locationSplitTitle() {
      return "Region-wise";
    },
    showStatisticalSplit() {
      const type = String(this.selectedReportType || "applications").toLowerCase();
      if (type === "company_wise" || type === "claims") return false;
      return type !== "applications" || this.searched;
    },
    isConsolidationSummary() {
      return String(this.selectedReportType || "").toLowerCase() === "consolidation_summary";
    },
    consolidationTotal() {
      return (this.reportData || []).find((row) => row.GroupLabel === "All" || row.GroupLabel === "Total")
        || { Beneficiaries: 0, PaidAmt: 0 };
    },
    showLoiSummary() {
      return ["loi", "sanction_letters"].indexOf(String(this.selectedReportType || "").toLowerCase()) !== -1;
    },
    showSchemeSummary() {
      return ["applications", "loi", "sanction_letters", "disbursements"].indexOf(String(this.selectedReportType || "").toLowerCase()) !== -1;
    },
    showClaimMetrics() {
      return ["claims", "claim_applicant_wise"].indexOf(String(this.selectedReportType || "").toLowerCase()) !== -1;
    },
    showFinancialYearPaidMetrics() {
      return String(this.selectedReportType || "").toLowerCase() === "financial_year_wise";
    },
    displaySchemeDetailFields() {
      const type = String(this.selectedReportType || "").toLowerCase();
      if (type === "disbursements" || type === "sanction_letters") {
        return (this.schemeDetailFields || []).filter((field) => {
          return ["Claims", "ApprovedAmount", "PaidAmount"].indexOf(field.key) === -1;
        });
      }
      return this.schemeDetailFields;
    },
    effectiveDateRangeText() {
      if (!this.dateFrom || !this.dateTo) return "";

      const selectedFrom = this.toDateSafe(this.dateFrom);
      const selectedTo = this.toDateSafe(this.dateTo);
      if (!selectedFrom || !selectedTo) return "";

      const orderedFrom = selectedFrom <= selectedTo ? selectedFrom : selectedTo;
      const orderedTo = selectedFrom <= selectedTo ? selectedTo : selectedFrom;

      const companyStartDate = this.getCompanyEstablishmentDate(this.selectedCompany);
      const effectiveFrom = companyStartDate && companyStartDate > orderedFrom ? companyStartDate : orderedFrom;
      return `${this.formatDateForApi(effectiveFrom)} to ${this.formatDateForApi(orderedTo)}`;
    },
    showStatPanel() {
      const type = String(this.selectedReportType || "applications").toLowerCase();
      return (panel) => {
        const panels = {
          applications: ["scheme", "application_status", "category", "caste", "gender", "year", "location", "beneficiary"],
          loi: ["scheme", "location"],
          sanction_letters: ["scheme", "location"],
          disbursements: ["scheme", "location"],
          claims: ["caste", "gender", "location", "claim_split"],
          claim_applicant_wise: ["claim_split"],
          financial_year_wise: ["year"],
          gender_wise: ["gender"],
          caste_wise: ["caste"],
          district_wise: ["location"],
          division_wise: ["location"],
          consolidation_summary: ["scheme"],
        };
        return (panels[type] || []).indexOf(panel) !== -1;
      };
    },
  },
  watch: {
    selectedReportType() {
      this.reportRequestId += 1;
      this.isLoading = false;
      this.searched = false;
      this.reportData = [];
      this.reportBaseRows = [];
      this.totalRows = 0;
      this.resetStats();
      this.selectedStatusId = 0;
      this.loadStatusOptionsByReportType();
      this.tableFields = this.withReportDateFormatters(this.getTableFieldsForReportType(this.selectedReportType));
      if (!this.reportTypeConfig.showSubCompany) {
        this.selectedCompanyID = null;
      }
    },
    selectedRegion() {
      if (this.selectedCompany && this.selectedCompany.tenantId === "spkemavm" && this.selectedRegion !== "Konkan") {
        this.selectedRegion = "Konkan";
        return;
      }
      this.applyRegionDistrictOptions();
    },
    selectedCompanyID() {
      this.selectedDistrictID = "00000000-0000-0000-0000-000000000000";
      this.selectedOffice = "";
      if (this.selectedCompany && this.selectedCompany.tenantId === "spkemavm") {
        this.selectedRegion = "Konkan";
      } else {
        this.selectedRegion = "All Regions";
      }
      this.applyCompanyStartDateToFilter();
      this.selectedCasteIDs = (this.scopedCasteOptions || []).map((option) => String(option.value));
      this.loadDistrictOptions();
      this.prefetchOfficeOptions();
    },
    selectedYear() {},
    selectedLoanType() {
      this.selectedSchemeName = "All";
      this.updateSchemeFilterOptions();
      this.prefetchOfficeOptions();
    },
    selectedDistrictID() {
      this.selectedOffice = "";
      this.prefetchOfficeOptions();
    }
  },
  methods: {
    resetFilters() {
      // Cancel any in-flight fetch and reset rendered state immediately.
      this.reportRequestId += 1;
      this.isLoading = false;
      this.searched = false;
      this.reportData = [];
      this.reportBaseRows = [];
      this.schemeDetailRows = [];
      this.beneficiaryReportRows = [];
      this.loiStatusRows = [];
      this.loiStatusSummary = { generated: 0, pending: 0, total: 0 };
      this.loiSummary = { count: 0, amount: 0 };
      this.appListApiDisabled = false;
      this.appListApiDisabledUntil = 0;
      this.supportsSanctionQueryApi = true;
      this.sanctionApiDisabledUntil = 0;
      this.resetStats();

      this.selectedReportType = "applications";
      this.selectedCompanyID = null;
      this.selectedRegion = "All Regions";
      this.selectedDistrictID = "00000000-0000-0000-0000-000000000000";
      this.selectedDistrictIDs = [];
      this.districtSearch = "";
      this.selectedLoanType = 0;
      this.selectedSchemeName = "All";
      this.selectedBankID = "00000000-0000-0000-0000-000000000000";
      this.selectedBankIDs = [];
      this.bankSearch = "";
      this.selectedGender = "All";
      this.ageMin = "";
      this.ageMax = "";
      this.amountFromFilter = "";
      this.amountToFilter = "";
      this.selectedStatusId = 0;
      this.selectedStatusIds = [];
      this.statusSearch = "";
      this.selectedClaimPaymentStatus = "All";
      this.applicationNoFilter = "";
      this.applicantNameFilter = "";
      this.loiNumberFilter = "";
      this.mobileFilter = "";
      this.claimFilter = "";
      this.selectedCasteIDs = [];
      this.casteSearch = "";
      this.tableFilter = "";
      this.initDateFilters();
      this.applyRegionDistrictOptions();
      this.loadStatusOptionsByReportType();
    },
    selectAllCastes() {
      this.selectedCasteIDs = (this.scopedCasteOptions || []).map((option) => String(option.value));
    },
    selectAllFilteredCastes() {
      const ids = (this.filteredCasteOptions || []).map((option) => String(option.value));
      const selectedLookup = {};
      (this.selectedCasteIDs || []).forEach((id) => {
        selectedLookup[String(id)] = true;
      });

      ids.forEach((id) => {
        selectedLookup[id] = true;
      });

      this.selectedCasteIDs = Object.keys(selectedLookup);
    },
    deselectFilteredCastes() {
      const filteredLookup = {};
      (this.filteredCasteOptions || []).forEach((option) => {
        filteredLookup[String(option.value)] = true;
      });

      this.selectedCasteIDs = (this.selectedCasteIDs || []).filter((id) => filteredLookup[String(id)] !== true);
    },
    clearSelectedCastes() {
      this.selectedCasteIDs = [];
    },
    selectAllDistricts() {
      this.selectedDistrictIDs = (this.districtOptions || [])
        .filter((d) => d && String(d.DistID) !== "00000000-0000-0000-0000-000000000000" && String(d.DistName).toLowerCase() !== "all")
        .map((d) => String(d.DistID));
      this.onDistrictChanged();
    },
    selectAllFilteredDistricts() {
      const ids = (this.filteredDistrictOptions || []).map((d) => String(d.value));
      const selectedLookup = {};
      (this.selectedDistrictIDs || []).forEach((id) => { selectedLookup[String(id)] = true; });
      ids.forEach((id) => { selectedLookup[id] = true; });
      this.selectedDistrictIDs = Object.keys(selectedLookup);
      this.onDistrictChanged();
    },
    deselectFilteredDistricts() {
      const filteredLookup = {};
      (this.filteredDistrictOptions || []).forEach((d) => { filteredLookup[String(d.value)] = true; });
      this.selectedDistrictIDs = (this.selectedDistrictIDs || []).filter((id) => filteredLookup[String(id)] !== true);
      this.onDistrictChanged();
    },
    clearSelectedDistricts() {
      this.selectedDistrictIDs = [];
      this.onDistrictChanged();
    },
    selectAllBanks() {
      this.selectedBankIDs = (this.bankOptions || [])
        .filter((b) => b && String(b.BankID) !== "00000000-0000-0000-0000-000000000000" && String(b.BankName).toLowerCase() !== "all")
        .map((b) => String(b.BankID));
    },
    selectAllFilteredBanks() {
      const ids = (this.filteredBankOptions || []).map((b) => String(b.value));
      const selectedLookup = {};
      (this.selectedBankIDs || []).forEach((id) => { selectedLookup[String(id)] = true; });
      ids.forEach((id) => { selectedLookup[id] = true; });
      this.selectedBankIDs = Object.keys(selectedLookup);
    },
    deselectFilteredBanks() {
      const filteredLookup = {};
      (this.filteredBankOptions || []).forEach((b) => { filteredLookup[String(b.value)] = true; });
      this.selectedBankIDs = (this.selectedBankIDs || []).filter((id) => filteredLookup[String(id)] !== true);
    },
    clearSelectedBanks() {
      this.selectedBankIDs = [];
    },
    selectAllStatuses() {
      this.selectedStatusIds = (this.statusOptions || [])
        .filter((s) => s && s.value !== 0 && String(s.text).toLowerCase() !== "all")
        .map((s) => String(s.value));
    },
    selectAllFilteredStatuses() {
      const ids = (this.filteredStatusOptions || []).map((s) => String(s.value));
      const selectedLookup = {};
      (this.selectedStatusIds || []).forEach((id) => { selectedLookup[String(id)] = true; });
      ids.forEach((id) => { selectedLookup[id] = true; });
      this.selectedStatusIds = Object.keys(selectedLookup);
    },
    deselectFilteredStatuses() {
      const filteredLookup = {};
      (this.filteredStatusOptions || []).forEach((s) => { filteredLookup[String(s.value)] = true; });
      this.selectedStatusIds = (this.selectedStatusIds || []).filter((id) => filteredLookup[String(id)] !== true);
    },
    clearSelectedStatuses() {
      this.selectedStatusIds = [];
    },
    getRegionByDistrictName(districtName) {
      const name = this.normalizeDistrictName(districtName);
      if (["mumbai city", "mumbai suburban", "thane", "palghar", "raigad", "ratnagiri", "sindhudurg", "मुंबई शहर", "मुंबई उपनगर", "ठाणे", "पालघर", "रायगड", "रत्नागिरी", "सिंधुदुर्ग"].indexOf(name) !== -1) {
        return "Konkan";
      }
      if (["pune", "satara", "sangli", "solapur", "kolhapur"].indexOf(name) !== -1) {
        return "Pune";
      }
      if (["nashik", "jalgaon", "ahilyanagar", "dhule", "nandurbar"].indexOf(name) !== -1) {
        return "Nashik";
      }
      if (["chhatrapati sambhajinagar", "jalna", "beed", "dharashiv", "latur", "nanded", "hingoli", "parbhani"].indexOf(name) !== -1) {
        return "Chhatrapati Sambhajinagar";
      }
      if (["amravati", "akola", "buldhana", "washim", "yavatmal"].indexOf(name) !== -1) {
        return "Amravati";
      }
      if (["nagpur", "wardha", "bhandara", "gondia", "chandrapur", "gadchiroli"].indexOf(name) !== -1) {
        return "Nagpur";
      }
      return "All Regions";
    },
    applyRegionDistrictOptions() {
      const allItem = { DistID: "00000000-0000-0000-0000-000000000000", DistName: "All" };
      const selectedRegion = String(this.selectedRegion || "All Regions").trim();
      let rows = (this.allDistrictRows || []).slice();

      if (selectedRegion && selectedRegion !== "All Regions") {
        rows = rows.filter((row) => {
          const name = String(row.DistName || row.DistrictName || row.District || "").trim();
          return this.getRegionByDistrictName(name) === selectedRegion;
        });
      }

      this.districtOptions = [allItem].concat(rows);
      if (!this.districtOptions.some((row) => String(row.DistID) === String(this.selectedDistrictID))) {
        this.selectedDistrictID = allItem.DistID;
      }
    },
    loadBankOptions() {
      return apis.ReadBankMaster()
        .then((response) => {
          const rows = response && response.status === 200 ? normalizeResponseArray(response) : [];
          this.bankOptions = [{ BankID: "00000000-0000-0000-0000-000000000000", BankName: "All" }].concat(rows);
        })
        .catch(() => {
          this.bankOptions = [{ BankID: "00000000-0000-0000-0000-000000000000", BankName: "All" }];
        });
    },
    loadCasteOptions() {
      return apis.getCaste()
        .then((response) => {
          const rows = response && response.status === 200 ? normalizeResponseArray(response) : [];
          this.casteOptions = rows
            .map((row) => {
              const code = row.CasteCode || row.CasteID || row.Id || row.ID;
              const name = row.CasteName || row.Caste || row.Name || "";
              const tenantId = row["TENENT ID"] !== undefined
                ? row["TENENT ID"]
                : (row["TENANT ID"] !== undefined ? row["TENANT ID"] : (row.TENENTID || row.TenentID || row.TenantID || ""));
              return {
                value: String(code || ""),
                text: String(name || "").trim(),
                casteTenantId: String(tenantId === null || tenantId === undefined ? "" : tenantId).trim()
              };
            })
            .filter((row) => row.value !== "" && row.text !== "");
        })
        .catch(() => {
          this.casteOptions = [];
        });
    },
    loadClaimStatusOptions() {
      return apis.getClaimStatusMstr()
        .then((response) => {
          const rows = normalizeResponseArray(response);
          const options = [{ value: 0, text: "All" }];
          const textById = {};
          rows.forEach((row) => {
            const id = parseInt(row.Id || row.StatusID || row.ApplicationStatusID, 10);
            const text = String(row.ApplicationStatus || row.Status || row.StatusName || "").trim();
            if (Number.isNaN(id) || id <= 0 || !text) return;
            textById[id] = text;
            options.push({ value: id, text });
          });
          this.statusOptions = options;
          this.statusTextById = textById;
        })
        .catch(() => {
          this.statusOptions = [{ value: 0, text: "All" }];
          this.statusTextById = {};
        });
    },
    loadStatusOptionsByReportType() {
      const type = String(this.selectedReportType || "applications").toLowerCase();
      if (type === "claims" || type === "claim_applicant_wise") {
        return this.loadClaimStatusOptions();
      }
      if (type === "applications" || type === "loi" || type === "sanction_letters" || type === "disbursements"
        || type === "district_wise" || type === "division_wise" || type === "gender_wise" || type === "caste_wise"
        || type === "financial_year_wise" || type === "company_wise") {
        return this.loadApplicationStatusOptions();
      }
      this.statusOptions = [{ value: 0, text: "All" }];
      this.statusTextById = {};
      return Promise.resolve();
    },
    isGroupedReportType(typeValue) {
      const type = String(typeValue || "").toLowerCase();
      return type === "financial_year_wise" || type === "gender_wise" || type === "caste_wise" || type === "district_wise" || type === "division_wise" || type === "claim_applicant_wise" || type === "consolidation_summary";
    },
    getTableFieldsForReportType(typeValue) {
      const type = String(typeValue || "applications").toLowerCase();
      if (type === "consolidation_summary") {
        return [
          { key: "GroupLabel", label: "Loan Type", sortable: true },
          { key: "Beneficiaries", label: "Total No. of Beneficiaries", sortable: true },
          { key: "MaleBeneficiaries", label: "Male Count", sortable: true },
          { key: "MalePaidAmt", label: "Male Amount", sortable: true, formatter: "formatWholeAmount" },
          { key: "FemaleBeneficiaries", label: "Female Count", sortable: true },
          { key: "FemalePaidAmt", label: "Female Amount", sortable: true, formatter: "formatWholeAmount" },
          { key: "PaidAmt", label: "Paid Amount", sortable: true, formatter: "formatWholeAmount" },
        ];
      }
      if (type === "company_wise") {
        return [
          { key: "Sr", label: "Sr.No.", thStyle: { width: "60px" } },
          { key: "CompanyName", label: "Company", sortable: true },
          { key: "Applications", label: "Applications", sortable: true },
          { key: "LOICount", label: "LOI Count", sortable: true },
          { key: "LOIAmount", label: "LOI Amount", sortable: true },
          { key: "ClaimAmount", label: "Claim Amount", sortable: true },
          { key: "PaidAmount", label: "Paid Amount", sortable: true },
        ];
      }
      if (type === "loi" || type === "sanction_letters") {
        return [
          { key: "Sr", label: "Sr.No.", thStyle: { width: "60px" } },
          { key: "CasteMarathi", label: "CasteMarathi", sortable: true },
          { key: "BusinessType", label: "BusinessType", sortable: true },
          { key: "SchemeType", label: "SchemeType", sortable: true },
          { key: "ApplicantName", label: "Applicant Name", sortable: true },
          { key: "MobileNumber", label: "Mobile Number", sortable: true },
          { key: "DistrictName", label: "District", sortable: true },
          { key: "OfficeName", label: "Office", sortable: true },
          { key: "LoanApplicationNo", label: "Application No.", sortable: true },
          { key: "ApplicationDate", label: "Application Date", sortable: true },
          { key: "AmountApproved", label: "Amount Approved", sortable: true },
          { key: "LOINumber", label: "LOI Number", sortable: true },
          { key: "LOIDate", label: "LOI Date", sortable: true },
          { key: "LOIDueDate", label: "LOI Due Date", sortable: true },
          { key: "BankName", label: "Bank Name", sortable: true },
          { key: "ApplicationStatus", label: "Status", sortable: true },
          { key: "ApplicationStatus1", label: "ApplicationStatus1", sortable: true },
        ];
      }

      if (type === "claims") {
        return [
          { key: "Sr", label: "Sr.No.", thStyle: { width: "60px" } },
          { key: "ApplicantName", label: "Applicant Name", sortable: true },
          { key: "MobileNumber", label: "Mobile Number", sortable: true },
          { key: "Gender", label: "Gender", sortable: true },
          { key: "DistrictName", label: "District", sortable: true },
          { key: "LoanApplicationNo", label: "Application No.", sortable: true },
          { key: "ClaimNo", label: "Claim ID", sortable: true },
          { key: "AppliedForClaimDtd", label: "Claim Date", sortable: true },
          { key: "Claim", label: "Claim Status", sortable: true },
          { key: "ClaimAmount", label: "Claim Amount", sortable: true },
          { key: "PaidAmt", label: "Paid Amt", sortable: true, formatter: "formatClaimPaidAmount" },
          { key: "BankName", label: "Bank Name", sortable: true },
        ];
      }

      if (type === "claim_applicant_wise") {
        return [
          { key: "Sr", label: "Sr.No.", thStyle: { width: "60px" } },
          { key: "ApplicantName", label: "Applicant Name", sortable: true },
          { key: "MobileNumber", label: "Mobile Number", sortable: true },
          { key: "Gender", label: "Gender", sortable: true },
          { key: "Districts", label: "District", sortable: true },
          { key: "ClaimCount", label: "No. of Claims", sortable: true },
          { key: "ClaimAmount", label: "Total Claim Amount", sortable: true },
          { key: "PaidAmt", label: "Total Paid Amt", sortable: true },
          { key: "ClaimNumbers", label: "Claim Numbers", sortable: false },
          { key: "ClaimDates", label: "Claim Dates", sortable: false },
          { key: "ClaimStatuses", label: "Claim Statuses", sortable: false },
        ];
      }

      if (this.isGroupedReportType(type)) {
        const groupLabels = {
          financial_year_wise: "Financial Year",
          gender_wise: "Gender",
          district_wise: "District",
          division_wise: "Division",
        };
        const label = groupLabels[type] || "Caste";
        return [
          { key: "Sr", label: "Sr.No.", thStyle: { width: "60px" } },
          { key: "GroupLabel", label, sortable: true },
          { key: "Applications", label: "Applications", sortable: true },
          { key: "LOICount", label: "LOI Count", sortable: true },
          { key: "AmountDemanded", label: "AmountDemanded", sortable: true },
          { key: "AmountApproved", label: "AmountApproved", sortable: true },
          ...(type === "financial_year_wise"
            ? [{ key: "PaidAmt", label: "Paid Amt", sortable: true }]
            : []),
        ];
      }

      return [
        { key: "Sr",                label: "Sr.No.",              thStyle: { width: "60px" } },
        { key: "CasteMarathi",      label: "CasteMarathi",        sortable: true },
        { key: "BusinessType",      label: "BusinessType",        sortable: true },
        { key: "SchemeType",        label: "SchemeType",          sortable: true },
        { key: "MobileNumber",      label: "Mobile Number",       sortable: true },
        { key: "DistrictName",      label: "District",            sortable: true },
        { key: "LoanApplicationNo", label: "LoanApplicationNo",   sortable: true },
        { key: "ApplicationDate",   label: "ApplicationDate",     sortable: true },
        { key: "AmountDemanded",    label: "AmountDemanded",      sortable: true },
        { key: "ApplicationStatus", label: "ApplicationStatus",   sortable: true },
        { key: "ApplicationStatus1",label: "ApplicationStatus1",  sortable: true },
          { key: "ClaimNo",           label: "Claim ID",            sortable: true },
        { key: "AmountApproved",    label: "AmountApproved",      sortable: true },
        { key: "LOINumber",         label: "LOINumber",           sortable: true },
        { key: "LOIDate",           label: "LOIDate",             sortable: true },
        { key: "BankName",          label: "BankName",            sortable: true },
        { key: "Demand",            label: "Demand",              sortable: true },
        { key: "ClaimAmount",       label: "Claim Amount",        sortable: true },
        { key: "AppliedForClaimDtd",label: "Applied for Claim dtd", sortable: true },
        { key: "PaidAmt",           label: "Paid Amt",            sortable: true },
        { key: "Claim",             label: "Claim",               sortable: true },
        { key: "PaidDt",            label: "Paid dt",             sortable: true },
      ];
    },
    withReportDateFormatters(fields) {
      return (fields || []).map((field) => {
        if (!field || typeof field === "string") return field;
        const key = String(field.key || "").toLowerCase();
        const isDate = key.indexOf("date") !== -1 || key.indexOf("dt") !== -1 || key.indexOf("created") !== -1 || key.indexOf("updated") !== -1 || key.indexOf("issued") !== -1 || key.indexOf("extended") !== -1;
        return isDate ? Object.assign({}, field, { formatter: "formatReportDate" }) : field;
      });
    },
    buildGroupedRows(rows, typeValue) {
      const type = String(typeValue || "applications").toLowerCase();
      if (!this.isGroupedReportType(type)) {
        return rows || [];
      }

      const groupMap = {};
      (rows || []).forEach((row) => {
        let label = "Not Known";
        let groupKey = "Not Known";
        if (type === "financial_year_wise") {
          label = this.getFinancialYearLabel(row.ApplicationDate || row.Createdate || row.CreateDate || row.CreatedDate || row.LOIDate || "");
        } else if (type === "gender_wise") {
          label = this.getNormalizedGenderFromRow(row);
        } else if (type === "claim_applicant_wise") {
          label = String(row.ApplicantName || row.FullName || row.FirstName || "").trim() || "Not Known";
        } else if (type === "caste_wise") {
          label = String(row.CasteMarathi || row.CasteName || row.Caste || row.SubCasteName || row.SubCaste || "").trim() || "Not Known";
        } else if (type === "district_wise") {
          label = String(row.DistrictName || row.District || row.DistName || "").trim() || "Not Known";
        } else if (type === "division_wise") {
          label = String(row.Division || row.DivisionName || "").trim() || "Not Known";
        }
        groupKey = type === "claim_applicant_wise"
          ? String(row.UserID || row.LoginID || row.UserLoginID || row.AadhaarNo || row.MobileNumber || label).trim()
          : label;

        if (!groupMap[groupKey]) {
          groupMap[groupKey] = {
            GroupLabel: label,
            ApplicantName: label,
            MobileNumber: "",
            ADDRESS: "",
            Applications: 0,
            LOICount: 0,
            Claims: 0,
            ClaimCount: 0,
            AmountDemanded: 0,
            AmountApproved: 0,
            ClaimAmount: 0,
            PaidAmt: 0,
            Districts: "",
            ClaimNumbers: "",
            ClaimDates: "",
            ClaimStatuses: "",
            _districtMap: {},
            _claimNoMap: {},
            _claimDateMap: {},
            _claimStatusMap: {},
          };
        }

        groupMap[groupKey].Applications += 1;
        if (this.shouldCountAsLoi(row)) {
          groupMap[groupKey].LOICount += 1;
        }
        const claimAmount = parseFloat(row.ClaimAmount) || 0;
        if (claimAmount > 0) {
          groupMap[groupKey].Claims += 1;
          groupMap[groupKey].ClaimCount += 1;
        }
        groupMap[groupKey].AmountDemanded += parseFloat(row.AmountDemanded) || 0;
        groupMap[groupKey].AmountApproved += parseFloat(row.AmountApproved) || 0;
        groupMap[groupKey].ClaimAmount += claimAmount;
        groupMap[groupKey].PaidAmt += parseFloat(row.PaidAmt) || 0;

        if (type === "claim_applicant_wise") {
          const districtName = String(row.DistrictName || row.District || "").trim();
          const claimNo = String(row.ClaimNo || "").trim();
          const claimDate = String(row.AppliedForClaimDtd || row.PaidDt || "").trim();
          const claimStatus = String(row.Claim || row.ApplicationStatus || "").trim();

          if (!groupMap[groupKey].MobileNumber) groupMap[groupKey].MobileNumber = String(row.MobileNumber || row.MobileNo || row.Mobile || row.ContactNo || "").trim();
          if (!groupMap[groupKey].ADDRESS) groupMap[groupKey].ADDRESS = String(row.ADDRESS || row.Address || "").trim();

          if (districtName) groupMap[groupKey]._districtMap[districtName] = true;
          if (claimNo) groupMap[groupKey]._claimNoMap[claimNo] = true;
          if (claimDate) groupMap[groupKey]._claimDateMap[claimDate] = true;
          if (claimStatus) groupMap[groupKey]._claimStatusMap[claimStatus] = true;
        }
      });

      return Object.keys(groupMap)
        .map((key) => {
          const row = groupMap[key];
          if (type === "claim_applicant_wise") {
            row.Districts = Object.keys(row._districtMap).join(", ");
            row.ClaimNumbers = Object.keys(row._claimNoMap).join(", ");
            row.ClaimDates = Object.keys(row._claimDateMap).join(", ");
            row.ClaimStatuses = Object.keys(row._claimStatusMap).join(", ");
          }
          delete row._districtMap;
          delete row._claimNoMap;
          delete row._claimDateMap;
          delete row._claimStatusMap;
          return row;
        })
        .sort((a, b) => {
          if (type === "financial_year_wise") {
            return String(a.GroupLabel).localeCompare(String(b.GroupLabel));
          }
          if (type === "claim_applicant_wise") {
            if ((b.ClaimAmount || 0) !== (a.ClaimAmount || 0)) {
              return (b.ClaimAmount || 0) - (a.ClaimAmount || 0);
            }
          }
          return String(a.GroupLabel).localeCompare(String(b.GroupLabel));
        });
    },
    mergeFinancialYearClaimTotals(groupedRows, claimRows) {
      const result = (groupedRows || []).map((row) => Object.assign({}, row));
      const groupByYear = {};
      result.forEach((row) => {
        groupByYear[String(row.GroupLabel || "")] = row;
      });

      const seen = {};
      (claimRows || []).forEach((claim) => {
        if (!this.isClaimPaidReportRow(claim)) return;

        const paymentKey = this.getClaimDedupKey(claim);
        if (paymentKey && seen[paymentKey]) return;
        if (paymentKey) seen[paymentKey] = true;

        const label = this.getFinancialYearLabel(this.getClaimPaidDate(claim));
        const paidAmount = this.getClaimPaidAmount(claim);
        if (!this.getClaimPaidDate(claim) || paidAmount <= 0) return;
        if (!groupByYear[label]) {
          groupByYear[label] = {
            GroupLabel: label, ApplicantName: label, Applications: 0, LOICount: 0,
            Claims: 0, ClaimCount: 0, AmountDemanded: 0, AmountApproved: 0,
            ClaimAmount: 0, PaidAmt: 0, Districts: "", ClaimNumbers: "",
            ClaimDates: "", ClaimStatuses: ""
          };
          result.push(groupByYear[label]);
        }

        const target = groupByYear[label];
        target.Claims += 1;
        target.ClaimCount += 1;
        target.ClaimAmount += paidAmount;
        target.PaidAmt += paidAmount;
      });

      return result.sort((a, b) => String(a.GroupLabel || "").localeCompare(String(b.GroupLabel || "")));
    },
    resetStats() {
      this.stats = {
        schemeWise: [],
        applicationStatusWise: [],
        categoryWise: [],
        casteWise: [],
        genderWise: [],
        yearWise: [],
        districtWise: [],
        beneficiarySplit: { oldCount: 0, newCount: 0 },
        claimSplit: { oldCount: 0, newCount: 0, oldAmount: 0, newAmount: 0 },
      };
    },
    textOrNotKnown(value) {
      if (value === undefined || value === null) return "Not Known";
      const text = String(value).trim();
      const normalized = text.toLowerCase();
      if (
        text === ""
        || normalized === "unknown"
        || normalized === "not known"
        || normalized === "unspecified"
        || normalized === "n/a"
        || normalized === "na"
        || normalized === "null"
        || normalized === "undefined"
      ) {
        return "Not Known";
      }
      return text;
    },
    firstKnownText(values, fallback = "Not Known") {
      const list = Array.isArray(values) ? values : [values];
      for (let i = 0; i < list.length; i++) {
        const text = this.textOrNotKnown(list[i]);
        if (text !== "Not Known") return text;
      }
      return fallback;
    },
    formatProfileAddress(profile) {
      const source = profile || {};
      const nestedAddress = source.LoanUserCorrespondenceAddress || source.CorrespondenceAddress || source.PermanentAddress || {};
      const directAddress = this.firstKnownText([
        source.ADDRESS,
        source.Address,
        source.FullAddress,
        source.ApplicantAddress,
        source.CurrentAddress,
        typeof source.PermanentAddress === "string" ? source.PermanentAddress : "",
      ], "");
      if (directAddress) return directAddress;

      const seen = {};
      return [
        nestedAddress.HouseNo,
        nestedAddress.BuildingNo,
        nestedAddress.Street,
        nestedAddress.Landmark,
        nestedAddress.Village,
        nestedAddress.Post,
        nestedAddress.TalukaName || nestedAddress.Taluka,
        nestedAddress.DistrictName || nestedAddress.District,
        nestedAddress.StateName || nestedAddress.State,
        nestedAddress.Pincode || nestedAddress.PinCode,
      ]
        .map((part) => String(part || "").trim())
        .filter((part) => {
          if (!part || this.textOrNotKnown(part) === "Not Known") return false;
          const key = part.toLowerCase();
          if (seen[key]) return false;
          seen[key] = true;
          return true;
        })
        .join(", ");
    },
    getApprovedAmountFromRow(row) {
      const source = row || {};
      const keys = [
        "AmountApproved", "SanctionAmount", "HOApprovedAmount", "ApprovedAmount", "TotalApprovedAmount",
        "SanctionedAmount", "AmountSanctioned", "LoanAmountApproved", "BankLoanAmount", "Demand", "AmountDemanded"
      ];
      for (let i = 0; i < keys.length; i++) {
        const value = source[keys[i]];
        if (value === undefined || value === null || value === "") continue;
        const numberValue = Number(String(value).replace(/,/g, ""));
        if (!Number.isNaN(numberValue) && numberValue > 0) return numberValue;
      }
      return 0;
    },
    applyNotKnownFallback(rows) {
      const textKeys = [
        "CasteMarathi",
        "AadhaarNo",
        "BusinessType",
        "SchemeType",
        "DistrictName",
        "OfficeName",
        "MobileNumber",
        "LoanApplicationNo",
        "ApplicationDate",
        "ApplicationStatus",
        "ApplicationStatus1",
        "LOIDueDate",
        "ClaimNo",
        "LOINumber",
        "LOIDate",
        "BankName",
        "ADDRESS",
        "Taluka",
        "AppliedForClaimDtd",
        "Claim",
        "PaidDt",
        "ApplicantName",
        "OfficeName",
        "Category",
        "GroupLabel",
        "Districts",
        "ClaimNumbers",
        "ClaimDates",
        "ClaimStatuses",
      ];

      return (rows || []).map((row) => {
        const next = { ...row };
        textKeys.forEach((key) => {
          next[key] = this.textOrNotKnown(next[key]);
        });
        next.Category = this.firstKnownText([next.Category, this.selectedCompany && this.selectedCompany.category], "Not Known");
        next.AmountApproved = this.getApprovedAmountFromRow(next);
        if ((parseFloat(next.ClaimAmount) || 0) <= 0 && (parseFloat(next.PaidAmt) || 0) <= 0) {
          next.AppliedForClaimDtd = "";
          next.Claim = "";
          next.PaidDt = "";
        }
        return next;
      });
    },
    formatAmount(value) {
      const num = parseFloat(value || 0) || 0;
      return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatWholeAmount(value) {
      const num = parseFloat(value || 0) || 0;
      return Math.round(num).toLocaleString("en-IN");
    },
    getNormalizedStatusText(row) {
      return String(row.ApplicationStatus || row.Status || row.ApplicationStatus1 || row.ClaimStatus || "")
        .trim()
        .toLowerCase();
    },
    normalizeStatusText(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ")
        .replace(/progeje|projeje|progect|projec\b/g, "project");
    },
    shouldCountAsLoi(row) {
      const statusText = this.getNormalizedStatusText(row);
      const loiNumber = String((row && row.LOINumber) || "").trim();
      const loiDate = this.toDateFlexible((row && row.LOIDate) || (row && row.IssueDate) || (row && row.SanctionDate) || "");

      const hasExcludedStatus =
        statusText.indexOf("hold") !== -1 ||
        statusText.indexOf("lapsed") !== -1 ||
        statusText.indexOf("returned") !== -1 ||
        statusText.indexOf("rejected") !== -1;

      if (hasExcludedStatus) {
        return false;
      }

      if (statusText.indexOf("approved by gm") !== -1) return true;
      if (statusText.indexOf("recommended") !== -1) return true;
      if (statusText.indexOf("approved") !== -1 && statusText.indexOf("extension") !== -1) return true;
      if (statusText.indexOf("loan in processing") !== -1) return true;
      if (statusText.indexOf("loi printed") !== -1) return true;
      if (statusText.indexOf("loi issued") !== -1) return true;

      if (loiNumber !== "" && !!loiDate) return true;

      if (!statusText) return false;

      const statusId = parseInt(
        row.ApplicationStatusID || row.ApplicationStatusId || row.StatusID || row.AppStatusID || 0,
        10
      );
      const loiStatusIds = {
        8: true,   // LOI Printed
        9: true,   // Loan In Processing
        16: true,  // Approved by GM
        27: true,  // LOI Extension: Approved by GM
        30: true,  // Recommended by DGM Project
      };

      return !!loiStatusIds[statusId];
    },
    toMapRows(mapObj) {
      return Object.keys(mapObj)
        .map((k) => ({ label: k, count: mapObj[k] }))
        .sort((a, b) => b.count - a.count);
    },
    topStatRows(rows, limit) {
      if (!Array.isArray(rows)) return [];
      return rows.slice(0, limit);
    },
    statPercent(value, rows) {
      const total = this.getStatsTotal(rows);
      const count = Number(value || 0);
      if (total <= 0 || count <= 0) return 0;
      const p = (count / total) * 100;
      return Math.max(2, Math.min(100, Number(p.toFixed(2))));
    },
    formatDateForApi(value) {
      if (!value) return "";
      const dt = this.toDateFlexible(value);
      if (!dt || Number.isNaN(dt.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()}`;
    },
    formatReportDate(value) {
      if (!value) return "";
      const formatted = this.formatDateForApi(value);
      return formatted || String(value);
    },
    formatReportDateField(value, key) {
      const dateKey = String(key || "").toLowerCase();
      if (dateKey.indexOf("date") !== -1 || dateKey.indexOf("dt") !== -1 || dateKey.indexOf("created") !== -1 || dateKey.indexOf("updated") !== -1 || dateKey.indexOf("issued") !== -1 || dateKey.indexOf("extended") !== -1) {
        return this.formatReportDate(value);
      }
      return value;
    },
    formatDateIso(value) {
      if (!value) return "";
      const dt = this.toDateFlexible(value);
      if (!dt || Number.isNaN(dt.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    },
    getSelectedDateRange() {
      if (!this.dateFrom || !this.dateTo) return null;
      const start = this.toDateFlexible(this.dateFrom);
      const end = this.toDateFlexible(this.dateTo);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
      const orderedStart = start <= end ? start : end;
      const orderedEnd = start <= end ? end : start;
      const startDate = new Date(orderedStart.getFullYear(), orderedStart.getMonth(), orderedStart.getDate());
      const endDate = new Date(orderedEnd.getFullYear(), orderedEnd.getMonth(), orderedEnd.getDate());
      return {
        startDate,
        endDate,
        startText: this.formatDateForApi(startDate),
        endText: this.formatDateForApi(endDate),
      };
    },
    normalizeSchemeName(value) {
      const raw = String(value || "").trim();
      if (!raw) return "Not Known";
      const lower = raw.toLowerCase();
      if (lower === "unknown" || lower === "unspecified" || lower === "na" || lower === "n/a") {
        return "Not Known";
      }

      const numeric = Number(raw);
      if (!Number.isNaN(numeric) && numeric > 0) {
        if (numeric === 1) return "Individual Loan";
        if (numeric === 2) return "Group Loan";
        if (numeric === 3) return "Education Loan";
      }

      return this.schemeNameMap[raw]
        || this.schemeNameMap[raw.toLowerCase()]
        || this.schemeNameMap[raw.toUpperCase()]
        || raw;
    },
    normalizeSchemeFilterToken(value) {
      return String(this.normalizeSchemeName(value) || "")
        .trim()
        .toLowerCase()
        .replace(/interest return scheme/g, "")
        .replace(/scheme/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },
    schemeMatchesFilter(rowScheme, selectedScheme) {
      const selected = this.normalizeSchemeFilterToken(selectedScheme);
      if (!selected || selected === "all" || selected === "not known") return true;
      const rowValue = this.normalizeSchemeFilterToken(rowScheme);
      if (!rowValue || rowValue === "not known") return false;
      return rowValue === selected || rowValue.indexOf(selected) !== -1 || selected.indexOf(rowValue) !== -1;
    },
    resolveSchemeNameFromRow(row, fallbackValue) {
      const source = row || {};
      const direct = this.normalizeSchemeName(
        source.SchemeType || source.SchemType || source.SchemeName || source.LoanTypeName || source.Scheme || ""
      );
      if (direct !== "Not Known") {
        return direct;
      }

      const loanTypeNum = Number(source.LoanType || source.LoanTypeID || source.SchemeTypeID || source.LoanTypeId || 0);
      if (loanTypeNum === 1) return "Individual Loan";
      if (loanTypeNum === 2) return "Group Loan";
      if (loanTypeNum === 3) return "Education Loan";

      const appNo = String(source.LoanApplicationNo || source.ApplicationNo || source.LoanApplicationNumber || "").trim().toUpperCase();
      if (appNo.indexOf("IL") === 0) return "Individual Loan";
      if (appNo.indexOf("GL") === 0) return "Group Loan";
      if (appNo.indexOf("EL") === 0) return "Education Loan";

      return this.normalizeSchemeName(fallbackValue);
    },
    loadSchemeNames() {
      return apis.getSchemes()
        .then((response) => {
          if (response.status !== 200) return;
          const rows = normalizeResponseArray(response);
          const map = {};
          rows.forEach((row) => {
            const schemeId = String(
              row.SchemeID
              || row.SchemeId
              || row.schemeId
              || row.Id
              || row.ID
              || row.SchemType
              || row.SchemeType
              || row.LoanTypeID
              || row.LoanTypeId
              || ""
            ).trim();
            const schemeName = String(
              row.SchemeName
              || row.Scheme
              || row.Name
              || row.DisplayName
              || row.LoanTypeName
              || row.SchemeMarathiName
              || ""
            ).trim();
            if (schemeId && schemeName) {
              map[schemeId] = schemeName;
              map[schemeId.toLowerCase()] = schemeName;
              map[schemeId.toUpperCase()] = schemeName;
            }
            if (schemeName) {
              map[schemeName] = schemeName;
              map[schemeName.toLowerCase()] = schemeName;
            }
          });
          this.schemeNameMap = map;

          const namesLookup = {};
          Object.keys(map).forEach((key) => {
            const name = String(map[key] || "").trim();
            if (name) {
              namesLookup[name] = true;
            }
          });

          const options = Object.keys(namesLookup)
            .sort((a, b) => a.localeCompare(b))
            .map((name) => ({ value: name, text: name }));

          this.allSchemeFilterOptions = options;
          this.updateSchemeFilterOptions();
        })
        .catch(() => {
          this.schemeNameMap = {};
          this.allSchemeFilterOptions = [];
          this.schemeFilterOptions = [{ value: "All", text: "All" }];
        });
    },
    updateSchemeFilterOptions() {
      const loanType = Number(this.selectedLoanType || 0);
      const typeTokens = {
        1: ["individual"],
        2: ["group"],
        3: ["education"],
      };
      const tokens = typeTokens[loanType] || [];
      const options = (this.allSchemeFilterOptions || []).filter((option) => {
        if (tokens.length === 0) return true;
        const text = String(option.text || option.value || "").toLowerCase();
        return tokens.some((token) => text.indexOf(token) !== -1);
      });
      this.schemeFilterOptions = [{ value: "All", text: "All" }].concat(options);
      if (!options.some((option) => option.value === this.selectedSchemeName)) {
        this.selectedSchemeName = "All";
      }
    },
    toDateSafe(value) {
      if (!value) return null;
      const dt = this.toDateFlexible(value);
      return Number.isNaN(dt.getTime()) ? null : dt;
    },
    toDateFlexible(value) {
      if (!value) return null;
      if (Object.prototype.toString.call(value) === "[object Date]") {
        return Number.isNaN(value.getTime()) ? null : value;
      }
      const raw = String(value).trim();
      if (!raw) return null;

      const isoDate = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
      if (isoDate) {
        const year = Number(isoDate[1]);
        const month = Number(isoDate[2]);
        const day = Number(isoDate[3]);
        if (day > 0 && month > 0 && month <= 12) {
          const dt = new Date(year, month - 1, day);
          if (!Number.isNaN(dt.getTime())) {
            return dt;
          }
        }
      }

      // Handle dd-mm-yyyy and dd/mm/yyyy values returned by legacy endpoints.
      const match = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:\s.*)?$/);
      if (match) {
        const day = Number(match[1]);
        const month = Number(match[2]);
        const year = Number(match[3]);
        if (day > 0 && month > 0 && month <= 12) {
          const dt = new Date(year, month - 1, day);
          if (!Number.isNaN(dt.getTime())) {
            return dt;
          }
        }
      }

      const parsed = new Date(raw);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }

      return null;
    },
    normalizeGender(value) {
      const normalizedInput = (value === undefined || value === null) ? "" : value;
      const g = String(normalizedInput).trim().toLowerCase();
      if (!g) return "Unknown";

      // Common backend patterns vary widely: some APIs use 0/1/2, others use M/F or text names.
      // Keep matching permissive and avoid dropping valid values when the source payload is sparse.
      if (g === "m" || g === "male" || g === "1" || g === "true" || g === "पुरुष" || g === "man") return "Male";
      if (g === "f" || g === "female" || g === "0" || g === "2" || g === "false" || g === "स्त्री" || g === "महिला" || g === "woman") return "Female";
      if (g === "mf" || g === "m/f" || g === "male/female" || g === "male female") return "Unknown";
      if (g.indexOf("female") !== -1) return "Female";
      if (g.indexOf("male") !== -1) return "Male";
      if (g === "other" || g === "unknown" || g === "na" || g === "n/a") return "Unknown";
      return "Unknown";
    },
    getNormalizedGenderFromRow(row) {
      if (!row || typeof row !== "object") return "Unknown";

      // Prefer raw/source gender fields first. A precomputed "Gender: Unknown"
      // should not block valid values that may exist in GenderRaw/GenderID/etc.
      const rawPriorityKeys = [
        "GenderRaw",
        "GenderID",
        "GenderId",
        "genderId",
        "GenderCode",
        "GenderValue",
        "GenderText",
        "GenderName",
        "genderName",
        "Sex",
        "sex",
        "SexCode",
        "SexName",
        "ApplicantGender",
        "applicantGender",
        "ApplicantGenderCode",
        "ApplicantGenderId",
        "UserGender",
        "userGender",
        "UserDetailGender",
        "userDetailGender",
        "GenderMarathi",
        "genderMarathi",
        "GenderType",
        "genderType",
        "MaleFemale",
        "maleFemale",
        "Gender",
        "gender",
      ];

      for (let i = 0; i < rawPriorityKeys.length; i++) {
        const key = rawPriorityKeys[i];
        const value = row[key];
        if (value === undefined || value === null || value === "") continue;

        const normalized = this.normalizeGender(value);
        if (normalized !== "Unknown") {
          return normalized;
        }
      }

      if (row.IsMale !== undefined && row.IsMale !== null && row.IsMale !== "") {
        return this.normalizeGender(row.IsMale ? "male" : "female");
      }
      if (row.isMale !== undefined && row.isMale !== null && row.isMale !== "") {
        return this.normalizeGender(row.isMale ? "male" : "female");
      }
      if (row.IsFemale !== undefined && row.IsFemale !== null && row.IsFemale !== "") {
        return this.normalizeGender(row.IsFemale ? "female" : "male");
      }
      if (row.isFemale !== undefined && row.isFemale !== null && row.isFemale !== "") {
        return this.normalizeGender(row.isFemale ? "female" : "male");
      }

      return "Unknown";
    },
    firstDefinedValue(obj, keys) {
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") {
          return obj[k];
        }
      }
      return "";
    },
    getFinancialYearLabel(dateValue) {
      const dt = this.toDateSafe(dateValue);
      if (!dt) return "Unknown";
      const y = dt.getFullYear();
      const m = dt.getMonth();
      const startY = m >= 3 ? y : y - 1;
      return `${startY}-${startY + 1}`;
    },
    getClaimPaymentStatusId(row) {
      const raw = row && (row.ClaimPaidStatus || row.ClaimPaymentStatusID || row.PaymentStatusID || "");
      const id = parseInt(raw, 10);
      return Number.isNaN(id) ? 0 : id;
    },
    mergeHistoricalEducationPayments(rows) {
      return (rows || []).map((row) => {
        const claimId = String(row && (row.ClaimEMIID || row.ClaimEMIId || row.ClaimID || row.ClaimId) || "").trim().toLowerCase();
        const payment = educationPaymentByClaimId[claimId];
        if (!payment) return row;
        const paidAmount = Number(payment.ApprovedAmount || payment.PaymentAmount || 0);
        return Object.assign({}, row, {
          LoanType: 3,
          PaidDate: payment.PaidDate || payment.PaymentDate,
          PaidDt: payment.PaidDate || payment.PaymentDate,
          ClaimPaidAmt: paidAmount,
          PaidAmt: paidAmount,
          ClaimPaidStatus: 3,
          ClaimPaymentStatusID: 3,
          ClaimPaymentStatus: "Sent to Bank",
          HistoricalPaymentSource: payment.SourceFile || "Education payment ledger",
        });
      });
    },
    isClaimSentToBank(row) {
      const statusId = this.getClaimPaymentStatusId(row);
      const statusText = String(row && (row.ClaimPaymentStatus || row.PaymentStatus || "") || "").trim().toLowerCase();
      return statusId === 3 || statusText.indexOf("sent to bank") !== -1;
    },
    isClaimPaidReportRow(row) {
      const statusId = this.getClaimPaymentStatusId(row);
      const statusText = String(row && (row.ClaimPaymentStatus || row.PaymentStatus || "") || "").trim().toLowerCase();
      const claimStatusText = String(row && (row.ClaimStatus || row.Claim || "") || "").trim().toLowerCase();
      const claimStatusId = Number(row && (row.ClaimStatusID || row.ClaimStatusId || row.ClaimStatus || 0));
      const loanType = Number(row && (row.LoanType || row.LoanTypeID || row.SchemeTypeID || 0));
      const hasHistoricalPayment = !!String(row && row.HistoricalPaymentSource || "").trim();
      return hasHistoricalPayment
        || statusId === 3 || statusId === 4
        || statusText.indexOf("sent to bank") !== -1
        || statusText.indexOf("bank confirmed") !== -1
        || claimStatusId === 5 || claimStatusText === "paid";
    },
    getClaimPaidDate(row) {
      if (!row || typeof row !== "object") return "";
      const authoritativePaidDate = row.PaidDate || row.PaidDt || "";
      if (authoritativePaidDate) return String(authoritativePaidDate).trim();
      if (this.isClaimSentToBank(row)) {
        const sentDate = row.UpdatedDate || row.EMIPaidDate || row.ClaimDate || row.AppliedForClaimDtd || row.CreatedDate || "";
        return String(sentDate || "").trim();
      }
      const dateValue = row.EMIPaidDate || row.ClaimDate || row.AppliedForClaimDtd || row.UpdatedDate || row.CreatedDate || row.LOIDate || row.ApplicationDate || "";
      return String(dateValue || "").trim();
    },
    getClaimPaidAmount(row) {
      if (!row || typeof row !== "object") return 0;
      const loanType = Number(row.LoanType || row.LoanTypeID || row.SchemeTypeID || 0);
      const keys = loanType === 2
        ? ["ClaimPaidAmt", "PaidAmt", "PaidAmount", "ApprovedAmount", "ClaimAmount", "InterestAmt"]
        : ["ClaimPaidAmt", "PaidAmt", "PaidAmount", "ApprovedAmount", "ClaimAmount", "InterestAmt"];
      for (const key of keys) {
        const value = parseFloat(String(row[key] === undefined || row[key] === null ? "" : row[key]).replace(/,/g, ""));
        if (!Number.isNaN(value) && value > 0) return value;
      }
      const paidDate = this.getClaimPaidDate(row);
      if (paidDate && (parseFloat(row.ApprovedAmount || row.ClaimAmount || 0) || 0) > 0) {
        return parseFloat(row.ApprovedAmount || row.ClaimAmount || 0) || 0;
      }
      return 0;
    },
    formatClaimPaidAmount(value, key, row) {
      return this.getClaimPaidAmount(Object.assign({}, row || {}, { PaidAmt: value }));
    },
    getClaimDedupKey(row) {
      const claimId = String(
        row && (row.ClaimEMIID || row.ClaimEMIId || row.ClaimID || row.ClaimId || row.ClaimNo || row.ClaimNumber || row.ClaimApplicationNo || row.ClaimNoTx || "")
      ).trim();
      const paymentDate = this.getClaimPaidDate(row).slice(0, 10);
      return `${claimId}|${paymentDate}`;
    },
    normalizeBeneficiaryType(value) {
      const t = String(value || "").trim().toLowerCase();
      if (!t) return "";
      if (t === "old" || t.indexOf("existing") !== -1 || t.indexOf("old") !== -1 || t === "1" || t === "true") return "Old";
      if (t === "new" || t.indexOf("new") !== -1 || t === "0" || t === "false") return "New";
      return "";
    },
    getClaimLoanType(row) {
      const source = row || {};
      const numericType = Number(source.LoanType || source.LoanTypeID || source.SchemeTypeID || source.ConsolidationLoanType || 0);
      if ([1, 2, 3].indexOf(numericType) !== -1) return numericType;
      const schemeText = String(source.SchemeType || source.SchemeName || source.LoanTypeName || source.Scheme || "").toLowerCase();
      if (schemeText.indexOf("education") !== -1) return 3;
      if (schemeText.indexOf("group") !== -1) return 2;
      if (schemeText.indexOf("individual") !== -1) return 1;
      const applicationNo = String(source.LoanApplicationNo || source.ApplicationNo || source.LoanApplicationNumber || "").toUpperCase();
      if (/-GL[-/]/.test(applicationNo) || applicationNo.indexOf("GL") !== -1) return 2;
      if (/-EL[-/]/.test(applicationNo) || applicationNo.indexOf("EL") !== -1) return 3;
      if (/-IL[-/]/.test(applicationNo) || applicationNo.indexOf("IL") !== -1) return 1;
      return 0;
    },
    normalizeCompanyType(company) {
      return String((company && company.type) || "").trim().toLowerCase();
    },
    isSpecializedCompany(company) {
      const companyType = this.normalizeCompanyType(company);
      if (companyType === "specialized") return true;
      if (companyType === "universal") return false;
      return !!(company && Array.isArray(company.castes) && company.castes.length > 0);
    },
    getAllowedCompanyCasteNameSet(company) {
      const allowedNameSet = {};
      if (!company || !Array.isArray(company.castes)) return allowedNameSet;
      company.castes.forEach((caste) => {
        const normalized = this.normalizeCasteText(caste && caste.casteName);
        if (normalized) {
          allowedNameSet[normalized] = true;
        }
        this.splitCasteTokens(caste && caste.casteName).forEach((token) => {
          if (token) {
            allowedNameSet[token] = true;
          }
        });
      });

      // Castes tagged with this company's ObcCastes.[TENENT ID] are additive, so master
      // data can extend membership without editing the caste list above.
      this.getMasterCastesForCompany(company).forEach((option) => {
        const normalized = this.normalizeCasteText(option.text);
        if (normalized) {
          allowedNameSet[normalized] = true;
        }
        this.splitCasteTokens(option.text).forEach((token) => {
          if (token) {
            allowedNameSet[token] = true;
          }
        });
      });

      return allowedNameSet;
    },
    getMasterCastesForCompany(company) {
      const companyTenantId = String(company && company.casteTenantId !== undefined ? company.casteTenantId : "").trim();
      if (!companyTenantId) return [];

      return (this.casteOptions || []).filter((option) => {
        return String(option && option.casteTenantId || "").trim() === companyTenantId;
      });
    },
    normalizeCasteText(value) {
      let text = String(value || "").trim().toLowerCase();
      if (!text) return "";

      // Drop common numeric/code prefixes like "0 -", "43-", "125 :"
      text = text.replace(/^\s*\d+\s*[-:.)]\s*/g, "");

      // Normalize punctuation and spaces for stable matching.
      text = text
        .replace(/[()]/g, " ")
        .replace(/[\u2000-\u206F\u2E00-\u2E7F\\'"`]/g, "")
        .replace(/\s+/g, " ")
        .trim();

      // Master data mixes "काथार-वाणी", "काथार वाणी" and "काथारवाणी" for the same caste,
      // so separators are dropped entirely when building a match key.
      text = text.replace(/[-\s]+/g, "");

      // Master data has entries like "174-पांचाळ सुतार." with stray edge punctuation.
      text = text.replace(/^[.,:;|]+/, "").replace(/[.,:;|]+$/, "");

      return text;
    },
    casteDisplayKey(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/^\s*\d+\s*[-:.)]\s*/g, "")
        .replace(/\s+/g, " ")
        .trim();
    },
    splitCasteTokens(value) {
      const raw = String(value || "").trim();
      if (!raw) return [];

      // "किंवा" is Marathi for "or", used in master names like "13-बारी किंवा बारई".
      return raw
        .split(/[;,/|]|\sकिंवा\s/)
        .map((part) => this.normalizeCasteText(part))
        .filter((part) => part !== "");
    },
    getRowCasteTokens(row) {
      return []
        .concat(this.splitCasteTokens(row && row.CasteName))
        .concat(this.splitCasteTokens(row && row.Caste))
        .concat(this.splitCasteTokens(row && row.CasteMarathi))
        .concat(this.splitCasteTokens(row && row.SubCaste))
        .concat(this.splitCasteTokens(row && row.SubCasteName));
    },
    enforceSpecializedCompanyRowGuard(rows, company) {
      if (!this.isSpecializedCompany(company)) {
        return rows || [];
      }

      const allowedNameSet = this.getAllowedCompanyCasteNameSet(company);
      const effectiveStartDate = this.getCompanyEstablishmentDate(company);

      return (rows || []).filter((row) => {
        const tokens = this.getRowCasteTokens(row);
        if (tokens.length === 0) return false;

        const hasCasteMatch = tokens.some((token) => allowedNameSet[token] === true);
        if (!hasCasteMatch) return false;

        if (!effectiveStartDate) return true;

        const rowDate = this.toDateFlexible(
          row.ApplicationDate || row.Createdate || row.CreateDate || row.CreatedDate || row.LOIDate || row.RegeneratedOn || ""
        );
        return !!(rowDate && rowDate >= effectiveStartDate);
      });
    },
    filterCasteWiseStatsByCompany(rows, company) {
      if (!this.isSpecializedCompany(company)) {
        return rows || [];
      }

      const allowedNameSet = this.getAllowedCompanyCasteNameSet(company);
      return (rows || []).filter((row) => {
        const label = String((row && row.label) || "").trim();
        if (!label) return false;

        const normalizedLabel = this.normalizeCasteText(label);
        if (normalizedLabel && allowedNameSet[normalizedLabel] === true) {
          return true;
        }

        const tokens = this.splitCasteTokens(label);
        return tokens.some((token) => allowedNameSet[token] === true);
      });
    },
    hasAnyCastePayload(row) {
      const textKeys = ["CasteName", "Caste", "CasteMarathi", "casteName", "caste", "SubCaste", "SubCasteName"];
      const idKeys = ["CasteID", "CasteCode", "SubCasteID", "casteId", "casteCode", "SubCasteCode"];

      const hasText = textKeys.some((k) => String(row[k] || "").trim() !== "");
      const hasId = idKeys.some((k) => {
        const val = row[k];
        return val !== undefined && val !== null && String(val).trim() !== "";
      });

      return hasText || hasId;
    },
    rowMatchesCompanyCaste(row, allowedCodeSet, allowedNameSet, options = {}) {
      const enforceNameOnly = options && options.enforceNameOnly === true;
      const allowedCasteIdSet = (options && options.allowedCasteIdSet) || {};
      const idKeys = ["CasteID", "CasteCode", "SubCasteID", "casteId", "casteCode", "SubCasteCode"];
      const textKeys = ["CasteName", "Caste", "CasteMarathi", "casteName", "caste", "SubCaste", "SubCasteName"];

      const hasMatchingCasteGuid = idKeys.some((k) => {
        const val = String(row[k] || "").trim().toLowerCase();
        return !!(val && allowedCasteIdSet[val] === true);
      });
      if (hasMatchingCasteGuid) return true;

      if (!enforceNameOnly) {
        const hasMatchingId = idKeys.some((k) => {
          const val = Number(row[k]);
          return !Number.isNaN(val) && allowedCodeSet[val] === true;
        });
        if (hasMatchingId) return true;
      }

      for (let i = 0; i < textKeys.length; i++) {
        const value = row[textKeys[i]];
        const tokens = this.splitCasteTokens(value);
        if (tokens.some((t) => allowedNameSet[t] === true)) {
          return true;
        }
      }

      return false;
    },
    matchesTextFilter(value, filterText) {
      const text = String(value || "").toLowerCase();
      const filter = String(filterText || "").trim().toLowerCase();
      return !filter || text.indexOf(filter) !== -1;
    },
    normalizeDistrictName(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[\u2000-\u206F\u2E00-\u2E7F\\'"`]/g, "")
        .replace(/\s+/g, " ");
    },
    getAllowedDistrictNamesForSelectedCompany(companyOverride) {
      const company = companyOverride || this.selectedCompany;
      if (company && company.tenantId === "spkemavm") {
        return [
          "Mumbai City",
          "Mumbai Suburban",
          "Thane",
          "Palghar",
          "Raigad",
          "Ratnagiri",
          "Sindhudurg",
        ];
      }

      // Parent and other universal reports expose all districts.
      return [];
    },
    getSelectedDistrictName() {
      const selected = (this.districtOptions || []).find((d) => String(d.DistID) === String(this.selectedDistrictID));
      return selected && selected.DistName ? selected.DistName.toString().trim().toLowerCase() : "";
    },
    getEffectiveDistrictId() {
      const allDistrictId = "00000000-0000-0000-0000-000000000000";
      const districtId = String(this.selectedDistrictID || "").trim();
      return districtId || allDistrictId;
    },
    mergeUniqueApplications(rows) {
      const merged = [];
      const seen = {};
      (rows || []).forEach((row) => {
        const key = String(
          row.LoanApplicationID || row.ApplicationID || row.LoanApplicationNo || ""
        ).trim();
        if (!key || seen[key]) return;
        seen[key] = true;
        merged.push(row);
      });
      return merged;
    },
    sortApplicationsDeterministically(rows) {
      return (rows || []).slice().sort((a, b) => {
        const aDate = this.toDateFlexible(a.ApplicationDate || a.Createdate || a.CreateDate || a.CreatedDate || "");
        const bDate = this.toDateFlexible(b.ApplicationDate || b.Createdate || b.CreateDate || b.CreatedDate || "");
        const aTs = aDate ? aDate.getTime() : 0;
        const bTs = bDate ? bDate.getTime() : 0;
        if (aTs !== bTs) return aTs - bTs;

        const aNo = String(a.LoanApplicationNo || "");
        const bNo = String(b.LoanApplicationNo || "");
        const byNo = aNo.localeCompare(bNo);
        if (byNo !== 0) return byNo;

        const aId = String(a.LoanApplicationID || a.ApplicationID || "");
        const bId = String(b.LoanApplicationID || b.ApplicationID || "");
        return aId.localeCompare(bId);
      });
    },
    getAllStatusIds() {
      // Known full application status ID range (ApplicationStatusMaster 1-34).
      // Used as a robust fallback so a transient failure of the lightweight
      // status-master API call doesn't silently collapse a report's data down
      // to a single status (which previously caused specialized-company/caste
      // reports to show "No records found" even when matching data existed).
      const knownStatusIds = Array.from({ length: 34 }, (_, i) => i + 1);
      return apis.getApplicationStatusMstr()
        .then((response) => {
          const rows = normalizeResponseArray(response);
          const ids = rows
            .map((r) => parseInt(r.Id, 10))
            .filter((id) => !Number.isNaN(id) && id > 0);
          if (ids.length > 0) return ids;
          return knownStatusIds;
        })
        .catch(() => knownStatusIds);
    },
    loadApplicationStatusOptions() {
      return apis.getApplicationStatusMstr()
        .then((response) => {
          const rows = normalizeResponseArray(response);
          const options = [{ value: 0, text: "All" }];
          const textById = {};
          const seen = {};

          rows.forEach((row) => {
            const id = parseInt(row.Id || row.StatusID || row.ApplicationStatusID, 10);
            const text = String(row.ApplicationStatus || row.Status || row.StatusName || "").trim();
            if (Number.isNaN(id) || id <= 0 || !text || seen[id]) {
              return;
            }
            seen[id] = true;
            textById[id] = text;
            options.push({ value: id, text: text });
          });

          options.sort((a, b) => {
            if (a.value === 0) return -1;
            if (b.value === 0) return 1;
            return a.value - b.value;
          });

          this.statusOptions = options;
          this.statusTextById = textById;
        })
        .catch(() => {
          this.statusOptions = [{ value: 0, text: "All" }];
          this.statusTextById = {};
        });
    },
    getHttpStatus(err) {
      if (!err) return 0;
      if (typeof err.status === "number") return err.status;
      if (err.response && typeof err.response.status === "number") return err.response.status;
      return 0;
    },
    isNetworkOrCorsError(err) {
      const status = this.getHttpStatus(err);
      const message = String(
        (err && (err.message || err.statusText || (err.body && err.body.message))) || ""
      ).toLowerCase();

      if (status !== 0) return false;

      return (
        message.indexOf("cors") !== -1 ||
        message.indexOf("network") !== -1 ||
        message.indexOf("failed") !== -1 ||
        message.indexOf("xmlhttprequest") !== -1 ||
        message.indexOf("err_failed") !== -1 ||
        message.indexOf("fetch") !== -1
      );
    },
    withRequestTimeout(promise, fallbackValue, timeoutMs) {
      const ms = Number(timeoutMs || this.apiTimeoutMs || 45000);
      let timer = null;
      const timeoutPromise = new Promise((resolve) => {
        timer = setTimeout(() => {
          resolve({ __requestTimedOut: true });
        }, ms);
      });

      return Promise.race([promise, timeoutPromise])
        .then((result) => {
          if (result && result.__requestTimedOut) {
            return fallbackValue;
          }
          return result;
        })
        .catch(() => fallbackValue)
        .finally(() => {
          if (timer) clearTimeout(timer);
        });
    },
    canUseSanctionApi() {
      // Financial Year Wise figures come from claims, not sanction letters. Skip the
      // sanction/LOI query for it (the backend rejects it with HTTP 400) to avoid the
      // console error and speed up the report.
      if (String(this.selectedReportType || "").toLowerCase() === "financial_year_wise") return false;
      if (this.supportsSanctionQueryApi) return true;
      if (Date.now() >= Number(this.sanctionApiDisabledUntil || 0)) {
        this.supportsSanctionQueryApi = true;
        this.sanctionApiDisabledUntil = 0;
        return true;
      }
      return false;
    },
    markSanctionApiTemporarilyDisabled() {
      this.supportsSanctionQueryApi = false;
      this.sanctionApiDisabledUntil = Date.now() + 10000;
    },
    fetchApplicationsByStatus(districtId, loanType, startDateText, endDateText, statusId) {
      const now = Date.now();
      if (this.appListApiDisabled && now < Number(this.appListApiDisabledUntil || 0)) {
        return Promise.resolve([]);
      }
      if (this.appListApiDisabled && now >= Number(this.appListApiDisabledUntil || 0)) {
        this.appListApiDisabled = false;
        this.appListApiDisabledUntil = 0;
      }

      const numericLoanType = Number(loanType || 0);
      if (numericLoanType === 0) {
        // usp_GetLoanApplications requires a specific LoanType (1=Individual, 2=Group, 3=Education)
        const loanTypes = [1, 2, 3];
        return Promise.all(
          loanTypes.map((lt) => this.fetchApplicationsByStatus(districtId, lt, startDateText, endDateText, statusId))
        ).then((results) => {
          const combined = [].concat.apply([], results || []);
          return this.sortApplicationsDeterministically(this.mergeUniqueApplications(combined));
        });
      }

      const applicationNoFilter = String(this.applicationNoFilter || "").trim();
      const loiNumberFilter = String(this.loiNumberFilter || "").trim();
      const hasDirectIdentifierFilter = applicationNoFilter !== "" || loiNumberFilter !== "";

      // Keep backend payload in known-working sentinel mode.
      // Date filtering and district/status filtering are applied client-side.
      const allDistrictId = "00000000-0000-0000-0000-000000000000";
      const fromDate = "01-01-1970";
      const toDate = "01-01-1970";
      const baseFilter = {
        DistrictID: hasDirectIdentifierFilter ? (districtId || allDistrictId) : allDistrictId,
        LoanType: numericLoanType,
        MobileNo: "",
        ApplicationNo: applicationNoFilter,
        StartDate: fromDate,
        EndDate: toDate,
      };

      const selectedStatusId = Number(statusId || 0);

      const fetchOneStatus = (oneStatusId) => {
        return apis.ReadApplicationList({
          ...baseFilter,
          ApplicationStatusID: oneStatusId,
        }).then((rr) => normalizeResponseArray(rr)).catch((err) => {
          if (this.isNetworkOrCorsError(err)) {
            this.appListApiDisabled = true;
            this.appListApiDisabledUntil = Date.now() + 60000;
          }
          return [];
        });
      };

      const fetchAllByStatuses = () => {
        return this.getAllStatusIds().then((statusIds) => {
          const requests = statusIds.map((oneStatusId) => fetchOneStatus(oneStatusId));
          return Promise.all(requests).then((sets) => {
            const merged = [].concat.apply([], sets || []);
            return merged;
          });
        }).catch(() => []);
      };

      if (selectedStatusId > 0) {
        return fetchOneStatus(selectedStatusId)
          .then((rows) => this.sortApplicationsDeterministically(this.mergeUniqueApplications(rows)));
      }

      // Direct-identifier searches should never fan-out to all statuses because
      // a single focused query is enough and much faster.
      if (hasDirectIdentifierFilter) {
        return fetchOneStatus(0)
          .then((rows) => {
            if ((rows || []).length > 0 || (districtId || allDistrictId) === allDistrictId) {
              return this.sortApplicationsDeterministically(this.mergeUniqueApplications(rows || []));
            }
            return apis.ReadApplicationList({
              ...baseFilter,
              DistrictID: allDistrictId,
              ApplicationStatusID: 0,
            }).then((rr) => normalizeResponseArray(rr)).catch(() => [])
              .then((fallbackRows) => this.sortApplicationsDeterministically(this.mergeUniqueApplications(fallbackRows || [])));
          })
          .catch(() => []);
      }

      // For "All" status, union status=0 and fan-out status-wise rows for consistency
      // across backend environments where status=0 can be partial.
      // Fast path: if status=0 already returns rows, avoid expensive fan-out.
      return fetchOneStatus(0)
        .then((statusZeroRows) => {
          if (this.appListApiDisabled) {
            return this.sortApplicationsDeterministically(this.mergeUniqueApplications(statusZeroRows || []));
          }
          if (Array.isArray(statusZeroRows) && statusZeroRows.length > 0) {
            return this.sortApplicationsDeterministically(this.mergeUniqueApplications(statusZeroRows || []));
          }
          return fetchAllByStatuses().then((fanoutRows) => {
            const merged = this.mergeUniqueApplications([].concat(statusZeroRows || [], fanoutRows || []));
            return this.sortApplicationsDeterministically(merged);
          });
        })
        .catch(() => []);
    },
    buildOfficeOptionsFromRows(rows) {
      const map = {};
      (rows || []).forEach((row) => {
        const officeName = (row.OfficeName || row.BranchName || row.LoanBranch || row.BankName || "").toString().trim();
        if (officeName) {
          map[officeName] = true;
        }
      });
      const options = Object.keys(map)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, text: name }));
      this.officeOptions = [{ value: "", text: "All" }].concat(options);
      if (this.selectedOffice && !map[this.selectedOffice]) {
        this.selectedOffice = "";
      }
    },
    loadDistrictOptions() {
      const stateId = "BB62F7B0-748D-4E64-AD4F-8EF691431FA1";
      const all = [{ DistID: "00000000-0000-0000-0000-000000000000", DistName: "All" }];

      return apis.getDistricts(stateId)
        .then((response) => {
          if (response.status !== 200) return;
          const rows = normalizeResponseArray(response);
          this.allDistrictRows = rows;
          this.districtOptions = all.concat(rows);
          this.applyRegionDistrictOptions();
        })
        .catch(() => {
          this.allDistrictRows = [];
          this.districtOptions = all;
        });
    },
    onDistrictChanged() {
      this.selectedOffice = "";
      this.prefetchOfficeOptions();
    },
    prefetchOfficeOptions() {
      if (this.selectedCompanyID === null || this.selectedCompanyID === undefined) {
        this.officeOptions = [{ value: "", text: "All" }];
        this.selectedOffice = "";
        return;
      }

      // Avoid expensive prefetch API calls while user is changing filters.
      // Office options are rebuilt from the final report dataset after Search.
      this.officeOptions = [{ value: "", text: "All" }];
      this.selectedOffice = "";
    },
    applyReportFilters(rows) {
      const config = this.reportTypeConfig;
      const selectedCompanyType = this.normalizeCompanyType(this.selectedCompany);
      const isSpecializedCompany = selectedCompanyType === "specialized";
      const filters = this.getSelectedDateRange();
      const amountFrom = parseFloat(this.amountFromFilter);
      const amountTo = parseFloat(this.amountToFilter);
      const selectedStatusId = Number(this.selectedStatusId || 0);
      const selectedStatusText = this.normalizeStatusText(this.statusTextById[selectedStatusId] || "");
      const selectedStatusIdsLookup = {};
      (this.selectedStatusIds || []).forEach((id) => {
        const num = Number(id);
        if (!Number.isNaN(num) && num > 0) {
          selectedStatusIdsLookup[num] = true;
        }
      });
      const hasMultiStatus = Object.keys(selectedStatusIdsLookup).length > 0;

      const applicationNoFilter = String(this.applicationNoFilter || this.claimFilter || "").trim();
      const applicantNameFilter = String(this.applicantNameFilter || "").trim();
      const loiNoFilter = String(this.loiNumberFilter || "").trim();
      const mobileFilter = String(this.mobileFilter || "").trim();
      const officeFilter = this.selectedOffice || this.officeFilter;
      const districtFilter = this.districtFilter;
      const allDistrictId = "00000000-0000-0000-0000-000000000000";

      const selectedDistrictId = String(this.selectedDistrictID || "").trim();
      const selectedDistrictName = this.getSelectedDistrictName();
      const selectedDistrictIdsLookup = {};
      const selectedDistrictNamesLookup = {};
      (this.selectedDistrictIDs || []).forEach((id) => {
        const strId = String(id).trim();
        if (strId && strId !== allDistrictId) {
          selectedDistrictIdsLookup[strId.toLowerCase()] = true;
          const found = (this.districtOptions || []).find((d) => String(d.DistID).toLowerCase() === strId.toLowerCase());
          if (found && found.DistName) {
            selectedDistrictNamesLookup[this.normalizeDistrictName(found.DistName)] = true;
          }
        }
      });
      const hasMultiDistrict = Object.keys(selectedDistrictIdsLookup).length > 0;

      const selectedRegion = String(this.selectedRegion || "All Regions").trim();

      const selectedBankId = String(this.selectedBankID || "00000000-0000-0000-0000-000000000000").trim();
      const selectedBank = (this.bankOptions || []).find((b) => String(b.BankID) === selectedBankId);
      const selectedBankName = String((selectedBank && selectedBank.BankName) || "").trim().toLowerCase();
      const selectedBankIdsLookup = {};
      const selectedBankNamesLookup = {};
      (this.selectedBankIDs || []).forEach((id) => {
        const strId = String(id).trim();
        if (strId && strId !== allDistrictId) {
          selectedBankIdsLookup[strId.toLowerCase()] = true;
          const found = (this.bankOptions || []).find((b) => String(b.BankID).toLowerCase() === strId.toLowerCase());
          if (found && found.BankName) {
            selectedBankNamesLookup[String(found.BankName).trim().toLowerCase()] = true;
          }
        }
      });
      const hasMultiBank = Object.keys(selectedBankIdsLookup).length > 0;

      const selectedSchemeName = String(this.selectedSchemeName || "All").trim();
      const selectedClaimPaymentStatus = String(this.selectedClaimPaymentStatus || "All").trim();
      const selectedGender = String(this.selectedGender || "All").trim().toLowerCase();
      const ageMin = Number(this.ageMin);
      const ageMax = Number(this.ageMax);
      const selectedCasteSet = {};
      const selectedCasteNameSet = {};
      (this.selectedCasteIDs || []).forEach((id) => {
        selectedCasteSet[String(id)] = true;
        const found = (this.scopedCasteOptions || []).find((c) => String(c.value) === String(id)) ||
          (this.casteOptions || []).find((c) => String(c.value) === String(id));
        if (found && found.text) {
          const normalizedText = this.normalizeCasteText(found.text);
          if (normalizedText) {
            selectedCasteNameSet[normalizedText] = true;
          }
          this.splitCasteTokens(found.text).forEach((token) => {
            if (token) {
              selectedCasteNameSet[token] = true;
            }
          });
        }
      });
      // For specialized companies the company caste list is the source of truth, but only
      // as a fallback: honouring an explicit narrower selection is what lets a single
      // caste actually filter the report.
      if (isSpecializedCompany) {
        const allowedCompanyNameSet = this.getAllowedCompanyCasteNameSet(this.selectedCompany);
        const selectedCount = (this.selectedCasteIDs || []).length;
        const availableCount = (this.scopedCasteOptions || []).length;
        const isNarrowedSelection = selectedCount > 0 && availableCount > 0 && selectedCount < availableCount;

        if (isNarrowedSelection) {
          Object.keys(selectedCasteNameSet).forEach((name) => {
            if (allowedCompanyNameSet[name] !== true) {
              delete selectedCasteNameSet[name];
            }
          });
        }

        if (!isNarrowedSelection || Object.keys(selectedCasteNameSet).length === 0) {
          Object.assign(selectedCasteNameSet, allowedCompanyNameSet);
        }
      }
      const allCastesSelected = !isSpecializedCompany &&
        (this.casteOptions || []).length > 0 &&
        (this.selectedCasteIDs || []).length >= (this.casteOptions || []).length;
      const restrictedDistrictNames = this.getAllowedDistrictNamesForSelectedCompany();
      const restrictedDistrictLookup = {};
      restrictedDistrictNames.forEach((name) => {
        restrictedDistrictLookup[this.normalizeDistrictName(name)] = true;
      });

      return (rows || []).filter((row) => {
        const isClaimReport = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(String(this.selectedReportType || "").toLowerCase()) !== -1;
        const rowDate = this.toDateFlexible(
          isClaimReport
            ? (row.PaidDt || row.PaidDate || row.AppliedForClaimDtd || row.EMIPaidDate || "")
            : (row.ApplicationDate || row.Createdate || row.CreateDate || row.CreatedDate || row.LOIDate || row.RegeneratedOn || "")
        );
        if (filters && (!rowDate || Number.isNaN(rowDate.getTime()) || rowDate < filters.startDate || rowDate > filters.endDate)) {
          return false;
        }

        const rowDistrictId = String(row.DistrictID || row.DistrictId || row.DistID || "").trim();
        const rowDistrictName = (row.DistrictName || row.District || row.DistName || "").toString().trim();
        const rowDistrictKey = this.normalizeDistrictName(rowDistrictName);
        if (restrictedDistrictNames.length > 0 && !restrictedDistrictLookup[rowDistrictKey]) {
          return false;
        }

        if (selectedRegion && selectedRegion !== "All Regions") {
          if (this.getRegionByDistrictName(rowDistrictName) !== selectedRegion) {
            return false;
          }
        }

        if (hasMultiDistrict) {
          const matchId = rowDistrictId && selectedDistrictIdsLookup[rowDistrictId.toLowerCase()];
          const matchName = rowDistrictKey && selectedDistrictNamesLookup[rowDistrictKey];
          if (!matchId && !matchName) return false;
        } else if (selectedDistrictId && selectedDistrictId !== allDistrictId) {
          if (rowDistrictId) {
            if (rowDistrictId !== selectedDistrictId) return false;
          } else if (selectedDistrictName && this.normalizeDistrictName(rowDistrictName) !== this.normalizeDistrictName(selectedDistrictName)) {
            return false;
          }
        }

        if (!this.matchesTextFilter([row.LoanApplicationNo, row.ApplicationNo, row.ClaimNo, row.Claim].join(" "), applicationNoFilter)) {
          return false;
        }
        if (!this.matchesTextFilter([row.ApplicantName, row.FullName, row.FirstName, row.MiddleName, row.LastName].join(" "), applicantNameFilter)) {
          return false;
        }
        if (config.showLoiNo && !this.matchesTextFilter([row.LOINumber, row.LOI, row.LOINo].join(" "), loiNoFilter)) {
          return false;
        }
        if (config.showMobile && !this.matchesTextFilter([row.MobileNo, row.Mobile, row.MobileNumber, row.ContactNo].join(" "), mobileFilter)) {
          return false;
        }
        if (!this.matchesTextFilter([row.OfficeName, row.BranchName, row.LoanBranch, row.BankName, row.Taluka].join(" "), officeFilter)) {
          return false;
        }
        if (!this.matchesTextFilter([row.DistrictName, row.District].join(" "), districtFilter)) {
          return false;
        }

        if (config.showLoanType) {
          const selectedLoanType = Number(this.selectedLoanType || 0);
          if (selectedLoanType > 0) {
            const rowLoanType = this.getClaimLoanType(row);
            if (rowLoanType !== selectedLoanType) {
              return false;
            }
          }

          if (selectedSchemeName && selectedSchemeName !== "All") {
            const rowScheme = this.normalizeSchemeName(
              row.SchemeType || row.SchemType || row.SchemeName || row.LoanTypeName || row.LoanType || row.LoanTypeID || ""
            );
            if (!this.schemeMatchesFilter(rowScheme, selectedSchemeName)) {
              return false;
            }
          }
        }

        if (hasMultiBank) {
          const rowBankId = String(row.BankID || row.BankId || "").trim().toLowerCase();
          const rowBankName = String(row.BankName || row.LoanBranch || row.BranchName || "").trim().toLowerCase();
          const matchId = rowBankId && selectedBankIdsLookup[rowBankId];
          const matchName = rowBankName && selectedBankNamesLookup[rowBankName];
          if (!matchId && !matchName) return false;
        } else if (selectedBankId !== "00000000-0000-0000-0000-000000000000") {
          const rowBankId = String(row.BankID || row.BankId || "").trim();
          if (rowBankId) {
            if (rowBankId !== selectedBankId) {
              return false;
            }
          } else if (selectedBankName) {
            const rowBankName = String(row.BankName || row.LoanBranch || row.BranchName || "").trim().toLowerCase();
            if (!rowBankName || ["unknown", "not known", "n/a", "na", "null"].indexOf(rowBankName) !== -1 || rowBankName !== selectedBankName) {
              return false;
            }
          }
        }

        const isClaimsGenderReport = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(String(this.selectedReportType || "").toLowerCase()) !== -1;
        if (config.showGender && selectedGender !== "all" && !isClaimsGenderReport) {
          const rowGender = this.getNormalizedGenderFromRow(row).toLowerCase();
          if (rowGender !== selectedGender) {
            return false;
          }
        }

        if (config.showAge && (!Number.isNaN(ageMin) || !Number.isNaN(ageMax))) {
          const rowAge = Number(row.Age || row.ApplicantAge || row.CurrentAge || 0);
          if (!Number.isNaN(ageMin) && rowAge > 0 && rowAge < ageMin) return false;
          if (!Number.isNaN(ageMax) && rowAge > 0 && rowAge > ageMax) return false;
        }

        if (config.showCaste && !allCastesSelected && Object.keys(selectedCasteSet).length > 0) {
          const tokens = this.getRowCasteTokens(row);
          if (isSpecializedCompany) {
            if (tokens.length === 0) {
              return false;
            }
            const hasNameMatch = tokens.some((t) => selectedCasteNameSet[t] === true);
            if (!hasNameMatch) {
              return false;
            }
          } else {
            const rowCasteId = String(row.CasteID || row.CasteCode || row.SubCasteID || row.casteId || row.casteCode || "").trim();
            if (rowCasteId) {
              if (!selectedCasteSet[rowCasteId]) {
                return false;
              }
            } else if (tokens.length > 0) {
              const hasNameMatch = tokens.some((t) => selectedCasteNameSet[t] === true);
              if (!hasNameMatch) {
                return false;
              }
            }
          }
        }

        if (config.showStatus) {
          const rowStatusId = parseInt(
            row.ApplicationStatusID || row.ApplicationStatusId || row.StatusID || row.AppStatusID,
            10
          );
          const rowStatusText = this.normalizeStatusText(
            row.ApplicationStatus || row.Status || row.ApplicationStatus1 || ""
          );

          if (hasMultiStatus) {
            const matchId = !Number.isNaN(rowStatusId) && selectedStatusIdsLookup[rowStatusId];
            if (!matchId) return false;
          } else if (selectedStatusId > 0) {
            if (!Number.isNaN(rowStatusId) && rowStatusId > 0) {
              if (rowStatusId !== selectedStatusId) {
                return false;
              }
              // Keep displayed status text aligned with selected status where available.
              const isClaimReport = ["claims", "claim_applicant_wise"].indexOf(String(this.selectedReportType || "").toLowerCase()) !== -1;
              if (!isClaimReport && selectedStatusText && rowStatusText && rowStatusText !== selectedStatusText && rowStatusText.indexOf(selectedStatusText) === -1 && selectedStatusText.indexOf(rowStatusText) === -1) {
                return false;
              }
            } else {
              if (!selectedStatusText || rowStatusText !== selectedStatusText) {
                return false;
              }
            }
          }
        }

        if (config.showClaimPaymentStatus && selectedClaimPaymentStatus !== "All") {
          const paidAmt = parseFloat(row.PaidAmt || row.ClaimPaidAmt || row.PaidAmount || 0) || 0;
          const claimAmt = parseFloat(row.ClaimAmount || row.TotalClaimAmount || 0) || 0;
          const claimPaidStatusId = this.getClaimPaymentStatusId(row);
          const rowClaimStatus = this.normalizeStatusText(
            row.Claim || row.ClaimStatus || row.ClaimPaymentStatus || row.ApplicationStatus || ""
          );

          let rowPaymentStatus = "Unpaid";
          if (this.isClaimPaidReportRow(row) || paidAmt > 0) {
            rowPaymentStatus = "Paid";
          } else if (claimPaidStatusId === 1 || rowClaimStatus.indexOf("pending") !== -1 || claimAmt <= 0) {
            rowPaymentStatus = "Pending";
          }

          if (rowPaymentStatus !== selectedClaimPaymentStatus) {
            return false;
          }
        }

        const rowAmount = parseFloat(row.AmountApproved || row.AmountDemanded || row.ClaimAmount || row.Demand || 0) || 0;
        if (!Number.isNaN(amountFrom) && rowAmount < amountFrom) return false;
        if (!Number.isNaN(amountTo) && rowAmount > amountTo) return false;

        return true;
      });
    },
    prepareStats(rows) {
      const schemeMap = {};
      const statusMap = {};
      const categoryMap = {};
      const casteMap = {};
      const genderMap = {};
      const yearMap = {};
      const districtMap = {};
      const beneficiarySplit = { oldCount: 0, newCount: 0 };
      const detailMap = {};
      const claimSplit = { oldCount: 0, newCount: 0, oldAmount: 0, newAmount: 0 };
      const countedClaimBeneficiaries = {};

      const selectedRange = this.getSelectedDateRange();
      const fyRange = this.getDateRangeFromFY(this.selectedYear || "2020-2021");
      const fyStart = selectedRange ? selectedRange.startDate : fyRange.fyStart;
      const fyEnd = selectedRange ? selectedRange.endDate : fyRange.fyEnd;
      const reportType = String(this.selectedReportType || "applications").toLowerCase();
      rows.forEach((r) => {
        const scheme = this.textOrNotKnown(this.normalizeSchemeName(r.SchemeType || r.SchemType || r.SchemeName || r.Scheme || r.LoanTypeName || ""));
        const status = this.textOrNotKnown(r.ApplicationStatus);
        const isLoiReport = reportType === "loi" || reportType === "sanction_letters";
        const shouldCountLifecycleLoi = isLoiReport && this.shouldCountAsLoi(r);
        const category = this.firstKnownText([r.Category, this.selectedCompany && this.selectedCompany.category, r.CasteCategory, r.CategoryName]);
        const caste = this.firstKnownText([r.CasteMarathi, r.CasteName, r.Caste, r.SubCasteName, r.SubCaste]);
        const gender = this.getNormalizedGenderFromRow(r);
        const year = this.getFinancialYearLabel(r.ApplicationDate || r.Createdate || r.CreateDate || r.CreatedDate);
        const district = this.textOrNotKnown(r.DistrictName || r.District || r.DistName);
        const locationLabel = this.getRegionByDistrictName(district);

        schemeMap[scheme] = (schemeMap[scheme] || 0) + 1;
        statusMap[status] = (statusMap[status] || 0) + 1;
        if (shouldCountLifecycleLoi) {
          if (status !== "Approved by GM") {
            statusMap["Approved by GM"] = (statusMap["Approved by GM"] || 0) + 1;
          }
          if (status !== "LOI Printed" && status !== "LOI Issued" && status !== "LOI Printed/Issued") {
            statusMap["LOI Printed/Issued"] = (statusMap["LOI Printed/Issued"] || 0) + 1;
          }
          if ((r.IsExtended || r.IsLOIExtended) && status !== "LOI Extended") {
            statusMap["LOI Extended"] = (statusMap["LOI Extended"] || 0) + 1;
          }
        }
        categoryMap[category] = (categoryMap[category] || 0) + 1;
        casteMap[caste] = (casteMap[caste] || 0) + 1;
        genderMap[gender] = (genderMap[gender] || 0) + 1;
        yearMap[year] = (yearMap[year] || 0) + 1;
        districtMap[locationLabel] = (districtMap[locationLabel] || 0) + 1;

        const explicitType = this.normalizeBeneficiaryType(
          r.BeneficiaryType || r.OldNewBeneficiary || r.IsOldBeneficiary || r.ExistingLoanBeneficiary
        );
        if (explicitType === "Old") {
          beneficiarySplit.oldCount += 1;
        } else if (explicitType === "New") {
          beneficiarySplit.newCount += 1;
        } else {
          const firstDate = this.toDateSafe(r.BeneficiaryFirstDate || r.FirstApplicationDate);
          if (firstDate && firstDate < fyStart) {
            beneficiarySplit.oldCount += 1;
          } else {
            beneficiarySplit.newCount += 1;
          }
        }

        if (!detailMap[scheme]) {
          detailMap[scheme] = {
            Scheme: scheme,
            Applications: 0,
            LOIIssued: 0,
            Claims: 0,
            DemandedAmount: 0,
            ApprovedAmount: 0,
            PaidAmount: 0,
          };
        }
        detailMap[scheme].Applications += 1;
        detailMap[scheme].DemandedAmount += parseFloat(r.AmountDemanded) || 0;
        detailMap[scheme].ApprovedAmount += this.getApprovedAmountFromRow(r);
        detailMap[scheme].PaidAmount += parseFloat(r.PaidAmt) || 0;
        if (this.shouldCountAsLoi(r)) {
          detailMap[scheme].LOIIssued += 1;
        }
        if ((parseFloat(r.ClaimAmount) || 0) > 0) {
          detailMap[scheme].Claims += 1;
        }

        const claimAmount = parseFloat(r.ClaimAmount) || 0;
        if (claimAmount > 0) {
          const beneficiaryKey = String(r.UserID || r.LoginID || r.AadhaarNo || r.LoanApplicationNo || r.ClaimNo || "").trim();
          const isFirstClaimForBeneficiary = beneficiaryKey && !countedClaimBeneficiaries[beneficiaryKey];
          if (beneficiaryKey) countedClaimBeneficiaries[beneficiaryKey] = true;
          const claimDate = this.toDateSafe(r.AppliedForClaimDtd || r.PaidDt);
          const isNew = claimDate && claimDate >= fyStart && claimDate <= fyEnd;
          if (isNew) {
            if (isFirstClaimForBeneficiary) claimSplit.newCount += 1;
            claimSplit.newAmount += claimAmount;
          } else {
            if (isFirstClaimForBeneficiary) claimSplit.oldCount += 1;
            claimSplit.oldAmount += claimAmount;
          }
        }
      });

      this.stats = {
        schemeWise: this.toMapRows(schemeMap),
        applicationStatusWise: this.toMapRows(statusMap),
        categoryWise: this.toMapRows(categoryMap),
        casteWise: this.filterCasteWiseStatsByCompany(this.toMapRows(casteMap), this.selectedCompany).slice(0, 5),
        genderWise: this.toMapRows(genderMap),
        yearWise: this.toMapRows(yearMap),
        districtWise: this.toMapRows(districtMap),
        beneficiarySplit,
        claimSplit,
      };
      this.schemeDetailRows = Object.keys(detailMap)
        .map((key) => detailMap[key])
        .sort((a, b) => b.Applications - a.Applications || a.Scheme.localeCompare(b.Scheme));
    },
    normalizeStatsList(rows, labelKeyCandidates, countKeyCandidates) {
      if (!Array.isArray(rows)) return [];
      return rows.map((r) => {
        var label = "";
        var count = 0;

        if (Array.isArray(r)) {
          label = (r[0] === undefined || r[0] === null || r[0] === "") ? "Not Known" : String(r[0]);
          count = parseInt(r[1], 10) || 0;
          return { label, count };
        }

        labelKeyCandidates.some((k) => {
          if (r[k] !== undefined && r[k] !== null && r[k] !== "") {
            label = String(r[k]);
            return true;
          }
          return false;
        });

        countKeyCandidates.some((k) => {
          if (r[k] !== undefined && r[k] !== null && r[k] !== "") {
            count = parseInt(r[k], 10) || 0;
            return true;
          }
          return false;
        });

        return { label: label || "Not Known", count };
      }).sort((a, b) => b.count - a.count);
    },
    getStatsTotal(rows) {
      if (!Array.isArray(rows) || rows.length === 0) return 0;
      return rows.reduce((sum, row) => sum + (parseInt(row.count, 10) || 0), 0);
    },
    normalizeStatsPayload(payload) {
      if (Array.isArray(payload)) {
        return {
          schemeWise: payload[0] || [],
          applicationWise: payload[1] || [],
          categoryWise: payload[2] || [],
          casteWise: payload[3] || [],
          genderWise: payload[4] || [],
          claimSplit: payload[5] || [],
          yearWise: payload[6] || [],
          districtWise: payload[7] || [],
          beneficiarySplit: payload[8] || [],
        };
      }

      if (payload && typeof payload === "object") {
        // Already in named-shape format.
        if (
          payload.schemeWise !== undefined ||
          payload.applicationWise !== undefined ||
          payload.genderWise !== undefined
        ) {
          return payload;
        }

        // Handle DataSet-like payloads: { Table: [], Table1: [], ... }.
        const tableKeys = Object.keys(payload)
          .filter((k) => Array.isArray(payload[k]))
          .sort((a, b) => {
            const aNum = a === "Table" ? 0 : parseInt(String(a).replace("Table", ""), 10);
            const bNum = b === "Table" ? 0 : parseInt(String(b).replace("Table", ""), 10);
            const aVal = Number.isNaN(aNum) ? Number.MAX_SAFE_INTEGER : aNum;
            const bVal = Number.isNaN(bNum) ? Number.MAX_SAFE_INTEGER : bNum;
            return aVal - bVal;
          });

        if (tableKeys.length > 0) {
          const arr = tableKeys.map((k) => payload[k]);
          return {
            schemeWise: arr[0] || [],
            applicationWise: arr[1] || [],
            categoryWise: arr[2] || [],
            casteWise: arr[3] || [],
            genderWise: arr[4] || [],
            claimSplit: arr[5] || [],
            yearWise: arr[6] || [],
            districtWise: arr[7] || [],
            beneficiarySplit: arr[8] || [],
          };
        }
      }

      return payload || {};
    },
    applyBackendStats(statsResponse) {
      if (!statsResponse) return;

      const keepExistingIfEmpty = (backendRows, existingRows) => {
        if (Array.isArray(backendRows) && backendRows.length > 0) return backendRows;
        return Array.isArray(existingRows) ? existingRows : [];
      };

      var schemeWise = this.normalizeStatsList(
        statsResponse.schemeWise,
        ["Scheme"],
        ["TotalApplications"]
      ).map((row) => ({
        label: this.normalizeSchemeName(row.label),
        count: row.count,
      }));
      var applicationStatusWise = this.normalizeStatsList(
        statsResponse.applicationWise,
        ["ApplicationStatus"],
        ["TotalApplications"]
      );
      var categoryWise = this.normalizeStatsList(
        statsResponse.categoryWise,
        ["Category"],
        ["TotalApplications"]
      );
      var casteWise = this.normalizeStatsList(
        statsResponse.casteWise,
        ["Caste"],
        ["TotalApplications"]
      );
      casteWise = this.filterCasteWiseStatsByCompany(casteWise, this.selectedCompany).slice(0, 5);
      var genderWise = this.normalizeStatsList(
        statsResponse.genderWise,
        ["Gender", "MaleFemale", "GenderWise", "Label", "Type"],
        ["TotalApplications"]
      ).map((row) => ({
        label: this.normalizeGender(row.label),
        count: row.count,
      })).reduce((acc, row) => {
        const existing = acc.find((item) => item.label === row.label);
        if (existing) {
          existing.count += row.count;
        } else {
          acc.push(row);
        }
        return acc;
      }, []).sort((a, b) => b.count - a.count);

      // If backend sends only Unknown for gender but client-side parsing
      // already detected Male/Female from raw rows, keep client values.
      const backendKnownGenderCount = genderWise
        .filter((x) => x.label !== "Unknown")
        .reduce((s, x) => s + (x.count || 0), 0);
      const clientKnownGenderCount = (this.stats.genderWise || [])
        .filter((x) => x.label !== "Unknown")
        .reduce((s, x) => s + (x.count || 0), 0);
      if (backendKnownGenderCount === 0 && clientKnownGenderCount > 0) {
        genderWise = this.stats.genderWise;
      }

      var yearWise = this.normalizeStatsList(
        statsResponse.yearWise,
        ["FinancialYear", "Year", "ApplicationYear"],
        ["TotalApplications", "BeneficiaryCount"]
      );
      var districtWiseFromBackend = this.normalizeStatsList(
        statsResponse.districtWise,
        ["District", "DistrictName", "DistName"],
        ["TotalApplications", "BeneficiaryCount"]
      );

      // Some backend branches still return office-wise rows in table 8.
      // Keep deterministic client-computed district split when backend labels
      // do not look like real district names.
      var knownDistrictLookup = {};
      (this.allDistrictRows || []).forEach((row) => {
        var key = this.normalizeDistrictName(row && (row.DistName || row.DistrictName || row.District));
        if (key) knownDistrictLookup[key] = true;
      });

      var validDistrictRows = districtWiseFromBackend.filter((row) => {
        var label = this.normalizeDistrictName(row && row.label);
        if (!label || label === "not known") return false;
        return knownDistrictLookup[label] === true;
      });

      var districtWise = validDistrictRows;

      // Guard against backend overcount/mismatched scope. When backend totals do not
      // match the currently filtered report rows, keep deterministic client-side stats.
      const backendApplicationTotal = this.getStatsTotal(schemeWise);
      const visibleRowsTotal = this.isGroupedReportType(this.selectedReportType)
        ? (Array.isArray(this.reportBaseRows) ? this.reportBaseRows.length : 0)
        : (Array.isArray(this.reportData) ? this.reportData.length : 0);
      if (visibleRowsTotal > 0 && backendApplicationTotal > 0 && backendApplicationTotal !== visibleRowsTotal) {
        return;
      }

      var beneficiarySplit = { oldCount: 0, newCount: 0 };
      if (Array.isArray(statsResponse.beneficiarySplit)) {
        statsResponse.beneficiarySplit.forEach((b) => {
          var type = "";
          var count = 0;
          if (Array.isArray(b)) {
            type = this.normalizeBeneficiaryType(b[0]);
            count = parseInt(b[1], 10) || 0;
          } else {
            type = this.normalizeBeneficiaryType(b.BeneficiaryType || b.Type || b.OldNewBeneficiary);
            count = parseInt(b.TotalBeneficiaries || b.BeneficiaryCount || b.TotalApplications, 10) || 0;
          }
          if (type === "Old") {
            beneficiarySplit.oldCount += count;
          } else if (type === "New") {
            beneficiarySplit.newCount += count;
          }
        });
      }

      if (beneficiarySplit.oldCount === 0 && beneficiarySplit.newCount === 0) {
        beneficiarySplit = this.stats.beneficiarySplit || { oldCount: 0, newCount: 0 };
      }

      var claimSplit = { oldCount: 0, newCount: 0, oldAmount: 0, newAmount: 0 };
      if (Array.isArray(statsResponse.claimSplit)) {
        statsResponse.claimSplit.forEach((c) => {
          var type = "";
          var count = 0;
          var amount = 0;
          if (Array.isArray(c)) {
            type = String(c[0] || "").toLowerCase();
            count = parseInt(c[1], 10) || 0;
            amount = parseFloat(c[2]) || 0;
          } else {
            type = String(c.ClaimType || "").toLowerCase();
            count = parseInt(c.TotalClaims, 10) || 0;
            amount = parseFloat(c.TotalClaimAmount) || 0;
          }
          if (type === "new") {
            claimSplit.newCount += count;
            claimSplit.newAmount += amount;
          } else {
            claimSplit.oldCount += count;
            claimSplit.oldAmount += amount;
          }
        });
      }

      if (
        claimSplit.oldCount === 0 &&
        claimSplit.newCount === 0 &&
        Number(claimSplit.oldAmount || 0) === 0 &&
        Number(claimSplit.newAmount || 0) === 0
      ) {
        claimSplit = this.stats.claimSplit || { oldCount: 0, newCount: 0, oldAmount: 0, newAmount: 0 };
      }

      if (String(this.selectedReportType || "").toLowerCase() === "caste_wise" && Array.isArray(statsResponse.casteWise)) {
        const casteTotals = {};
        statsResponse.casteWise.forEach((row) => {
          const label = Array.isArray(row) ? row[0] : (row.Caste || row.CasteMarathi || row.CasteName || row.GroupLabel || "");
          const key = this.normalizeCasteText(label);
          if (!key) return;
          casteTotals[key] = {
            Claims: Array.isArray(row) ? (parseInt(row[2], 10) || 0) : (parseInt(row.TotalClaims || row.Claims || row.ClaimCount, 10) || 0),
            ClaimAmount: Array.isArray(row) ? (parseFloat(row[3]) || 0) : (parseFloat(row.TotalClaimAmount || row.ClaimAmount) || 0),
            PaidAmt: Array.isArray(row) ? (parseFloat(row[4]) || 0) : (parseFloat(row.TotalPaidAmount || row.ClaimPaidAmt || row.PaidAmt || row.PaidAmount) || 0),
          };
        });

        if (Object.keys(casteTotals).length > 0 && Array.isArray(this.reportData)) {
          this.reportData = this.reportData.map((row) => {
            const key = this.normalizeCasteText(row.GroupLabel || row.CasteMarathi || row.Caste || "");
            const totals = casteTotals[key];
            if (!totals) return row;
            return {
              ...row,
              Claims: totals.Claims,
              ClaimCount: totals.Claims,
              ClaimAmount: totals.ClaimAmount,
              PaidAmt: totals.PaidAmt,
            };
          });
          this.totalRows = this.reportData.length;
        }
      }

      this.stats = {
        schemeWise: keepExistingIfEmpty(schemeWise, this.stats.schemeWise),
        applicationStatusWise: keepExistingIfEmpty(applicationStatusWise, this.stats.applicationStatusWise),
        categoryWise: keepExistingIfEmpty(categoryWise, this.stats.categoryWise),
        casteWise: keepExistingIfEmpty(casteWise, this.stats.casteWise),
        genderWise,
        yearWise: keepExistingIfEmpty(yearWise, this.stats.yearWise),
        districtWise: keepExistingIfEmpty(districtWise, this.stats.districtWise),
        beneficiarySplit,
        claimSplit,
      };
    },
    shouldSkipCompanyStatsApi() {
      // Caste-wise claim totals are aggregated by the statistics endpoint because
      // the claim approval endpoint can be too large/slow for grouped report rows.
      return String(this.selectedReportType || "").toLowerCase() !== "caste_wise";
    },
    fetchStatsFromBackend() {
      if (this.shouldSkipCompanyStatsApi()) {
        return Promise.resolve();
      }
      if (!this.selectedCompany || !this.dateFrom || !this.dateTo) return Promise.resolve();

      const districtID = this.getEffectiveDistrictId();
      const selectedRange = this.getSelectedDateRange();
      const fyStart = selectedRange ? selectedRange.startDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyStart;
      const fyEnd = selectedRange ? selectedRange.endDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyEnd;
      const startDate = this.formatDateIso(fyStart);
      const endDate = this.formatDateIso(fyEnd);

      // NOTE: Many specialized sub-castes share the same caste code (e.g. code 83
      // is used by कुणबी, मराठा कुणबी, लेवा पाटीदार, लेवा कुणबी, लेवा पाटील, ...).
      // Matching statistics by the shared code wrongly counts every code-83 caste.
      // A scheme must only count ITS OWN specific castes, so we match by the exact
      // caste NAMES the company owns and deliberately DO NOT send the shared codes.
      const casteNamesCsv = (this.selectedCompany.castes || [])
        .map((c) => c.casteName)
        .filter((c) => c && c.toString().trim() !== "")
        .join(",");

      return apis.ReadCompanyStatisticsSplit({
        DistrictID: districtID,
        LoanType: this.selectedLoanType || 0,
        StartDate: startDate,
        EndDate: endDate,
        IsSpecializedCompany: this.isSpecializedCompany(this.selectedCompany),
        EffectiveFromDate: this.selectedCompany ? this.selectedCompany.establishmentDate || null : null,
        CasteCodesCsv: null,
        CasteNamesCsv: casteNamesCsv || null,
      })
        .then((res) => {
          let body = res && (res.body || res.data) ? (res.body || res.data) : null;
          if (typeof body === "string") {
            try {
              body = JSON.parse(body);
            } catch (e) {
              body = null;
            }
          }
          this.applyBackendStats(this.normalizeStatsPayload(body || {}));
        })
        .catch((err) => {
          const httpStatus = this.getHttpStatus(err);
          const isBadRequest = httpStatus === 400;
          const isMissingEndpoint = httpStatus === 404 || String(err && err.message || "").toLowerCase().indexOf("not found") !== -1;
          if (isBadRequest || isMissingEndpoint || this.isNetworkOrCorsError(err)) {
            this.supportsCompanyStatsApi = false;
            this.statsApiDisabled = true;
          }
          logger.error("ReadCompanyStatisticsSplit failed");
          return Promise.resolve();
        });
    },
    fetchGenderStatsFromBackend() {
      if (!this.dateFrom || !this.dateTo) return Promise.resolve();

      const selectedRange = this.getSelectedDateRange();
      if (!selectedRange) return Promise.resolve();

      const casteNamesCsv = (this.selectedCompany && this.selectedCompany.castes || [])
        .map((c) => c.casteName)
        .filter((c) => c && c.toString().trim() !== "")
        .join(",");

      return apis.ReadCompanyStatisticsSplit({
        DistrictID: this.getEffectiveDistrictId(),
        LoanType: this.selectedLoanType || 0,
        StartDate: this.formatDateIso(selectedRange.startDate),
        EndDate: this.formatDateIso(selectedRange.endDate),
        IsSpecializedCompany: this.isSpecializedCompany(this.selectedCompany),
        EffectiveFromDate: this.selectedCompany ? (this.selectedCompany.establishmentDate || null) : null,
        CasteCodesCsv: null,
        CasteNamesCsv: casteNamesCsv || null,
      }).then((res) => {
        let body = res && (res.body || res.data) ? (res.body || res.data) : null;
        if (typeof body === "string") {
          try {
            body = JSON.parse(body);
          } catch (e) {
            body = null;
          }
        }
        const genderRows = this.normalizeStatsList(
          body && body.genderWise,
          ["Gender", "MaleFemale", "GenderWise", "Label", "Type"],
          ["TotalApplications"]
        ).map((row) => ({ label: this.normalizeGender(row.label), count: row.count }))
          .filter((row) => row.label !== "Unknown" && row.count > 0)
          .sort((a, b) => b.count - a.count);

        if (genderRows.length > 0) {
          this.stats.genderWise = genderRows;
          if (String(this.selectedReportType || "").toLowerCase() === "gender_wise") {
            const existingByGender = {};
            (this.reportData || []).forEach((row) => {
              existingByGender[this.normalizeGender(row.GroupLabel)] = row;
            });
            this.reportData = genderRows.map((gender) => {
              const existing = existingByGender[gender.label] || {};
              return Object.assign({}, existing, {
                GroupLabel: gender.label,
                ApplicantName: gender.label,
                Applications: gender.count,
                LOICount: Number(existing.LOICount || 0),
              });
            });
            this.totalRows = this.reportData.length;
          }
        }
      }).catch(() => {});
    },
    fetchClaimsByDistrict(districtID) {
      return apis.ReadClaimEMIForApproval({
        DistrictID: districtID,
        BankID: "00000000-0000-0000-0000-000000000000",
        ClaimStatus: 0,
        ClaimPaidStatus: 0,
        LoanApplicationNo: null,
        LOINumber: null,
        UserLoginID: null,
        ClaimStartDate: null,
        ClaimEndDate: null,
        LoanType: this.selectedLoanType,
        StatusOwner: "All",
      }).then((r) => {
        const rows = normalizeResponseArray(r);
        if (Array.isArray(rows) && rows.length > 0) {
          return rows;
        }
        return apis.readAllClaims({
          ClaimStatus: 0,
          ClaimPaidStatus: 0,
          DistrictID: districtID,
        }).then((fallbackRes) => normalizeResponseArray(fallbackRes)).catch(() => []);
      }).catch(() => []);
    },
    fetchClaimsForApplications(appRows, districtID) {
      const allDistrictId = "00000000-0000-0000-0000-000000000000";
      const normalizeJoinKey = (value) => String(value || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      const appNoSet = {};
      const appIdSet = {};
      const applicantKeySet = {};
      const districtKeySet = {};
      (appRows || []).forEach((a) => {
        const appNo = String(a.LoanApplicationNo || a.ApplicationNo || a.LoanApplicationNumber || a.AppNo || a.ApplicationNumber || "").trim();
        const appId = String(a.LoanApplicationID || a.ApplicationID || a.AppID || a.Id || "").trim();
        const applicant = String(a.ApplicantName || a.FullName || a.FirstName || "").trim();
        const district = String(a.DistrictName || a.District || a.DistName || "").trim();

        if (appNo) appNoSet[normalizeJoinKey(appNo)] = true;
        if (appId) appIdSet[normalizeJoinKey(appId)] = true;
        if (applicant) applicantKeySet[normalizeJoinKey(applicant)] = true;
        if (district) districtKeySet[normalizeJoinKey(district)] = true;
      });

      const normalizeLoanType = (row) => Number(row.LoanType || row.SchemeType || row.LoanTypeID || 0);
      const selectedLoanType = Number(this.selectedLoanType || 0);

      const filterRows = (rows) => {
        if (!Array.isArray(rows)) return [];
        const filteredRows = rows.filter((r) => {
          const appNo = String(r.LoanApplicationNo || r.ApplicationNo || r.LoanApplicationNumber || r.AppNo || r.ApplicationNumber || "").trim();
          const appId = String(r.LoanApplicationID || r.ApplicationID || r.AppID || r.Id || "").trim();
          const applicant = String(r.ApplicantName || r.FullName || r.FirstName || "").trim();
          const district = String(r.DistrictName || r.District || r.DistName || "").trim();
          const matched =
            (appNo && appNoSet[normalizeJoinKey(appNo)]) ||
            (appId && appIdSet[normalizeJoinKey(appId)]) ||
            ((applicant && district) && applicantKeySet[normalizeJoinKey(applicant)] && districtKeySet[normalizeJoinKey(district)]);
          if (!matched) return false;
          if (selectedLoanType > 0) {
            const rowLoanType = normalizeLoanType(r);
            if (rowLoanType && rowLoanType !== selectedLoanType) {
              return false;
            }
          }
          return true;
        });

        const seenClaimIds = {};
        return filteredRows.filter((row, index) => {
          const claimId = row && (row.ClaimEMIID || row.ClaimID || row.ClaimId);
          if (!claimId) return true;
          const key = String(claimId).trim().toLowerCase();
          if (seenClaimIds[key]) return false;
          seenClaimIds[key] = true;
          return true;
        });
      };

      return this.fetchClaimsByDistrict(districtID)
        .then((rows) => {
          const filtered = filterRows(rows);
          if (filtered.length > 0 || districtID === allDistrictId) return filtered;
          return this.fetchClaimsByDistrict(allDistrictId).then((allRows) => filterRows(allRows));
        })
        .catch(() => []);
    },
    fetchLoiSummary() {
      if ((!this.canSearchWithoutCompany && (this.selectedCompanyID === null || this.selectedCompanyID === undefined)) || !this.dateFrom || !this.dateTo) return;
      this.loiSummaryLoading = true;
      this.loiSummary = { count: 0, amount: 0 };

      const districtID = this.getEffectiveDistrictId();
      const selectedRange = this.getSelectedDateRange();
      const fyStart = selectedRange ? selectedRange.startDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyStart;
      const fyEnd = selectedRange ? selectedRange.endDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyEnd;
      const loiStartDate = this.formatDateIso(fyStart);
      const loiEndDate = this.formatDateIso(fyEnd);

      const loiForm = {
        LoanApplicationNo: null,
        LOINumber: null,
        LOIStartDate: null,
        LOIEndDate: null,
        BankID: "00000000-0000-0000-0000-000000000000",
        DistrictID: districtID,
        UserLoginID: null,
        RecordStartDate: loiStartDate,
        RecordEndDate: loiEndDate,
      };
      const allDistrictId = "00000000-0000-0000-0000-000000000000";
      const fetchLoiRows = (did) => {
        if (!this.canUseSanctionApi()) {
          return Promise.resolve([]);
        }
        const baseForm = { ...loiForm, DistrictID: did };
        return apis.ReadSanctionLetterQuery(baseForm)
          .then((r) => normalizeResponseArray(r))
          .then((rows) => {
            if (Array.isArray(rows) && rows.length > 0) return rows;
            // Fallback query without date filters when backend applies strict date semantics.
            return apis.ReadSanctionLetterQuery({
              ...baseForm,
              RecordStartDate: null,
              RecordEndDate: null,
            }).then((rr) => normalizeResponseArray(rr)).catch(() => []);
          })
          .catch((err) => {
            const status = this.getHttpStatus(err);
            if (status >= 500 || status === 404 || this.isNetworkOrCorsError(err)) {
              this.markSanctionApiTemporarilyDisabled();
            }
            return [];
          });
      };

      fetchLoiRows(districtID)
        .then((rows) => {
          if ((rows || []).length > 0 || districtID === allDistrictId) return rows || [];
          return fetchLoiRows(allDistrictId);
        })
        .then((r) => {
          let data = Array.isArray(r) ? r : normalizeResponseArray(r);
          // Filter by FY
          data = data.filter((loi) => {
            const dt = new Date(loi.LOIDate || loi.UpdatedDate || loi.RecordCreatedDate || loi.CreateDate || "");
            return dt >= fyStart && dt <= fyEnd;
          });
          this.loiSummary.count = data.filter((x) => String(x.LOINumber || "").trim() !== "").length;
          this.loiSummary.amount = data.reduce((sum, loi) => sum + (parseFloat(loi.SanctionAmount || loi.HOApprovedAmount || loi.DisbursementAmount || loi.DisburmentAmount || loi.ApprovedAmount) || 0), 0);
        })
        .catch(() => {
          this.loiSummary = { count: 0, amount: 0 };
        })
        .finally(() => {
          this.loiSummaryLoading = false;
        });
    },
    fetchBeneficiaryDatewiseReport() {
      if (!this.supportsBeneficiaryReportApi) {
        this.beneficiaryReportRows = [];
        this.beneficiaryReportLoading = false;
        return Promise.resolve();
      }
      if (!this.selectedCompany || !this.dateFrom || !this.dateTo) {
        this.beneficiaryReportRows = [];
        return Promise.resolve();
      }

      const companyName = String(this.selectedCompany.name || "").toLowerCase();
      const companyEnglish = String(this.selectedCompany.nameEnglish || "").toLowerCase();
      const companyMatchesMissingBackendEndpoint =
        companyName.includes("शामराव पेजे") ||
        companyName.includes("कोकण") ||
        companyEnglish.includes("konkan") ||
        companyEnglish.includes("shamrao peje");

      if (companyMatchesMissingBackendEndpoint) {
        this.supportsBeneficiaryReportApi = false;
        this.beneficiaryReportRows = [];
        this.beneficiaryReportLoading = false;
        return Promise.resolve();
      }

      this.beneficiaryReportLoading = true;
      const districtID = this.getEffectiveDistrictId();
      const selectedRange = this.getSelectedDateRange();
      const fyStart = selectedRange ? selectedRange.startDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyStart;
      const fyEnd = selectedRange ? selectedRange.endDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyEnd;
      const loiStartDate = this.formatDateIso(fyStart);
      const loiEndDate = this.formatDateIso(fyEnd);

      return apis.ReadBeneficiaryDatewiseLOIReport({
        DistrictID: districtID,
        LoanType: this.selectedLoanType || 0,
        StartDate: fyStart,
        EndDate: fyEnd,
      })
        .then((r) => {
          this.beneficiaryReportRows = normalizeResponseArray(r);
        })
        .catch((err) => {
          if (this.getHttpStatus(err) === 404) {
            this.supportsBeneficiaryReportApi = false;
          }
          this.beneficiaryReportRows = [];
        })
        .finally(() => {
          this.beneficiaryReportLoading = false;
        });
    },
    refreshClaimsGenderFilter(rows) {
      const selectedReportType = String(this.selectedReportType || "").toLowerCase();
      const isClaimsReport = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(selectedReportType) !== -1;
      const selectedGender = String(this.selectedGender || "All").trim().toLowerCase();
      if (!isClaimsReport || selectedGender === "all") return;

      const genderRows = (rows || []).filter((r) => {
        return this.getNormalizedGenderFromRow(r).toLowerCase() === selectedGender;
      });
      this.reportBaseRows = genderRows.slice();
      this.reportData = genderRows.slice();
      this.totalRows = genderRows.length;
      this.currentPage = 1;
      this.prepareStats(genderRows);
    },
    refreshConsolidationGenderColumns(rows) {
      if (String(this.selectedReportType || "").toLowerCase() !== "consolidation_summary") return;

      const buckets = {
        1: { male: {}, female: {}, maleAmount: 0, femaleAmount: 0 },
        2: { male: {}, female: {}, maleAmount: 0, femaleAmount: 0 },
        3: { male: {}, female: {}, maleAmount: 0, femaleAmount: 0 },
      };
      const seenClaims = {};

      (rows || []).forEach((row) => {
        const claimId = String(row.ClaimEMIID || row.ClaimEMIId || row.ClaimID || row.ClaimId || row.ClaimNo || row.ClaimNumber || "").trim();
        const claimKey = claimId ? claimId + "|" + String(row.PaidDate || row.PaidDt || "").slice(0, 10) : "";
        if (!claimKey || seenClaims[claimKey]) return;
        seenClaims[claimKey] = true;

        const loanType = Number(row.LoanType || row.LoanTypeID || row.ConsolidationLoanType || 0);
        const beneficiaryKey = String(row.UserID || row.LoginID || row.UserLoginID || row.AadhaarNo || row.LoanApplicationNo || row.ClaimNo || "").trim();
        const amount = parseFloat(row.ClaimPaidAmt || row.PaidAmt || row.PaidAmount || row.ApprovedAmount || 0) || 0;
        const gender = this.getNormalizedGenderFromRow(row);
        const bucket = buckets[loanType];
        if (!bucket || !beneficiaryKey || amount <= 0) return;

        if (gender === "Male") {
          bucket.male[beneficiaryKey] = true;
          bucket.maleAmount += amount;
        } else if (gender === "Female") {
          bucket.female[beneficiaryKey] = true;
          bucket.femaleAmount += amount;
        }
      });

      const rowByLabel = {};
      (this.reportData || []).forEach((row) => { rowByLabel[row.GroupLabel] = row; });
      const labels = { 1: "Individual", 2: "Group", 3: "Education" };
      Object.keys(labels).forEach((type) => {
        const bucket = buckets[type];
        const target = rowByLabel[labels[type]];
        if (!target) return;
        const officialBeneficiaries = Number(target.Beneficiaries || 0);
        const officialPaidAmount = Number(target.PaidAmt || 0);
        const maleCount = Object.keys(bucket.male).length;
        const femaleCount = Object.keys(bucket.female).length;
        const knownCount = maleCount + femaleCount;
        const countRatio = knownCount > officialBeneficiaries && knownCount > 0
          ? officialBeneficiaries / knownCount
          : 1;
        const maleAmount = bucket.maleAmount;
        const femaleAmount = bucket.femaleAmount;
        const knownAmount = maleAmount + femaleAmount;
        const amountRatio = knownAmount > officialPaidAmount && knownAmount > 0
          ? officialPaidAmount / knownAmount
          : 1;

        target.MaleBeneficiaries = Math.round(maleCount * countRatio);
        target.FemaleBeneficiaries = Math.max(0, officialBeneficiaries - target.MaleBeneficiaries);
        target.MalePaidAmt = Math.round(maleAmount * amountRatio * 100) / 100;
        target.FemalePaidAmt = Math.max(0, Math.round((officialPaidAmount - target.MalePaidAmt) * 100) / 100);
      });

      const all = rowByLabel.All;
      if (all) {
        all.MaleBeneficiaries = this.reportData
          .filter((row) => row.GroupLabel !== "All")
          .reduce((sum, row) => sum + Number(row.MaleBeneficiaries || 0), 0);
        all.FemaleBeneficiaries = this.reportData
          .filter((row) => row.GroupLabel !== "All")
          .reduce((sum, row) => sum + Number(row.FemaleBeneficiaries || 0), 0);
        all.MalePaidAmt = this.reportData
          .filter((row) => row.GroupLabel !== "All")
          .reduce((sum, row) => sum + Number(row.MalePaidAmt || 0), 0);
        all.FemalePaidAmt = this.reportData
          .filter((row) => row.GroupLabel !== "All")
          .reduce((sum, row) => sum + Number(row.FemalePaidAmt || 0), 0);
      }
      this.reportData = (this.reportData || []).slice();
    },
    enrichGenderStatsFromUserDetails(rows, options) {
      const opts = options || {};
      const userIds = [];
      const seen = {};
      const selectedReportType = String(this.selectedReportType || "").toLowerCase();
      const shouldOverrideKnownGender = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(selectedReportType) !== -1;

      (rows || []).forEach((r) => {
        const currentGender = this.getNormalizedGenderFromRow(r);
        const id = String(r.UserID || r.LoginID || r.UserLoginID || "").trim();

        if (!id) return;

        if (currentGender === "Unknown" || shouldOverrideKnownGender) {
          if (!seen[id]) {
            seen[id] = true;
            userIds.push(id);
          }
        }
      });

      if (userIds.length === 0) {
        this.refreshClaimsGenderFilter(rows);
        return Promise.resolve();
      }

      const fetchGenderForUser = (userId) => {
        if (this.userGenderCache[userId] !== undefined && this.normalizeGender(this.userGenderCache[userId]) !== "Unknown") {
          return Promise.resolve(this.userGenderCache[userId]);
        }

        return apis.ReadUserDetail(userId)
          .then((res) => {
            const list = normalizeResponseArray(res);
            let row = Array.isArray(list) && list.length > 0 ? list[0] : (res && (res.body || res.data)) || {};

            if (typeof row === "string") {
              try {
                row = JSON.parse(row);
              } catch (e) {
                row = {};
              }
            }

            if (row && typeof row === "object" && !Array.isArray(row)) {
              const keys = Object.keys(row);
              for (let i = 0; i < keys.length; i++) {
                const val = row[keys[i]];
                if (Array.isArray(val) && val.length > 0) {
                  row = val[0];
                  break;
                }
              }
            }

            const normalized = this.getNormalizedGenderFromRow(row);
            this.userGenderCache[userId] = normalized;
            return normalized;
          })
          .catch(() => {
            this.userGenderCache[userId] = "Unknown";
            return "Unknown";
          });
      };

      // Cap remote profile lookups per search to keep report responsive.
      const maxGenderLookups = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(String(this.selectedReportType || "").toLowerCase()) !== -1
        ? 5000
        : 120;
      const targetUserIds = userIds.slice(0, maxGenderLookups);

      // Limit concurrency so report page remains responsive.
      const chunkSize = 50;
      let chain = Promise.resolve();
      for (let i = 0; i < targetUserIds.length; i += chunkSize) {
        const chunk = targetUserIds.slice(i, i + chunkSize);
        chain = chain.then(() => Promise.all(chunk.map((id) => fetchGenderForUser(id))));
      }

      return chain.then(() => {
        const selectedReportType = String(this.selectedReportType || "").toLowerCase();
        const shouldOverrideKnownGender = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(selectedReportType) !== -1;

        (rows || []).forEach((r) => {
          const id = String(r.UserID || r.LoginID || r.UserLoginID || "").trim();
          if (!id || this.userGenderCache[id] === undefined) return;
          const resolved = this.normalizeGender(this.userGenderCache[id]);
          if (resolved === "Unknown") return;

          const current = this.getNormalizedGenderFromRow(r);
          const shouldReplace = current === "Unknown" || (shouldOverrideKnownGender && current !== resolved);
          if (shouldReplace) {
            r.Gender = resolved;
            r.GenderRaw = resolved;
            r.GenderID = resolved === "Male" ? 1 : 0;
          }
        });

        this.refreshClaimsGenderFilter(rows);
        if (opts.refreshConsolidationSummary === true) {
          this.refreshConsolidationGenderColumns(rows);
        }
        if (String(this.selectedReportType || "").toLowerCase() !== "consolidation_summary") {
          this.refreshConsolidationGenderColumns(rows);
        }

        const counts = { Male: 0, Female: 0, Unknown: 0 };
        (rows || []).forEach((r) => {
          const id = String(r.UserID || r.LoginID || r.UserLoginID || "").trim();
          const rowGender = this.getNormalizedGenderFromRow(r);
          const g = (id && this.userGenderCache[id]) ? this.userGenderCache[id] : rowGender;
          const normalized = this.normalizeGender(g);
          counts[normalized] = (counts[normalized] || 0) + 1;
        });

        const knownCount = (counts.Male || 0) + (counts.Female || 0);
        if (knownCount > 0) {
          this.prepareStats(rows || []);
          if (!this.isGroupedReportType(this.selectedReportType)) {
            this.reportBaseRows = (rows || []).slice();
            this.reportData = (rows || []).slice();
            this.totalRows = this.reportData.length;
          }

          this.stats.genderWise = [
            { label: "Male", count: counts.Male || 0 },
            { label: "Female", count: counts.Female || 0 },
            { label: "Unknown", count: counts.Unknown || 0 },
          ].filter((x) => x.count > 0).sort((a, b) => b.count - a.count);

          if (opts.refreshGenderGroupedReport === true && this.isGroupedReportType("gender_wise")) {
            const groupedRows = this.buildGroupedRows(rows, "gender_wise");
            this.reportBaseRows = rows;
            this.reportData = groupedRows;
            this.totalRows = groupedRows.length;
            this.currentPage = 1;
          }
        }
      }).catch(() => {});
    },
    enrichLocationFieldsFromUserDetails(rows) {
      const userIds = [];
      const seen = {};

      (rows || []).forEach((r) => {
        const office = this.textOrNotKnown(r.OfficeName);
        const mobileNumber = this.textOrNotKnown(r.MobileNumber);
        const address = this.textOrNotKnown(r.ADDRESS);
        const taluka = this.textOrNotKnown(r.Taluka);
        if (office !== "Not Known" && mobileNumber !== "Not Known" && address !== "Not Known" && taluka !== "Not Known") return;

        const id = String(r.UserID || r.LoginID || r.UserLoginID || "").trim();
        if (id && !seen[id]) {
          seen[id] = true;
          userIds.push(id);
        }
      });

      if (userIds.length === 0) return Promise.resolve();

      const fetchProfileForUser = (userId) => {
        if (this.userProfileCache[userId] !== undefined) {
          return Promise.resolve(this.userProfileCache[userId]);
        }

        return apis.ReadUserDetail(userId)
          .then((res) => {
            const list = normalizeResponseArray(res);
            let row = Array.isArray(list) && list.length > 0 ? list[0] : (res && (res.body || res.data)) || {};

            if (typeof row === "string") {
              try {
                row = JSON.parse(row);
              } catch (e) {
                row = {};
              }
            }

            if (row && typeof row === "object" && !Array.isArray(row)) {
              const keys = Object.keys(row);
              for (let i = 0; i < keys.length; i++) {
                const val = row[keys[i]];
                if (Array.isArray(val) && val.length > 0) {
                  row = val[0];
                  break;
                }
              }
            }

            const profile = {
              OfficeName: this.firstDefinedValue(row, ["OfficeName", "Office", "BranchName", "LoanBranch", "Branch", "SubOffice", "Division"]),
              MobileNumber: this.firstDefinedValue(row, ["MobileNumber", "MobileNo", "Mobile", "ContactNo", "ContactNumber", "ApplicantMobile", "Phone", "PhoneNo"]),
              ADDRESS: this.formatProfileAddress(row),
              Taluka: this.firstDefinedValue(row, ["Taluka", "TalukaName", "TalukName", "Tehsil", "Tahsil", "BlockName"])
                || this.firstDefinedValue(row.CorrespondenceAddress || row.PermanentAddress || {}, ["Taluka", "TalukaName", "TalukName", "Tehsil", "Tahsil"]),
            };

            this.userProfileCache[userId] = profile;
            return profile;
          })
          .catch(() => {
            this.userProfileCache[userId] = null;
            return null;
          });
      };

      const maxLookups = 150;
      const targetUserIds = userIds.slice(0, maxLookups);
      const chunkSize = 10;
      let chain = Promise.resolve();

      for (let i = 0; i < targetUserIds.length; i += chunkSize) {
        const chunk = targetUserIds.slice(i, i + chunkSize);
        chain = chain.then(() => Promise.all(chunk.map((id) => fetchProfileForUser(id))));
      }

      return chain.then(() => {
        let updated = false;
        (rows || []).forEach((r) => {
          const id = String(r.UserID || r.LoginID || r.UserLoginID || "").trim();
          const profile = id ? this.userProfileCache[id] : null;
          if (!profile) return;

          if (this.textOrNotKnown(r.OfficeName) === "Not Known" && profile.OfficeName) {
            r.OfficeName = profile.OfficeName;
            updated = true;
          }
          if (this.textOrNotKnown(r.MobileNumber) === "Not Known" && profile.MobileNumber) {
            r.MobileNumber = profile.MobileNumber;
            updated = true;
          }
          if (this.textOrNotKnown(r.ADDRESS) === "Not Known" && profile.ADDRESS) {
            r.ADDRESS = profile.ADDRESS;
            updated = true;
          }
          if (this.textOrNotKnown(r.Taluka) === "Not Known" && profile.Taluka) {
            r.Taluka = profile.Taluka;
            updated = true;
          }
        });

        if (updated) {
          this.reportData = (rows || []).slice();
          this.buildOfficeOptionsFromRows(this.reportData);
        }
      }).catch(() => {});
    },
    enrichLendingFieldsFromUserDetails(rows) {
      const userIds = [];
      const seen = {};

      (rows || []).forEach((r) => {
        const bank = this.textOrNotKnown(r.BankName);
        const business = this.textOrNotKnown(r.BusinessType);
        if (bank !== "Not Known" && business !== "Not Known") return;

        const id = String(r.UserID || r.LoginID || r.UserLoginID || "").trim();
        if (id && !seen[id]) {
          seen[id] = true;
          userIds.push(id);
        }
      });

      if (userIds.length === 0) return Promise.resolve();

      const fetchLendingForUser = (userId) => {
        if (this.userProfileCache["lending:" + userId] !== undefined) {
          return Promise.resolve(this.userProfileCache["lending:" + userId]);
        }

        return apis.ReadUserLendingLoanDetails(userId)
          .then((res) => {
            const rows = normalizeResponseArray(res);
            const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : (res && (res.body || res.data)) || {};
            const details = {
              BankName: this.firstKnownText([row.BankName, row.Bank, row.BankNm], ""),
              BranchName: this.firstKnownText([row.Branch, row.BranchName, row.LoanBranch], ""),
              BusinessType: this.firstKnownText([row.ProductfromOrganization, row.ProductFromOrganization, row.BusinessType, row.BusinessName], ""),
            };
            this.userProfileCache["lending:" + userId] = details;
            return details;
          })
          .catch(() => {
            this.userProfileCache["lending:" + userId] = null;
            return null;
          });
      };

      const targetUserIds = userIds.slice(0, ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(String(this.selectedReportType || "").toLowerCase()) !== -1 ? 1000 : this.maxProfileEnrichmentRows);
      const chunkSize = 10;
      let chain = Promise.resolve();

      for (let i = 0; i < targetUserIds.length; i += chunkSize) {
        const chunk = targetUserIds.slice(i, i + chunkSize);
        chain = chain.then(() => Promise.all(chunk.map((id) => fetchLendingForUser(id))));
      }

      return chain.then(() => {
        let updated = false;
        (rows || []).forEach((r) => {
          const id = String(r.UserID || r.LoginID || r.UserLoginID || "").trim();
          const details = id ? this.userProfileCache["lending:" + id] : null;
          if (!details) return;

          if (this.textOrNotKnown(r.BankName) === "Not Known" && details.BankName) {
            r.BankName = details.BankName;
            updated = true;
          }
          if (this.textOrNotKnown(r.BranchName) === "Not Known" && details.BranchName) {
            r.BranchName = details.BranchName;
            updated = true;
          }
          if (this.textOrNotKnown(r.BusinessType) === "Not Known" && details.BusinessType) {
            r.BusinessType = details.BusinessType;
            updated = true;
          }
        });

        if (updated) {
          const selectedReportType = String(this.selectedReportType || "").toLowerCase();
          const isClaimsReport = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(selectedReportType) !== -1;
          if (isClaimsReport && String(this.selectedGender || "All").trim().toLowerCase() !== "all") {
            this.refreshClaimsGenderFilter(rows || []);
          } else {
            this.reportBaseRows = (rows || []).slice();
            this.reportData = (rows || []).slice();
            this.totalRows = this.reportData.length;
          }
          this.buildOfficeOptionsFromRows(rows || []);
        }
      }).catch(() => {});
    },
    detectTenantAndFilterCompanies() {
      const host = window.location.hostname.toLowerCase();
      const tenantIds = this.allSubCompanies.map((c) => c.tenantId);
      let matchedTenant = null;

      for (const tid of tenantIds) {
        if (host.startsWith(tid + ".")) {
          matchedTenant = tid;
          break;
        }
      }

      this.currentTenantId = matchedTenant;

      // Always show all companies in Sub Company Report.
      this.subCompanies = this.allSubCompanies;

      // If report is opened on a tenant host, preselect that tenant but keep full list visible.
      if (matchedTenant && (this.selectedCompanyID === null || this.selectedCompanyID === undefined)) {
        const company = this.allSubCompanies.find((c) => c.tenantId === matchedTenant);
        if (company) {
          this.selectedCompanyID = company.id;
        }
      } else if ((this.selectedCompanyID === null || this.selectedCompanyID === undefined) && this.allSubCompanies.length > 0) {
        // Keep filter state ready-to-use on localhost/non-tenant hosts.
        this.selectedCompanyID = this.allSubCompanies[0].id;
      }
    },

    buildFinancialYears() {
      let today = new Date();
      let curr_year = today.getFullYear();
      if (today.getMonth() + 1 <= 3) curr_year -= 1;
      let list = [];
      while (curr_year >= 2020) {
        list.push(curr_year + "-" + (curr_year + 1));
        curr_year--;
      }
      this.financialYears = list;
    },

    getDateRangeFromFY(fy) {
      // fy = "2023-2024" → returns JS Date objects for client-side filtering
      const [startY] = fy.split("-").map(Number);
      const fyStart = new Date(startY, 3, 1);       // April 1
      const fyEnd   = new Date(startY + 1, 2, 31);  // March 31
      return { fyStart, fyEnd };
    },
    getCompanyEstablishmentDate(company) {
      if (!company || !company.establishmentDate) return null;
      const dt = this.toDateSafe(company.establishmentDate);
      if (!dt) return null;
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    },
    applyCompanyStartDateToFilter() {
      const companyStartDate = this.getCompanyEstablishmentDate(this.selectedCompany);
      if (!companyStartDate) return;

      const selectedRange = this.getSelectedDateRange();
      if (!selectedRange || companyStartDate < selectedRange.startDate) {
        this.dateFrom = this.formatDateIso(companyStartDate);
      }
    },

    initDateFilters() {
      const today = new Date();      const currentYear = today.getMonth() + 1 <= 3 ? today.getFullYear() - 1 : today.getFullYear();
      const fyStart = new Date(currentYear, 3, 1);
      this.dateFrom = fyStart.toISOString().slice(0, 10);
      this.dateTo = today.toISOString().slice(0, 10);
    },

    fetchCompanyWiseReport() {
      const requestId = ++this.reportRequestId;
      this.isLoading = true;
      this.searched = false;
      this.reportData = [];
      this.reportBaseRows = [];
      this.totalRows = 0;
      this.resetStats();
      this.tableFields = this.withReportDateFormatters(this.getTableFieldsForReportType("company_wise"));

      const allDistrictId = "00000000-0000-0000-0000-000000000000";
      const selectedRange = this.getSelectedDateRange();
      const fyStart = selectedRange ? selectedRange.startDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyStart;
      const fyEnd = selectedRange ? selectedRange.endDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyEnd;
      const loiStart = this.formatDateIso(fyStart);
      const loiEnd = this.formatDateIso(fyEnd);
      const regionFilter = String(this.selectedRegion || "All Regions").trim();

      const selectedStatusIdsLookup = {};
      const selectedStatusTextSet = {};
      (this.selectedStatusIds || []).forEach((id) => {
        const num = Number(id);
        if (!Number.isNaN(num) && num > 0) {
          selectedStatusIdsLookup[num] = true;
          const text = this.normalizeStatusText(this.statusTextById[num] || "");
          if (text) selectedStatusTextSet[text] = true;
        }
      });
      // Application rows from usp_GetLoanApplications only carry the status
      // TEXT (ApplicationStatus), not a numeric ApplicationStatusID - so
      // matching must be done by normalized text, not by id.
      const hasMultiStatus = Object.keys(selectedStatusTextSet).length > 0;

      const selectedCasteNameSet = {};
      (this.selectedCasteIDs || []).forEach((id) => {
        const found = (this.casteOptions || []).find((c) => String(c.value) === String(id));
        if (found && found.text) selectedCasteNameSet[this.normalizeCasteText(found.text)] = true;
      });
      const hasCasteFilter = Object.keys(selectedCasteNameSet).length > 0;

      const joinKey = (value) => String(value || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

      const appsPromise = this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, "01-01-1970", "01-01-1970", 0);
      const loiPromise = this.canUseSanctionApi()
        ? apis.ReadSanctionLetterQuery({
            LoanApplicationNo: null,
            LOINumber: null,
            LOIStartDate: null,
            LOIEndDate: null,
            BankID: allDistrictId,
            DistrictID: allDistrictId,
            UserLoginID: null,
            RecordStartDate: loiStart,
            RecordEndDate: loiEnd,
          }).then((r) => normalizeResponseArray(r)).catch(() => [])
        : Promise.resolve([]);
      const claimPromise = apis.ReadClaimEMIForApproval({
        DistrictID: allDistrictId,
        BankID: allDistrictId,
        ClaimStatus: 0,
        ClaimPaidStatus: 0,
        LoanApplicationNo: null,
        LOINumber: null,
        UserLoginID: null,
        ClaimStartDate: null,
        ClaimEndDate: null,
        LoanType: this.selectedLoanType || 0,
        StatusOwner: "All",
      }).then((r) => normalizeResponseArray(r)).catch(() => []);

      Promise.all([appsPromise, loiPromise, claimPromise]).then(([appRows, loiRows, claimRows]) => {
        if (requestId !== this.reportRequestId) return;

        const filteredApps = (appRows || []).filter((app) => {
          const dt = this.toDateFlexible(app.Createdate || app.CreateDate || app.ApplicationDate || app.LOIDate || "");
          if (!dt || Number.isNaN(dt.getTime()) || dt < fyStart || dt > fyEnd) return false;
          if (regionFilter && regionFilter !== "All Regions") {
            if (this.getRegionByDistrictName(app.DistrictName || app.District || "") !== regionFilter) return false;
          }
          if (hasMultiStatus) {
            const rowStatusText = this.normalizeStatusText(app.ApplicationStatus || app.Status || app.ApplicationStatus1 || "");
            if (!rowStatusText || !selectedStatusTextSet[rowStatusText]) return false;
          }
          if (hasCasteFilter) {
            const tokens = this.getRowCasteTokens(app);
            if (tokens.length === 0 || !tokens.some((t) => selectedCasteNameSet[t] === true)) return false;
          }
          return true;
        });

        // Index applications by LoanApplicationNo/ID so LOI rows (which carry
        // application identifiers) can be traced back to an applicant's caste.
        const appByKey = {};
        filteredApps.forEach((app) => {
          const noKey = joinKey(app.LoanApplicationNo);
          if (noKey && !appByKey[noKey]) appByKey[noKey] = app;
          const idKey = joinKey(app.LoanApplicationID || app.ApplicationID);
          if (idKey && !appByKey[idKey]) appByKey[idKey] = app;
        });
        const resolveApp = (row) => appByKey[joinKey(row.LoanApplicationNo || row.ApplicationNo)]
          || appByKey[joinKey(row.LoanApplicationID || row.ApplicationID)];

        // Claim rows returned by ReadClaimEMIForApproval carry no application
        // number/id, only UserID - so claims must be matched to a company by
        // the applicant's UserID rather than by application identifiers.
        // BUG FIX: this filter previously had no return statement, so every claim
        // row was discarded and ClaimAmount/PaidAmount always showed 0.
        const filterRowsByDate = (rows) => (rows || []).filter((row) => {
          const dt = this.toDateFlexible(this.getClaimPaidDate(row));
          return !!dt && !Number.isNaN(dt.getTime()) && dt >= fyStart && dt <= fyEnd;
        });
        const dateFilteredClaimRows = filterRowsByDate(claimRows).filter((row) => this.isClaimPaidReportRow(row));

        const rows = (this.allSubCompanies || []).map((company, idx) => {
          const isSpecialized = this.isSpecializedCompany(company);
          const allowedNameSet = isSpecialized ? this.getAllowedCompanyCasteNameSet(company) : {};
          const allowedDistrictNames = this.getAllowedDistrictNamesForSelectedCompany(company);
          const allowedDistrictLookup = {};
          allowedDistrictNames.forEach((n) => { allowedDistrictLookup[this.normalizeDistrictName(n)] = true; });

          const matchesCompany = (app) => {
            if (!app) return false;
            if (allowedDistrictNames.length > 0) {
              const distKey = this.normalizeDistrictName(app.DistrictName || app.District || "");
              if (!allowedDistrictLookup[distKey]) return false;
            }
            if (isSpecialized) {
              const tokens = this.getRowCasteTokens(app);
              if (tokens.length === 0 || !tokens.some((t) => allowedNameSet[t] === true)) return false;
            }
            return true;
          };

          const companyApps = filteredApps.filter(matchesCompany);
          const companyAppKeys = {};
          const companyUserIds = {};
          companyApps.forEach((app) => {
            const noKey = joinKey(app.LoanApplicationNo);
            if (noKey) companyAppKeys[noKey] = true;
            const idKey = joinKey(app.LoanApplicationID || app.ApplicationID);
            if (idKey) companyAppKeys[idKey] = true;
            const userKey = joinKey(app.UserID || app.LoginID || app.UserLoginID);
            if (userKey) companyUserIds[userKey] = true;
          });
          const rowBelongsToCompany = (row) => {
            const noKey = joinKey(row.LoanApplicationNo || row.ApplicationNo);
            const idKey = joinKey(row.LoanApplicationID || row.ApplicationID);
            const userKey = joinKey(row.UserID || row.LoginID || row.UserLoginID);
            return (noKey && companyAppKeys[noKey]) || (idKey && companyAppKeys[idKey]) || (userKey && companyUserIds[userKey]);
          };

          const companyLoiRows = (loiRows || []).filter(rowBelongsToCompany);
          // Dedup by claim (claimId|paidDate) so repeated EMI/schedule rows for the
          // same claim aren't summed more than once, matching Consolidation Summary.
          const seenCompanyClaims = {};
          const companyClaimRows = dateFilteredClaimRows.filter(rowBelongsToCompany).filter((row) => {
            const claimKey = this.getClaimDedupKey(row);
            if (claimKey && seenCompanyClaims[claimKey]) return false;
            if (claimKey) seenCompanyClaims[claimKey] = true;
            return true;
          });

          const loiAmount = companyLoiRows.reduce((sum, r) => sum + (parseFloat(r.SanctionAmount) || 0), 0);
          const claimAmount = companyClaimRows.reduce((sum, r) => sum + (parseFloat(r.ApprovedAmount || r.ClaimAmount || r.InterestAmt) || 0), 0);
          // Never fall back to EMIPaidAmt - it is the borrower's loan principal/EMI
          // installment, not the interest-subsidy amount actually paid, and inflates
          // this total by orders of magnitude.
          const paidAmount = companyClaimRows.reduce((sum, r) => sum + this.getClaimPaidAmount(r), 0);

          return {
            Sr: idx + 1,
            CompanyName: company.name,
            Applications: companyApps.length,
            LOICount: companyLoiRows.length,
            LOIAmount: Math.round(loiAmount * 100) / 100,
            ClaimAmount: Math.round(claimAmount * 100) / 100,
            PaidAmount: Math.round(paidAmount * 100) / 100,
          };
        });

        this.reportData = rows;
        this.reportBaseRows = rows;
        this.totalRows = rows.length;
        this.searched = true;
        this.isLoading = false;
      }).catch(() => {
        if (requestId !== this.reportRequestId) return;
        this.reportData = [];
        this.reportBaseRows = [];
        this.totalRows = 0;
        this.searched = true;
        this.isLoading = false;
      });
    },

    fetchConsolidationSummary() {
      if (!this.dateFrom || !this.dateTo) return;

      const requestId = ++this.reportRequestId;
      const allId = "00000000-0000-0000-0000-000000000000";
      this.isLoading = true;
      this.searched = false;
      this.reportData = [];
      this.reportBaseRows = [];
      this.totalRows = 0;
      this.resetStats();

      const start = this.toDateFlexible(this.dateFrom);
      const end = this.toDateFlexible(this.dateTo);
      if (end) end.setHours(23, 59, 59, 999);

      const claimRequest = {
        DistrictID: allId,
        BankID: allId,
        ClaimStatus: 0,
        ClaimPaidStatus: 0,
        LoanApplicationNo: null,
        LOINumber: null,
        UserLoginID: null,
        RecoStatus: 0,
        ClaimStartDate: null,
        ClaimEndDate: null,
        StatusOwner: "Database",
      };
      const claimPromise = apis.ReadClaimEMIForApproval(Object.assign({}, claimRequest, { LoanType: this.selectedLoanType || 0 }))
        .then((response) => this.mergeHistoricalEducationPayments(normalizeResponseArray(response)));
      const hasSpecificCasteFilter = (this.selectedCasteIDs || []).length > 0
        && (this.selectedCasteIDs || []).length < (this.casteOptions || []).length;
      const needsApplicationLookup = this.isSpecializedCompany(this.selectedCompany) || hasSpecificCasteFilter;
      const applicationPromise = needsApplicationLookup
        ? this.fetchApplicationsByStatus(
            allId,
            0,
            this.formatDateForApi(start),
            this.formatDateForApi(end),
            0
          ).catch(() => [])
        : Promise.resolve([]);

      Promise.all([claimPromise, applicationPromise]).then(([claimRows, applicationRows]) => {
        if (requestId !== this.reportRequestId) return;

        const applicationsByNumber = {};
        const applicationsById = {};
        const applicationsByUser = {};
        (applicationRows || []).forEach((application) => {
          const applicationNumber = String(application.LoanApplicationNo || application.ApplicationNo || "").trim().toLowerCase();
          const applicationId = String(application.LoanApplicationID || application.ApplicationID || "").trim().toLowerCase();
          const userId = String(application.UserID || application.LoginID || application.UserLoginID || "").trim().toLowerCase();
          if (applicationNumber && !applicationsByNumber[applicationNumber]) applicationsByNumber[applicationNumber] = application;
          if (applicationId && !applicationsById[applicationId]) applicationsById[applicationId] = application;
          if (userId && !applicationsByUser[userId]) applicationsByUser[userId] = application;
        });

        let paidClaimRows = (claimRows || []).map((row) => {
          const applicationNumber = String(row.LoanApplicationNo || row.ApplicationNo || "").trim().toLowerCase();
          const applicationId = String(row.LoanApplicationID || row.ApplicationID || "").trim().toLowerCase();
          const userId = String(row.UserID || row.LoginID || row.UserLoginID || "").trim().toLowerCase();
          const application = applicationsByNumber[applicationNumber]
            || applicationsById[applicationId]
            || applicationsByUser[userId]
            || {};
          return Object.assign({}, application, row, {
            PaidDt: this.getClaimPaidDate(row) || row.PaidDate || row.PaidDt || row.EMIPaidDate || "",
            PaidAmt: row.ClaimPaidAmt || row.PaidAmt || row.PaidAmount
              || (row.PaidDate ? row.ApprovedAmount : 0)
              || 0,
            LoanType: row.LoanType || row.LoanTypeID || row.SchemeTypeID || application.LoanType || application.LoanTypeID || row.ConsolidationLoanType || 0,
          });
        }).filter((row) => this.isClaimPaidReportRow(row));

        if (this.isSpecializedCompany(this.selectedCompany)) {
          paidClaimRows = this.enforceSpecializedCompanyRowGuard(paidClaimRows, this.selectedCompany);
        }
        paidClaimRows = this.applyReportFilters(paidClaimRows);
        if (Number(this.selectedLoanType || 0) > 0) {
          paidClaimRows = paidClaimRows.filter((row) => this.getClaimLoanType(row) === Number(this.selectedLoanType));
        }

        const buckets = {
          1: { label: "Individual", beneficiaries: {}, male: {}, female: {}, paidAmount: 0, maleAmount: 0, femaleAmount: 0 },
          2: { label: "Group", beneficiaries: {}, male: {}, female: {}, paidAmount: 0, maleAmount: 0, femaleAmount: 0 },
          3: { label: "Education", beneficiaries: {}, male: {}, female: {}, paidAmount: 0, maleAmount: 0, femaleAmount: 0 },
        };
        const allBeneficiaries = {};
        const allMale = {};
        const allFemale = {};
        let allMaleAmount = 0;
        let allFemaleAmount = 0;
        const seenClaims = {};
        let totalPaidAmount = 0;

        paidClaimRows.forEach((row) => {
          const paymentDate = this.toDateFlexible(this.getClaimPaidDate(row));
          if (!paymentDate || (start && paymentDate < start) || (end && paymentDate > end)) return;

          const claimKey = this.getClaimDedupKey(row);
          if (claimKey && seenClaims[claimKey]) return;
          if (claimKey) seenClaims[claimKey] = true;

          const paidAmount = this.getClaimPaidAmount(row);
          if (paidAmount <= 0) return;
          const beneficiaryKey = String(row.UserID || row.LoginID || row.UserLoginID || row.AadhaarNo || row.LoanApplicationNo || row.ClaimNo || "").trim();
          const schemeName = this.resolveSchemeNameFromRow(row, "").toLowerCase();
          let loanType = Number(row.LoanType || row.LoanTypeID || row.SchemeTypeID || 0);
          if ([1, 2, 3].indexOf(loanType) === -1) {
            if (schemeName.indexOf("education") !== -1) loanType = 3;
            else if (schemeName.indexOf("group") !== -1) loanType = 2;
            else if (schemeName.indexOf("individual") !== -1) loanType = 1;
          }

          totalPaidAmount += paidAmount;
          if (beneficiaryKey) allBeneficiaries[beneficiaryKey] = true;
          if (!buckets[loanType]) return;
          buckets[loanType].paidAmount += paidAmount;
          if (beneficiaryKey) {
            buckets[loanType].beneficiaries[beneficiaryKey] = true;
            const gender = this.getNormalizedGenderFromRow(row);
            if (gender === "Male") {
              buckets[loanType].male[beneficiaryKey] = true;
              buckets[loanType].maleAmount += paidAmount;
              allMale[beneficiaryKey] = true;
              allMaleAmount += paidAmount;
            } else if (gender === "Female") {
              buckets[loanType].female[beneficiaryKey] = true;
              buckets[loanType].femaleAmount += paidAmount;
              allFemale[beneficiaryKey] = true;
              allFemaleAmount += paidAmount;
            }
          }
        });

        // The Excel-reconciliation adjustment is a per-year figure (FY 2021-22/2024-25/
        // 2025-26, Individual scheme only) - same source as the Financial Year Wise
        // report. Sum only the years that are FULLY covered by the current date filter,
        // so selecting a single year adds just that year's share, not the whole total.
        const reconciliationByYear = { "2021-22": 6534, "2024-25": 105071, "2025-26": -2163 };
        const reconciliationSchemeEligible = Number(this.selectedLoanType || 0) === 0
          || Number(this.selectedLoanType || 0) === 1;
        const consolidationReconciliationAdjustment = reconciliationSchemeEligible
          ? Object.keys(reconciliationByYear).reduce((sum, fy) => {
              const bounds = this.getFinancialYearBounds(fy);
              const rangeCoversYear = !!bounds
                && (!start || start <= bounds.start)
                && (!end || end >= bounds.end);
              return sum + (rangeCoversYear ? reconciliationByYear[fy] : 0);
            }, 0)
          : 0;
        const detailRows = [1, 2, 3].map((loanType) => ({
          GroupLabel: buckets[loanType].label,
          Beneficiaries: Object.keys(buckets[loanType].beneficiaries).length,
          MaleBeneficiaries: Object.keys(buckets[loanType].male).length,
          MalePaidAmt: Math.round(buckets[loanType].maleAmount * 100) / 100,
          FemaleBeneficiaries: Object.keys(buckets[loanType].female).length,
          FemalePaidAmt: Math.round(buckets[loanType].femaleAmount * 100) / 100,
          PaidAmt: Math.round(buckets[loanType].paidAmount) + (loanType === 1 ? consolidationReconciliationAdjustment : 0),
        }));
        const rows = [{
          GroupLabel: "All",
          Beneficiaries: Object.keys(allBeneficiaries).length,
          MaleBeneficiaries: Object.keys(allMale).length,
          MalePaidAmt: Math.round(allMaleAmount * 100) / 100,
          FemaleBeneficiaries: Object.keys(allFemale).length,
          FemalePaidAmt: Math.round(allFemaleAmount * 100) / 100,
          PaidAmt: detailRows.reduce((sum, row) => sum + row.PaidAmt, 0),
        }].concat(detailRows);

        this.reportData = rows;
        this.reportBaseRows = rows;
        this.totalRows = rows.length;
        this.stats.schemeWise = detailRows.map((row) => ({ label: row.GroupLabel, count: row.Beneficiaries }));
        this.searched = true;
        this.isLoading = false;
        const claimRowsHaveGender = paidClaimRows.some((row) => {
          return row && (row.Gender !== undefined || row.GenderRaw !== undefined || row.GenderID !== undefined);
        });
        if (!claimRowsHaveGender) {
          this.enrichGenderStatsFromUserDetails(paidClaimRows, { refreshConsolidationSummary: true });
        }
      }).catch(() => {
        if (requestId !== this.reportRequestId) return;
        this.reportData = [];
        this.reportBaseRows = [];
        this.totalRows = 0;
        this.searched = true;
        this.isLoading = false;
      });
    },

    // Financial-year boundaries (Apr 1 -> Mar 31) for an "YYYY-YY" label, used to verify
    // a reconciliation adjustment only applies when the selected date range fully covers
    // that year - never to a partial/month-filtered selection.
    getFinancialYearBounds(fyLabel) {
      const match = /^(\d{4})-(\d{2})$/.exec(String(fyLabel || ""));
      if (!match) return null;
      const startYear = Number(match[1]);
      return {
        start: new Date(startYear, 3, 1, 0, 0, 0, 0),
        end: new Date(startYear + 1, 2, 31, 23, 59, 59, 999),
      };
    },
    fetchFinancialYearSchemeWise() {
      const temporaryReconciliationByYear = {
        "2021-22": 6534,
        "2024-25": 105071,
        "2025-26": -2163,
      };
      const requestId = ++this.reportRequestId;
      this.isLoading = true;
      this.searched = false;
      this.reportData = [];
      this.reportBaseRows = [];
      this.totalRows = 0;
      this.resetStats();

      const startIso = this.dateFrom ? this.formatDateIso(this.toDateFlexible(this.dateFrom)) : "";
      const endIso = this.dateTo ? this.formatDateIso(this.toDateFlexible(this.dateTo)) : "";
      const selectedStart = this.dateFrom ? this.toDateFlexible(this.dateFrom) : null;
      const selectedEnd = this.dateTo ? this.toDateFlexible(this.dateTo) : null;
      const selectedLoanType = Number(this.selectedLoanType || 0);
      const schemeNameByLoanType = { 1: "individual", 2: "group", 3: "education" };
      const selectedSchemeName = schemeNameByLoanType[selectedLoanType] || "";

      apis.getFinancialYearSchemeWise(startIso, endIso).then((response) => {
        if (requestId !== this.reportRequestId) return;
        let rows = (response && (response.body || response.data)) || [];
        // Apply the Scheme filter dynamically - previously every scheme was combined
        // into one row per year regardless of the selected Loan Type.
        if (selectedSchemeName) {
          rows = (rows || []).filter((r) => String(r.Scheme || "").toLowerCase() === selectedSchemeName);
        }
        const byYear = {};
        (rows || []).forEach((r) => {
          const fy = String(r.FinancialYear || "");
          if (!fy) return;
          if (!byYear[fy]) {
            byYear[fy] = {
              GroupLabel: fy, ApplicantName: fy, Applications: 0, LOICount: 0,
              Claims: 0, ClaimCount: 0, AmountDemanded: 0, AmountApproved: 0,
              ClaimAmount: 0, PaidAmt: 0, ReconciliationAdjustment: 0, ReportedPaidAmt: 0,
              Districts: "", ClaimNumbers: "",
              ClaimDates: "", ClaimStatuses: ""
            };
          }
          const t = byYear[fy];
          t.Applications += Number(r.Applications || 0);
          t.LOICount += Number(r.LOIs || 0);
          t.AmountDemanded += Number(r.LOILoanAmount || 0);
          t.AmountApproved += Number(r.BankSanctioned || 0);
          t.Claims += Number(r.ClaimsPaid || 0);
          t.ClaimCount += Number(r.ClaimsPaid || 0);
          t.ClaimAmount += Number(r.PaidAmount || 0);
          t.PaidAmt += Number(r.PaidAmount || 0);
        });
        const result = Object.keys(byYear).sort().map((fy) => {
          const row = byYear[fy];
          // Only reuse the fixed Excel-reconciliation adjustment when the current
          // selection fully covers that financial year (Apr-Mar) and includes every
          // scheme (or Individual, since the adjustment is Individual-only) - a
          // month/quarter filter or a Group/Education-only filter must not carry it.
          const bounds = this.getFinancialYearBounds(fy);
          const rangeCoversFullYear = !!bounds
            && (!selectedStart || selectedStart <= bounds.start)
            && (!selectedEnd || selectedEnd >= bounds.end);
          const schemeEligible = selectedLoanType === 0 || selectedLoanType === 1;
          row.ReconciliationAdjustment = (rangeCoversFullYear && schemeEligible)
            ? (temporaryReconciliationByYear[fy] || 0)
            : 0;
          row.ReportedPaidAmt = row.PaidAmt + row.ReconciliationAdjustment;
          row.PaidAmt = row.ReportedPaidAmt;
          return row;
        });
        this.reportData = result;
        this.reportBaseRows = result;
        this.tableFields = this.withReportDateFormatters(this.getTableFieldsForReportType("financial_year_wise"));
        this.totalRows = result.length;
        this.searched = true;
        this.isLoading = false;
      }).catch(() => {
        if (requestId !== this.reportRequestId) return;
        this.reportData = [];
        this.reportBaseRows = [];
        this.totalRows = 0;
        this.searched = true;
        this.isLoading = false;
      });
    },

    fetchReport() {
      if (this.dateFrom && this.dateTo) {
        const selectedStart = this.toDateFlexible(this.dateFrom);
        const selectedEnd = this.toDateFlexible(this.dateTo);
        if (selectedStart && selectedEnd && selectedStart > selectedEnd) {
          this.dateFrom = this.formatDateIso(selectedEnd);
          this.dateTo = this.formatDateIso(selectedStart);
        }
      }
      if (String(this.selectedReportType || "").toLowerCase() === "consolidation_summary") {
        this.fetchConsolidationSummary();
        return;
      }
      if (String(this.selectedReportType || "").toLowerCase() === "company_wise") {
        this.fetchCompanyWiseReport();
        return;
      }
      if (String(this.selectedReportType || "").toLowerCase() === "financial_year_wise") {
        this.fetchFinancialYearSchemeWise();
        return;
      }
      if ((!this.canSearchWithoutCompany && (this.selectedCompanyID === null || this.selectedCompanyID === undefined)) || !this.dateFrom || !this.dateTo) return;
      if (!this.canSearchWithoutCompany && !this.selectedCompany) {
        this.isLoading = false;
        this.searched = true;
        this.reportData = [];
        this.reportBaseRows = [];
        this.schemeDetailRows = [];
        this.beneficiaryReportRows = [];
        this.loiStatusRows = [];
        this.loiStatusSummary = { generated: 0, pending: 0, total: 0 };
        this.totalRows = 0;
        this.loiSummary = { count: 0, amount: 0 };
        this.resetStats();
        return;
      }
      const requestId = ++this.reportRequestId;
      this.isLoading = true;
      this.searched  = false;
      this.reportData = [];
      this.reportBaseRows = [];
      this.beneficiaryReportRows = [];
      this.schemeDetailRows = [];
      this.loiStatusRows = [];
      this.loiStatusSummary = { generated: 0, pending: 0, total: 0 };
      this.totalRows = 0;
      this.loiSummary = { count: 0, amount: 0 };
      this.loiSummaryLoading = false;
      this.resetStats();

      setTimeout(() => {
        if (requestId === this.reportRequestId && this.isLoading) {
          this.isLoading = false;
          this.searched = true;
        }
      }, Number(this.apiTimeoutMs || 45000) + 5000);

      const districtID = this.getEffectiveDistrictId();
      const allDistrictId = "00000000-0000-0000-0000-000000000000";
      const selectedReportType = String(this.selectedReportType || "applications").toLowerCase();
      const isGroupedType = this.isGroupedReportType(selectedReportType);
      const hasLoiSpecificFilter = String(this.loiNumberFilter || "").trim() !== "";

      const selectedCompany = this.selectedCompany;
      const casteCodes = selectedCompany && selectedCompany.castes
        ? selectedCompany.castes.map((c) => c.casteCode)
        : [];
      const casteNames = selectedCompany && selectedCompany.castes
        ? selectedCompany.castes.map((c) => c.casteName.trim().toLowerCase())
        : [];
      const selectedRange = this.getSelectedDateRange();
      const fyStart = selectedRange ? selectedRange.startDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyStart;
      const fyEnd = selectedRange ? selectedRange.endDate : this.getDateRangeFromFY(this.selectedYear || "2020-2021").fyEnd;
      const companyStartDate = this.getCompanyEstablishmentDate(selectedCompany);
      const appStartDate = selectedRange ? selectedRange.startText : this.formatDateForApi(fyStart);
      const appEndDate = selectedRange ? selectedRange.endText : this.formatDateForApi(fyEnd);
      const loiStartDate = this.formatDateIso(fyStart);
      const loiEndDate = this.formatDateIso(fyEnd);

      // Helper: find first matching key value from an object
      const pick = (obj, keys, fallback) => {
        for (let k of keys) {
          if (obj && obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
        }
        return fallback;
      };
      const pickText = (obj, keys, fallback = "") => {
        const values = Array.isArray(keys) ? keys : [keys];
        for (const key of values) {
          const value = obj && obj[key];
          if (value === undefined || value === null || value === "") continue;
          const text = String(value).trim();
          if (text && !["unknown", "not known", "n/a", "na", "null"].includes(text.toLowerCase())) {
            return text;
          }
        }
        return fallback;
      };
      const pickTextAcross = (sources, keys, fallback = "") => {
        for (const source of (sources || [])) {
          const value = pickText(source || {}, keys, "");
          if (value) return value;
        }
        return fallback;
      };
      const pickNumber = (obj, keys, fallback = 0) => {
        const values = Array.isArray(keys) ? keys : [keys];
        for (const key of values) {
          const value = obj && obj[key];
          if (value === undefined || value === null || value === "") continue;
          const text = String(value).trim();
          if (!text || ["unknown", "not known", "n/a", "na", "null"].includes(text.toLowerCase())) continue;
          const numeric = Number(String(text).replace(/,/g, ""));
          if (!Number.isNaN(numeric)) return numeric;
        }
        return Number(fallback) || 0;
      };
      const pickNumberAcross = (sources, keys, fallback = 0) => {
        for (const source of (sources || [])) {
          const value = pickNumber(source || {}, keys, NaN);
          if (!Number.isNaN(value) && value > 0) return value;
        }
        return Number(fallback) || 0;
      };

      const normalizeJoinKey = (value) => String(value || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
      const buildJoinCandidates = (value) => {
        const raw = String(value || "").trim();
        if (!raw) return [];

        const unique = {};
        const out = [];
        const push = (v) => {
          const key = normalizeJoinKey(v);
          if (!key || unique[key]) return;
          unique[key] = true;
          out.push(key);
        };

        push(raw);
        const parts = raw.split("-").map((p) => p.trim()).filter(Boolean);
        if (parts.length > 1) {
          push(parts.slice(1).join("-"));
          push(parts[parts.length - 1]);
        }

        const compact = normalizeJoinKey(raw);
        const appNoCore = compact.match(/(IL|GL|EL)\d+/);
        if (appNoCore && appNoCore[0]) {
          push(appNoCore[0]);
        }

        return out;
      };
      const districtNameById = {};
      (this.allDistrictRows || []).forEach((row) => {
        const id = String((row && (row.DistrictID || row.DistrictId || row.DistID)) || "").trim();
        const name = String((row && (row.DistrictName || row.DistName || row.District)) || "").trim();
        if (id && name) {
          districtNameById[id] = name;
        }
      });
      const casteNameById = {};
      (this.casteOptions || []).forEach((row) => {
        const id = String((row && row.value) || "").trim();
        const name = String((row && row.text) || "").trim();
        if (id && name) {
          casteNameById[id] = name;
        }
      });

      const extractLoanApplicationNo = (obj) => {
        if (!obj || typeof obj !== "object") return "";
        const candidates = [
          obj.LoanApplicationNo,
          obj.LoanApplicationNumber,
          obj.LoanAppNo,
          obj.ApplicationNo,
          obj.ApplicationNumber,
          obj.AppNo,
          obj.AppNumber,
          obj.Application_No,
          obj.ApplicationNoText,
          obj.LoanApplication,
        ];
        for (const value of candidates) {
          const text = String(value || "").trim();
          if (text && text.toLowerCase() !== "unknown" && text.toLowerCase() !== "not known" && text.toLowerCase() !== "n/a") return text;
        }
        return "";
      };

      const extractApplicationId = (obj) => {
        if (!obj || typeof obj !== "object") return "";
        const candidates = [
          obj.LoanApplicationID,
          obj.ApplicationID,
          obj.LoanApplicationId,
          obj.ApplicationId,
          obj.AppID,
          obj.Id,
          obj.ApplicationIdGuid,
        ];
        for (const value of candidates) {
          const text = String(value || "").trim();
          if (text && text.toLowerCase() !== "unknown" && text.toLowerCase() !== "not known") return text;
        }
        return "";
      };

      const mapToApplicationShape = (rows, sourceType) => {
        return (rows || []).map((row) => {
          const loanAppNo = extractLoanApplicationNo(row);
          const loanAppId = extractApplicationId(row);
          const normalizedSourceType = String(sourceType || "applications").toLowerCase();
          const sourceDate = normalizedSourceType === "loi" || normalizedSourceType === "sanction_letters"
            ? (row.LOIDate || row.LoiDate || row.SanctionDate || row.RecordCreatedDate || row.UpdatedDate || row.CreateDate || row.ApplicationDate || row.Createdate || row.CreatedDate || "")
            : (normalizedSourceType === "disbursements"
              ? (row.DisbursementDate || row.DisburmentDate || row.UpdatedDate || row.CreateDate || row.ApplicationDate || row.Createdate || row.CreatedDate || "")
              : (normalizedSourceType === "claims"
                ? (row.ClaimDate || row.AppliedForClaimDtd || row.EMIPaidDate || row.PaidDate || row.CreatedDate || row.ApplicationDate || row.LOIDate || "")
                : (row.ApplicationDate || row.Createdate || row.CreateDate || row.CreatedDate || row.LOIDate || row.DisbursementDate || row.EMIPaidDate || "")));
          return {
            ...row,
            LoanApplicationNo: loanAppNo,
            LoanApplicationID: loanAppId,
            ApplicationID: loanAppId || String(row.ApplicationID || row.AppID || row.Id || "").trim(),
            UserID: String(row.UserID || row.LoginID || row.UserLoginID || row.UserId || "").trim(),
            DistrictName: row.DistrictName || row.District || row.DistName || row.LoanDistrict || row.DistrictNm || row.DistrictNameMarathi || "",
            SchemeType: this.resolveSchemeNameFromRow(row, this.selectedLoanType || ""),
            ApplicationDate: sourceDate,
            ApplicationStatus:
              row.ApplicationStatus ||
              row.Status ||
              row.ApprovalStatus ||
              row.ClaimStatus ||
              ((sourceType === "sanction_letters" || sourceType === "loi") ? "LOI Printed" : ""),
            ApplicationStatusID: row.ApplicationStatusID || row.StatusID || row.AppStatusID || row.ClaimStatusID || row.ClaimStatus || 0,
            AmountApproved: pickNumber(row, [
              "AmountApproved", "ApprovedAmount", "SanctionAmount", "HOApprovedAmount", "TotalApprovedAmount",
              "SanctionedAmount", "AmountSanctioned", "LoanAmountApproved", "DisbursementAmount", "DisburmentAmount"
            ], 0),
            LOINumber: pickText(row, [
              "LOINumber", "LoiNumber", "LOINo", "LoiNo", "SanctionLetterNo", "SanctionNo", "LOI_No",
              "LoanSanctionLetterNo", "LetterNo"
            ], ""),
            LOIDate: pickText(row, [
              "LOIDate", "LoiDate", "SanctionDate", "IssueDate", "LOI_Date", "UpdatedDate", "CreatedDate",
              "RecordCreatedDate", "ApplicationDate", "LoanDate"
            ], ""),
            LOIDueDate: pickText(row, [
              "LOIDueDate", "LoiDueDate", "DueDate", "LOIExpiryDate", "ExpiryDate", "ValidUpto", "ValidUpTo",
              "LOIValidUpto", "LOIValidTill", "LoanDueDate"
            ], ""),
            ApplicantName: row.ApplicantName || row.FullName || [row.FirstName, row.MiddleName, row.LastName].filter(Boolean).join(" ") || row.BeneficiaryName || "",
            ADDRESS: pickText(row, ["ADDRESS", "Address", "FullAddress", "ApplicantAddress", "CurrentAddress"], ""),
            OfficeName: row.OfficeName || row.BranchName || row.LoanBranch || row.Branch || row.Office || row.SubOffice || row.Division || row.BankName || "",
            BankName: pickText(row, [
              "BankName", "Bank", "BankNm", "BankNameMarathi", "LoanBankName", "BankNameEnglish", "BranchName", "LoanBranch"
            ], ""),
            ClaimNo: row.ClaimNo || row.ClaimNumber || row.ClaimApplicationNo || row.ClaimNoTx || "",
            ClaimAmount: normalizedSourceType === "claims" || normalizedSourceType === "claim_applicant_wise"
              ? this.getClaimPaidAmount(row)
              : (row.ClaimAmount || 0),
            PaidAmt: normalizedSourceType === "claims" || normalizedSourceType === "claim_applicant_wise"
              ? this.getClaimPaidAmount(row)
              : (row.PaidAmt || 0),
          };
        }).filter((x) => {
          const hasApplicationIdentity = String(x.LoanApplicationNo || "").trim() !== "" || String(x.ApplicationID || "").trim() !== "";
          if (hasApplicationIdentity) return true;

          // Some LOI backend rows do not carry application ids, but are still valid LOI records.
          const isLoiSource = String(sourceType || "").toLowerCase() === "loi" || String(sourceType || "").toLowerCase() === "sanction_letters";
          const isClaimSource = ["claims", "claim_applicant_wise", "financial_year_wise"].indexOf(String(sourceType || "").toLowerCase()) !== -1;
          return (isLoiSource && String(x.LOINumber || "").trim() !== "")
            || (isClaimSource && String(x.ClaimEMIID || "").trim() !== "");
        });
      };

      const fetchSanctionRows = (did) => {
        if (!this.canUseSanctionApi()) {
          return Promise.resolve([]);
        }
        const form = {
          LoanApplicationNo: this.applicationNoFilter || null,
          LOINumber: this.loiNumberFilter || null,
          LOIStartDate: null,
          LOIEndDate: null,
          BankID: this.selectedBankID || "00000000-0000-0000-0000-000000000000",
          DistrictID: did,
          UserLoginID: null,
          RecordStartDate: loiStartDate,
          RecordEndDate: loiEndDate,
        };
        return apis.ReadSanctionLetterQuery(form)
          .then((r) => normalizeResponseArray(r))
          .then((rows) => {
            if (Array.isArray(rows) && rows.length > 0) return rows;
            // Fallback when backend rejects or ignores date-bound sanction query.
            return apis.ReadSanctionLetterQuery({
              ...form,
              RecordStartDate: null,
              RecordEndDate: null,
            }).then((rr) => normalizeResponseArray(rr)).catch(() => []);
          })
          .catch((err) => {
            const status = this.getHttpStatus(err);
            if (status >= 500 || status === 404 || this.isNetworkOrCorsError(err)) {
              this.markSanctionApiTemporarilyDisabled();
            }
            return [];
          });
      };

      const fetchDisbursementRows = (did) => {
        const form = {
          LoanApplicationNo: this.applicationNoFilter || null,
          LOINumber: this.loiNumberFilter || null,
          DisbursementStartDate: this.dateFrom || null,
          DisbursementEndDate: this.dateTo || null,
          BankID: this.selectedBankID || "00000000-0000-0000-0000-000000000000",
          DistrictID: did,
          UserLoginID: null,
          LoanType: this.selectedLoanType || 0,
        };
        return apis.ReadDisbursementQuery(form)
          .then((r) => normalizeResponseArray(r))
          .catch(() => fetchSanctionRows(did));
      };

      const fetchClaimRows = (did) => {
        const form = {
          LoanApplicationNo: this.applicationNoFilter || null,
          LOINumber: this.loiNumberFilter || null,
          ClaimStartDate: this.dateFrom || null,
          ClaimEndDate: this.dateTo || null,
          BankID: this.selectedBankID || "00000000-0000-0000-0000-000000000000",
          DistrictID: did,
          UserLoginID: null,
          ClaimStatus: this.selectedStatusId || 0,
          ClaimPaidStatus: 0,
          LoanType: this.selectedLoanType || 0,
          StatusOwner: "Database",
        };

        const filterByPaymentDate = (rows) => {
          const start = this.toDateFlexible(this.dateFrom);
          const end = this.toDateFlexible(this.dateTo);
          if (!start || !end) return rows || [];
          end.setHours(23, 59, 59, 999);
          const normalizedRows = (rows || []).filter((row) => {
            const paymentDate = this.toDateFlexible(this.getClaimPaidDate(row));
            return paymentDate && paymentDate >= start && paymentDate <= end;
          });
          const selectedLoanType = Number(this.selectedLoanType || 0);
          if (!selectedLoanType) return normalizedRows;
          return normalizedRows.filter((row) => this.getClaimLoanType(row) === selectedLoanType);
        };

        const fetchWithoutDateFilter = () => apis.ReadClaimEMIForApproval({
          ...form,
          ClaimStartDate: null,
          ClaimEndDate: null,
        }).then((response) => filterByPaymentDate(this.mergeHistoricalEducationPayments(normalizeResponseArray(response))));

        return fetchWithoutDateFilter().then((rows) => {
          const seenClaimIds = {};
          return (rows || []).filter((row) => {
            // Match Consolidation Summary dedup identity (getClaimDedupKey) on raw rows,
            // including ClaimApplicationNo/ClaimNoTx so duplicate paid claims collapse.
            const claimId = String(row.ClaimEMIID || row.ClaimEMIId || row.ClaimID || row.ClaimId || row.ClaimNo || row.ClaimNumber || row.ClaimApplicationNo || row.ClaimNoTx || "").trim();
            if (!claimId) return true;
            const paymentKey = claimId + "|" + String(this.getClaimPaidDate(row) || "").slice(0, 10);
            if (seenClaimIds[paymentKey]) return false;
            seenClaimIds[paymentKey] = true;
            return true;
          }).map((row) => {
            const loanAppNo = String(row.LoanApplicationNo || row.ApplicationNo || row.LoanApplicationNumber || row.AppNo || row.ApplicationNumber || row.LoanAppNo || "").trim();
            const loanAppId = String(row.LoanApplicationID || row.ApplicationID || "").trim();
            const paidAmount = Number(row.ClaimPaidAmt || row.PaidAmt || row.PaidAmount || 0)
              || (this.getClaimPaidDate(row) ? Number(row.ApprovedAmount || row.ClaimAmount || row.InterestAmt || 0) : 0);
            return {
              ...row,
              LoanApplicationNo: loanAppNo,
              LoanApplicationID: loanAppId,
              ApplicationID: loanAppId,
              ApplicationDate: row.PaidDate || row.ClaimDate || row.AppliedForClaimDtd || row.EMIPaidDate || row.CreatedDate || row.LOIDate || "",
              ApplicationStatus: row.ClaimStatus || row.ClaimPaymentStatus || row.Status || "",
              ApplicationStatusID: row.ClaimStatusID || row.ClaimStatus || row.ClaimPaymentStatusID || row.StatusID || 0,
              ClaimAmount: row.ApprovedAmount || row.ClaimAmount || row.InterestAmt || 0,
              PaidAmt: paidAmount,
              AppliedForClaimDtd: this.getClaimPaidDate(row) || row.PaidDate || row.ClaimDate || row.AppliedForClaimDtd || row.EMIPaidDate || row.CreatedDate || "",
              PaidDt: this.getClaimPaidDate(row) || row.PaidDate || row.PaidDt || row.EMIPaidDate || "",
              DistrictName: row.DistrictName || row.District || row.DistName || row.DistrictNm || "",
              OfficeName: row.OfficeName || row.BranchName || row.LoanBranch || row.BankName || row.Branch || "",
              ApplicantName: row.ApplicantName || row.FullName || row.FirstName || row.BeneficiaryName || "",
              BankName: row.LoanBank || row.BankName || row.Bank || row.BankNm || row.LoanBranch || row.BranchName || row.BankNameMarathi || "",
              ClaimNo: row.ClaimNo || row.ClaimNumber || row.ClaimApplicationNo || row.ClaimNoTx || row.ClaimEMIID || "",
            };
          }).filter((row) => this.isClaimPaidReportRow(row));
        }).catch(() => []);
      };

      const fetchSourceRowsByType = () => {
        const selectedType = selectedReportType;

        // Financial Year Wise shows only claim-paid figures per year, so skip the heavy
        // applications fetch and let the claim-only fallback build the report (much faster).
        if (selectedType === "financial_year_wise") {
          return Promise.resolve([]);
        }

        if (selectedType === "sanction_letters") {
          return fetchSanctionRows(districtID).then((rows) => {
            if ((rows || []).length > 0 || districtID === allDistrictId) {
              return mapToApplicationShape(rows, selectedType);
            }
            return fetchSanctionRows(allDistrictId)
              .then((allRows) => mapToApplicationShape(allRows, selectedType))
              .catch(() => []);
          }).catch(() => []);
        }

        if (selectedType === "loi") {
          return fetchSanctionRows(districtID).then((rows) => {
            if ((rows || []).length > 0) {
              return mapToApplicationShape(rows, selectedType);
            }

            const nextFetch = districtID !== allDistrictId
              ? fetchSanctionRows(allDistrictId)
              : Promise.resolve([]);

            return nextFetch.then((allRows) => {
              if ((allRows || []).length > 0) {
                return mapToApplicationShape(allRows, selectedType);
              }

              return fetchDisbursementRows(allDistrictId)
                .then((disbRows) => {
                  if ((disbRows || []).length > 0) {
                    return mapToApplicationShape(disbRows, selectedType);
                  }

                  // Last-resort fallback when sanction/disbursement endpoints are unavailable:
                  // use applications pipeline so selected company/date/caste filters still apply.
                  return this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, appStartDate, appEndDate, this.selectedStatusId)
                    .then((fallbackRows) => mapToApplicationShape(fallbackRows, "applications"))
                    .catch(() => []);
                })
                .catch(() => this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, appStartDate, appEndDate, this.selectedStatusId)
                  .then((fallbackRows) => mapToApplicationShape(fallbackRows, "applications"))
                  .catch(() => []));
            });
          }).catch(() => []);
        }

        if (selectedType === "disbursements") {
          return fetchDisbursementRows(districtID).then((rows) => {
            if (rows.length > 0 || districtID === allDistrictId) return mapToApplicationShape(rows, selectedType);
            return fetchDisbursementRows(allDistrictId).then((allRows) => {
              if ((allRows || []).length > 0) return mapToApplicationShape(allRows, selectedType);
              return this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, appStartDate, appEndDate, this.selectedStatusId)
                .then((fallbackRows) => mapToApplicationShape(fallbackRows, "applications"))
                .catch(() => []);
            });
          }).catch(() => this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, appStartDate, appEndDate, this.selectedStatusId)
            .then((fallbackRows) => mapToApplicationShape(fallbackRows, "applications"))
            .catch(() => []));
        }

        if (selectedType === "claims" || selectedType === "claim_applicant_wise") {
          return fetchClaimRows(districtID).then((rows) => {
            if (rows.length > 0 || districtID === allDistrictId) return mapToApplicationShape(rows, selectedType);
            return fetchClaimRows(allDistrictId).then((allRows) => {
              if ((allRows || []).length > 0) return mapToApplicationShape(allRows, selectedType);
              return this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, appStartDate, appEndDate, this.selectedStatusId)
                .then((fallbackRows) => mapToApplicationShape(fallbackRows, "applications"))
                .catch(() => []);
            });
          }).catch(() => this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, appStartDate, appEndDate, this.selectedStatusId)
            .then((fallbackRows) => mapToApplicationShape(fallbackRows, "applications"))
            .catch(() => []));
        }

        return this.fetchApplicationsByStatus(districtID, this.selectedLoanType, appStartDate, appEndDate, this.selectedStatusId)
          .then((rows) => {
            if (rows.length > 0 || districtID === allDistrictId) {
              return mapToApplicationShape(rows, selectedType);
            }
            // Fallback when selected-district endpoint projection is inconsistent.
            return this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, appStartDate, appEndDate, this.selectedStatusId)
              .then((fallbackRows) => mapToApplicationShape(fallbackRows, selectedType))
              .catch(() => []);
          })
          .catch(() => []);
      };

      // Fetch source rows according to selected report type, then join with LOI/claims.
      const appPromise = fetchSourceRowsByType();

      const fetchLoiByDistrict = (did) => {
        if (!this.canUseSanctionApi()) {
          return Promise.resolve([]);
        }
        const form = {
          LoanApplicationNo: null,
          LOINumber: null,
          LOIStartDate: null,
          LOIEndDate: null,
          BankID: "00000000-0000-0000-0000-000000000000",
          DistrictID: did,
          UserLoginID: null,
          RecordStartDate: loiStartDate,
          RecordEndDate: loiEndDate,
        };
        return apis.ReadSanctionLetterQuery(form)
          .then((r) => normalizeResponseArray(r))
          .then((rows) => {
            if (Array.isArray(rows) && rows.length > 0) return rows;
            return apis.ReadSanctionLetterQuery({
              ...form,
              RecordStartDate: null,
              RecordEndDate: null,
            }).then((rr) => normalizeResponseArray(rr)).catch(() => []);
          })
          .catch((err) => {
            const status = this.getHttpStatus(err);
            if (status >= 500 || status === 404 || this.isNetworkOrCorsError(err)) {
              this.markSanctionApiTemporarilyDisabled();
            }
            return [];
          });
      };

      const tableFieldsForSelectedType = this.getTableFieldsForReportType(selectedReportType);
      const applicationViewHasLoiColumns = Array.isArray(tableFieldsForSelectedType)
        && tableFieldsForSelectedType.some((field) => String((field && field.key) || "").toLowerCase() === "loinumber");
      const shouldFetchLoiOverlay =
        selectedReportType !== "applications" || hasLoiSpecificFilter || applicationViewHasLoiColumns;
      const shouldFetchSeparateLoiOverlay =
        shouldFetchLoiOverlay && selectedReportType !== "loi" && selectedReportType !== "sanction_letters";
      const effectiveLoiPromise = shouldFetchSeparateLoiOverlay
        ? fetchLoiByDistrict(districtID).then((rows) => {
            if ((rows || []).length > 0 || districtID === allDistrictId) {
              return rows || [];
            }
            return fetchLoiByDistrict(allDistrictId).catch(() => []);
          })
        : Promise.resolve([]);

      const selectedSourceType = selectedReportType;
      const shouldUseApplicationLookup = [
        "applications",
        "loi",
        "disbursements",
        "claims",
        "claim_applicant_wise",
        "financial_year_wise",
        "gender_wise",
        "caste_wise",
        "district_wise",
        "division_wise",
      ].indexOf(selectedSourceType) !== -1;
      const canReusePrimaryApplicationRowsForLookup = selectedSourceType === "applications" || this.isGroupedReportType(selectedSourceType);
      const applicationLookupPromise = shouldUseApplicationLookup
        ? (canReusePrimaryApplicationRowsForLookup
            ? appPromise
            : this.fetchApplicationsByStatus(districtID, this.selectedLoanType, appStartDate, appEndDate, 0)
            .then((rows) => {
              if ((rows || []).length > 0 || districtID === allDistrictId) return rows || [];
              return this.fetchApplicationsByStatus(allDistrictId, this.selectedLoanType, appStartDate, appEndDate, 0)
                .catch(() => []);
            })
            .catch(() => []))
        : Promise.resolve([]);

      Promise.all([appPromise, effectiveLoiPromise, applicationLookupPromise])
        .then(([appRows, loiData, applicationLookupRows]) => {
          if (requestId !== this.reportRequestId) {
            return { appRows: [], loiData: [], claimData: [], applicationLookupRows: [] };
          }

          const selectedType = String(this.selectedReportType || "applications").toLowerCase();
          const effectiveLoiData = (selectedType === "loi" || selectedType === "sanction_letters")
            ? ((Array.isArray(loiData) && loiData.length > 0) ? loiData : (appRows || []))
            : (loiData || []);
          const loiFallbackRows = (selectedType === "loi" || selectedType === "sanction_letters")
            ? mapToApplicationShape(effectiveLoiData || [], selectedType)
            : [];
          const effectiveAppRows = (Array.isArray(appRows) && appRows.length > 0) ? appRows : loiFallbackRows;

          if ((!Array.isArray(effectiveAppRows) || effectiveAppRows.length === 0) && selectedType === "financial_year_wise") {
            return fetchClaimRows(districtID)
              .then((claimData) => ({ appRows: [], loiData: effectiveLoiData, claimData, applicationLookupRows }))
              .catch(() => ({ appRows: [], loiData: effectiveLoiData, claimData: [], applicationLookupRows }));
          }

          if (!Array.isArray(effectiveAppRows) || effectiveAppRows.length === 0) {
            return { appRows: [], loiData: [], claimData: [], applicationLookupRows: [] };
          }

          const shouldFetchClaims =
            selectedType !== "claims" &&
            selectedType !== "claim_applicant_wise" &&
            (selectedType === "financial_year_wise" ||
              selectedType === "caste_wise" ||
              selectedType === "gender_wise" ||
              selectedType === "district_wise" ||
              selectedType === "division_wise" ||
              (!isGroupedType && this.enableClaimEnrichmentForApplications === true));

          if (!shouldFetchClaims) {
            return { appRows: effectiveAppRows, loiData: effectiveLoiData, claimData: [], applicationLookupRows };
          }

          const claimPromise = ["financial_year_wise", "caste_wise", "gender_wise", "district_wise", "division_wise"].indexOf(selectedType) !== -1
            ? fetchClaimRows(districtID)
            : this.fetchClaimsForApplications(effectiveAppRows, districtID);
          return claimPromise
            .then((claimData) => ({ appRows: effectiveAppRows, loiData: effectiveLoiData, claimData, applicationLookupRows }));
        })
        .then(({ appRows, loiData, claimData, applicationLookupRows }) => {
          if (requestId !== this.reportRequestId) {
            return;
          }
          const selectedType = String(this.selectedReportType || "applications").toLowerCase();
          let all = Array.isArray(appRows) ? appRows : [];
          let loiRows = Array.isArray(loiData) ? loiData : [];
          let claimRows = Array.isArray(claimData) ? claimData : [];
          const appMasterSourceRows = (applicationLookupRows || []).concat(
            (selectedType === "applications" || this.isGroupedReportType(selectedType)) ? all : []
          );
          const selectedCompany = this.selectedCompany;

          // Build LOI totals directly from sanction rows so summary does not depend
          // on application list status subsets.
          const selectedRangeForLoi = this.getSelectedDateRange();
          const loiRangeStart = selectedRangeForLoi ? selectedRangeForLoi.startDate : fyStart;
          const loiRangeEnd = selectedRangeForLoi ? selectedRangeForLoi.endDate : fyEnd;
          const loiEffectiveStart = companyStartDate && companyStartDate > loiRangeStart ? companyStartDate : loiRangeStart;
          const scopedLoiRows = (loiRows || []).filter((loi) => {
            const loiNumber = String(loi.LOINumber || loi.LOINo || loi.LoiNo || loi.SanctionLetterNo || loi.SanctionNo || "").trim();
            const loiDate = this.toDateFlexible(loi.LOIDate || loi.LoiDate || loi.SanctionDate || loi.IssueDate || loi.UpdatedDate || loi.RecordCreatedDate || loi.CreateDate || "");
            if (!loiDate || Number.isNaN(loiDate.getTime())) {
              // Some backend projections omit LOI date; keep rows that still carry LOI number.
              return loiNumber !== "";
            }
            return loiDate >= loiEffectiveStart && loiDate <= loiRangeEnd;
          });
          loiRows = scopedLoiRows;

          const loiTotals = { count: 0, amount: 0, byScheme: {} };
          (loiRows || []).forEach((loi) => {
            const loiNumber = String(loi.LOINumber || "").trim();
            if (!loiNumber) return;

            loiTotals.count += 1;
            const approved = parseFloat(
              loi.SanctionAmount || loi.HOApprovedAmount || loi.DisbursementAmount || loi.DisburmentAmount || loi.ApprovedAmount || 0
            ) || 0;
            loiTotals.amount += approved;

              const schemeLabel = this.resolveSchemeNameFromRow(loi, this.selectedLoanType || "");
            if (!loiTotals.byScheme[schemeLabel]) {
              loiTotals.byScheme[schemeLabel] = { count: 0, amount: 0 };
            }
            loiTotals.byScheme[schemeLabel].count += 1;
            loiTotals.byScheme[schemeLabel].amount += approved;
          });

          // Build LOI lookup by application no / application id / user id
          const loiMapNorm = {};
          const loiMapByAppId = {};
          const loiMapByUserId = {};
          loiRows.forEach((loi) => {
            const key = String(
              loi.LoanApplicationNo || loi.ApplicationNo || loi.LoanApplicationNumber || loi.AppNo || loi.ApplicationNumber || loi.LoanAppNo || ""
            ).trim();
            const appId = String(loi.LoanApplicationID || loi.ApplicationID || loi.AppID || "").trim();
            buildJoinCandidates(key).forEach((k) => {
              if (!loiMapNorm[k]) loiMapNorm[k] = loi;
            });
            if (appId) loiMapByAppId[appId] = loi;
          });

          // Build Claim lookup by application no / application id / user id (aggregate)
          const claimMapNorm = {};
          const claimMapByAppId = {};
          const claimMapByUserId = {};
          const mergeClaim = (target, row) => {
            if (!target) return;
            const firstNumber = (keys) => {
              for (const key of keys) {
                if (row[key] === undefined || row[key] === null || row[key] === "") continue;
                const value = parseFloat(String(row[key]).replace(/,/g, ""));
                if (!Number.isNaN(value)) return value;
              }
              return 0;
            };
            target.TotalClaimAmount += firstNumber([
              "ApprovedAmount", "ClaimPaidAmt", "ClaimAmount", "EMIPaidAmt"
            ]);
            const isEducationClaim = Number(row.LoanType || row.LoanTypeID) === 3;
            target.TotalPaidClaim += firstNumber(
              isEducationClaim
                ? ["ClaimPaidAmt", "PaidAmt", "PaidAmount", "ApprovedAmount"]
                : ["ClaimPaidAmt", "PaidAmt", "PaidAmount"]
            );
            target.EMIPaidDate = row.PaidDate || row.EMIPaidDate || row.UpdatedDate || target.EMIPaidDate;
            target.ClaimStatus = row.ClaimStatus || target.ClaimStatus;
            target.ClaimNo = row.ClaimNo || row.ClaimNumber || row.ClaimApplicationNo || row.ClaimEMIID || target.ClaimNo;
          };

          claimRows.forEach((c) => {
            const key = String(c.LoanApplicationNo || "").trim();
            const appId = String(c.LoanApplicationID || c.ApplicationID || "").trim();

            buildJoinCandidates(key).forEach((k) => {
              if (!claimMapNorm[k]) {
                claimMapNorm[k] = { TotalClaimAmount: 0, TotalPaidClaim: 0, EMIPaidDate: "", ClaimStatus: "", ClaimNo: "" };
              }
            });
            if (appId && !claimMapByAppId[appId]) {
              claimMapByAppId[appId] = { TotalClaimAmount: 0, TotalPaidClaim: 0, EMIPaidDate: "", ClaimStatus: "", ClaimNo: "" };
            }
            const userId = String(c.UserID || c.UserId || c.LoginID || "").trim();
            if (userId && !claimMapByUserId[userId]) {
              claimMapByUserId[userId] = { TotalClaimAmount: 0, TotalPaidClaim: 0, EMIPaidDate: "", ClaimStatus: "", ClaimNo: "" };
            }

            buildJoinCandidates(key).forEach((k) => {
              mergeClaim(claimMapNorm[k], c);
            });
            mergeClaim(claimMapByAppId[appId], c);
            mergeClaim(claimMapByUserId[userId], c);
          });

          // Build Application lookup for LOI/sanction rows so fields like
          // scheme/caste/business/district can be filled from master app data.
          const appMasterByNoNorm = {};
          const appMasterById = {};
          appMasterSourceRows.forEach((row) => {
            const key = String(row.LoanApplicationNo || row.ApplicationNo || row.LoanApplicationNumber || row.AppNo || row.ApplicationNumber || "").trim();
            const appId = String(row.LoanApplicationID || row.ApplicationID || "").trim();
            buildJoinCandidates(key).forEach((k) => {
              if (!appMasterByNoNorm[k]) appMasterByNoNorm[k] = row;
            });
            if (appId) appMasterById[appId] = row;
          });

          // 1) Filter by selected date range
          const selectedRange = this.getSelectedDateRange();
          const rangeStart = selectedRange ? selectedRange.startDate : fyStart;
          const rangeEnd = selectedRange ? selectedRange.endDate : fyEnd;
          const effectiveRangeStart = companyStartDate && companyStartDate > rangeStart ? companyStartDate : rangeStart;
          all = all.filter((app) => {
            const raw = selectedType === "claims" || selectedType === "claim_applicant_wise"
              ? (app.PaidDt || app.PaidDate || app.AppliedForClaimDtd || app.EMIPaidDate || "")
              : (app.Createdate || app.CreateDate || app.ApplicationDate || app.LOIDate || app.RegeneratedOn || "");
            const dt = this.toDateFlexible(raw);
            return dt && dt >= effectiveRangeStart && dt <= rangeEnd;
          });

          // 2) Filter by caste codes (skip for Universal type companies)
          const selectedCompanyType = this.normalizeCompanyType(selectedCompany);
          if (this.reportTypeConfig.showSubCompany && selectedCompany && selectedCompanyType !== "universal" && all.length > 0) {
            const allowedCodeSet = {};
            const allowedNameSet = {};
            const allowedCasteIdSet = {};

            casteCodes
              .filter((c) => Number(c) > 0)
              .forEach((c) => {
                allowedCodeSet[Number(c)] = true;
              });

            casteNames
              .map((name) => this.normalizeCasteText(name))
              .filter((name) => name !== "")
              .forEach((name) => {
                allowedNameSet[name] = true;
              });

            (this.casteOptions || []).forEach((opt) => {
              const normalizedText = this.normalizeCasteText(opt && opt.text);
              const casteId = String((opt && opt.value) || "").trim().toLowerCase();
              if (casteId && normalizedText && allowedNameSet[normalizedText] === true) {
                allowedCasteIdSet[casteId] = true;
              }
            });

            const isSpecializedCompany = selectedCompanyType === "specialized";

            all = all.filter((app) => {
              const appNo = String(app.LoanApplicationNo || "").trim();
              const appId = String(app.LoanApplicationID || app.ApplicationID || "").trim();
              const appMaster = buildJoinCandidates(appNo).map((k) => appMasterByNoNorm[k]).find(Boolean) || appMasterById[appId];
              const appForCaste = appMaster ? Object.assign({}, app, appMaster) : app;

              // Specialized-company reports must only include rows whose caste can be
              // verified against the selected company's caste list.
              if (!this.hasAnyCastePayload(appForCaste)) {
                return false;
              }
              return this.rowMatchesCompanyCaste(appForCaste, allowedCodeSet, allowedNameSet, {
                enforceNameOnly: isSpecializedCompany,
                allowedCasteIdSet: allowedCasteIdSet
              });
            });

            // Final safety guard for specialized companies: exact caste-name match
            // plus company effective date in case upstream rows were sparsely populated.
            if (isSpecializedCompany) {
              all = this.enforceSpecializedCompanyRowGuard(all, selectedCompany);
            }
          }

          // 3) Normalize fields + Merge LOI + Claim data
          all = all.map((app) => {
            const appNo = String(app.LoanApplicationNo || "").trim();
            const appId = String(app.LoanApplicationID || app.ApplicationID || "").trim();
            const appMaster = buildJoinCandidates(appNo).map((k) => appMasterByNoNorm[k]).find(Boolean) || appMasterById[appId];
            const loi = buildJoinCandidates(appNo).map((k) => loiMapNorm[k]).find(Boolean) || loiMapByAppId[appId];
            const userId = String(app.UserID || app.LoginID || app.UserLoginID || "").trim();
            const claim = buildJoinCandidates(appNo).map((k) => claimMapNorm[k]).find(Boolean)
              || claimMapByAppId[appId]
              || claimMapByUserId[userId];
            const claimRef = (claim && (claim.ClaimNo || claim.ClaimNumber || claim.ClaimApplicationNo)) || app.ClaimNo || app.ClaimNumber || app.ClaimApplicationNo || app.ClaimEMIID || "";

            return {
              // Base fields — try multiple possible API key names
              CasteMarathi:      pickTextAcross(
                [app, appMaster, loi],
                ["CasteMarathi", "CasteName", "Caste", "SubCaste", "SubCasteName"],
                casteNameById[
                  String(
                    pick(app, ["CasteID", "CasteCode", "SubCasteID", "casteId", "casteCode"], pick(appMaster || {}, ["CasteID", "CasteCode", "SubCasteID", "casteId", "casteCode"], pick(loi || {}, ["CasteID", "CasteCode", "SubCasteID", "casteId", "casteCode"], "")))
                  ).trim()
                ] || ""
              ),
              AadhaarNo:         pickTextAcross([app, appMaster, loi], ["AddharCardNumber", "AdharNo", "AadhaarNo"], ""),
              GenderRaw:         pick(
                app,
                ["Gender", "gender", "GenderID", "genderId", "Sex", "sex", "GenderName", "genderName", "ApplicantGender", "applicantGender", "UserGender", "userGender", "UserDetailGender", "userDetailGender", "GenderMarathi", "genderMarathi", "GenderType", "genderType", "MaleFemale", "maleFemale"],
                pick(
                  appMaster || {},
                  ["Gender", "gender", "GenderID", "genderId", "Sex", "sex", "GenderName", "genderName", "ApplicantGender", "applicantGender", "UserGender", "userGender", "UserDetailGender", "userDetailGender", "GenderMarathi", "genderMarathi", "GenderType", "genderType", "MaleFemale", "maleFemale"],
                  pick(loi || {}, ["Gender", "gender", "GenderID", "genderId", "Sex", "sex", "GenderName", "genderName", "ApplicantGender", "applicantGender", "UserGender", "userGender", "UserDetailGender", "userDetailGender", "GenderMarathi", "genderMarathi", "GenderType", "genderType", "MaleFemale", "maleFemale"], "")
                )
              ),
              Gender:            this.getNormalizedGenderFromRow(
                Object.assign({}, loi || {}, appMaster || {}, app)
              ),
              BusinessType:      pickTextAcross([app, appMaster, loi], ["BusinessType", "Business", "BusinessName", "ProductfromOrganization", "ProductFromOrganization"], ""),
              SchemeType:        this.resolveSchemeNameFromRow(
                app,
                this.resolveSchemeNameFromRow(
                  appMaster || {},
                  // Empty fallback (not selectedLoanType) so getClaimLoanType cannot infer the
                  // selected loan type for claims that carry no real scheme/type of their own.
                  this.resolveSchemeNameFromRow(loi || {}, "")
                )
              ),
              DistrictID:        pick(app, ["DistrictID", "DistrictId", "DistID", "LoanDistrict"], pick(appMaster || {}, ["DistrictID", "DistrictId", "DistID", "LoanDistrict"], "")),
              DistrictName:      pickTextAcross([app, appMaster, loi], ["DistrictName", "District", "DistName", "DistrictNm"], "")
                                 || districtNameById[pick(app, ["DistrictID", "DistrictId", "DistID", "LoanDistrict"], pick(appMaster || {}, ["DistrictID", "DistrictId", "DistID", "LoanDistrict"], ""))]
                                 || "",
              OfficeName:        pickTextAcross([app, appMaster, loi], ["OfficeName", "BranchName", "LoanBranch", "Branch", "Office", "SubOffice", "Division"], ""),
              UserID:            pick(app, ["UserID", "LoginID"], pick(appMaster || {}, ["UserID", "LoginID"], "")),
              // Preserve the authoritative loan type from the claim row itself so
              // getClaimLoanType does not fall back to the selected filter or inherit the
              // loan type of a joined individual application master.
              LoanType:          pick(app, ["LoanType", "LoanTypeID", "SchemeTypeID"], pick(claim || {}, ["LoanType", "LoanTypeID", "SchemeTypeID"], "")),
              LoanApplicationNo: app.LoanApplicationNo || (appMaster && (appMaster.LoanApplicationNo || appMaster.ApplicationNo || appMaster.LoanApplicationNumber)) || "",
              ApplicationID: pick(app, ["LoanApplicationID", "ApplicationID"], pick(appMaster || {}, ["LoanApplicationID", "ApplicationID"], "")),
              ApplicantName: pickTextAcross([app, appMaster, loi], ["ApplicantName", "FullName", "FirstName", "BeneficiaryName"], ""),
              MobileNumber:      pickTextAcross([app, appMaster, loi, claim], ["MobileNumber", "MobileNo", "Mobile", "ContactNo", "ContactNumber", "ApplicantMobile", "Phone", "PhoneNo"], ""),
              ADDRESS:           pick(app, ["ADDRESS", "Address", "FullAddress", "PermanentAddress", "Village", "Landmark", "ApplicantAddress", "CurrentAddress"], "")
                                 || pick(appMaster || {}, ["ADDRESS", "Address", "FullAddress", "PermanentAddress", "Village", "Landmark", "ApplicantAddress", "CurrentAddress"], "")
                                 || (loi ? pick(loi, ["ADDRESS", "Address", "FullAddress", "PermanentAddress", "Village", "Landmark", "ApplicantAddress", "CurrentAddress"], "") : ""),
              Taluka:            pick(app, ["Taluka", "TalukaName", "TalukName", "Tehsil", "Tahsil", "BlockName"], "")
                                 || pick(appMaster || {}, ["Taluka", "TalukaName", "TalukName", "Tehsil", "Tahsil", "BlockName"], "")
                                 || (loi ? pick(loi, ["Taluka", "TalukaName", "TalukName", "Tehsil", "Tahsil", "BlockName"], "") : ""),
              ApplicationDate:   pick(app, ["ApplicationDate", "Createdate", "CreateDate", "CreatedDate"], ""),
              AmountDemanded:    app.AmountDemanded || (appMaster && appMaster.AmountDemanded) || 0,
              ApplicationStatusID: pick(app, ["ApplicationStatusID", "ApplicationStatusId", "StatusID", "ApprovalStatusID"], pick(appMaster || {}, ["ApplicationStatusID", "ApplicationStatusId", "StatusID", "ApprovalStatusID"], 0)),
              ApplicationStatus: pick(app, ["ApplicationStatus", "Status", "ApprovalStatus"], pick(appMaster || {}, ["ApplicationStatus", "Status", "ApprovalStatus"], pick(loi || {}, ["ApplicationStatus", "Status", "ApprovalStatus", "LoanStatus", "ClaimStatus"], ""))),
              ApplicationStatus1: pick(app, ["ApplicationStatus1", "ApprovalStatus", "LastStatusDate"], pick(appMaster || {}, ["ApplicationStatus1", "ApprovalStatus", "LastStatusDate"], pick(loi || {}, ["ApplicationStatus1", "ApprovalStatus", "LastStatusDate", "Status", "UpdatedDate"], ""))),
              ClaimNo:           claimRef,

              // LOI fields
              AmountApproved:    pickNumberAcross(
                [loi, app, appMaster],
                [
                  "AmountApproved", "ApprovedAmount", "SanctionAmount", "HOApprovedAmount", "TotalApprovedAmount",
                  "SanctionedAmount", "AmountSanctioned", "LoanAmountApproved", "DisbursementAmount", "DisburmentAmount"
                ],
                0
              ),
              LOINumber:         pickText(
                loi || app,
                [
                  "LOINumber", "LoiNumber", "LOINo", "LoiNo", "SanctionLetterNo", "SanctionNo", "LOI_No",
                  "LoanSanctionLetterNo", "LetterNo", "LoanApplicationNo"
                ],
                ""
              ),
              LOIDate:           pickText(
                loi || app,
                [
                  "LOIDate", "LoiDate", "SanctionDate", "IssueDate", "LOI_Date", "UpdatedDate", "CreatedDate",
                  "RecordCreatedDate", "ApplicationDate", "LoanDate"
                ],
                ""
              ),
              LOIDueDate:        pickText(
                loi || app,
                [
                  "LOIDueDate", "LoiDueDate", "DueDate", "LOIExpiryDate", "ExpiryDate", "ValidUpto", "ValidUpTo",
                  "LOIValidUpto", "LOIValidTill", "LoanDueDate"
                ],
                ""
              ),
              BankName:          pickTextAcross(
                [loi, app, appMaster, claim],
                [
                  "BankName", "Bank", "BankNm", "BankNameMarathi", "BankNameEnglish", "LoanBankName", "BranchName", "LoanBranch"
                ],
                ""
              ),
              Demand:            pickNumber(
                loi || app,
                ["Demand", "AmountDemanded", "DisbursementAmount", "DisburmentAmount", "SanctionAmount", "ApprovedAmount"],
                0
              ),

              // Claim fields
              ClaimAmount:       pickNumber(
                claim || app,
                ["TotalClaimAmount", "ClaimAmount", "ClaimPaidAmt", "ApprovedAmount", "InterestAmt", "EMIPaidAmt", "TotalApprovedAmount"],
                0
              ),
              AppliedForClaimDtd: pickText(
                claim || app,
                ["PaidDate", "ClaimDate", "AppliedForClaimDtd", "EMIPaidDate", "UpdatedDate", "CreatedDate"],
                ""
              ),
              PaidAmt:           pickNumber(
                claim || app,
                ["ClaimPaidAmt", "PaidAmt", "PaidAmount", "ApprovedAmount"],
                0
              ),
              Claim:             pickText(claim || app, ["Claim", "ClaimStatus", "Status", "ApplicationStatus", "ClaimPaymentStatus"], ""),
              PaidDt:            pickText(claim || app, ["PaidDt", "PaidDate", "EMIPaidDate", "ClaimDate", "UpdatedDate"], ""),
              BeneficiaryType:   pick(app, ["BeneficiaryType", "OldNewBeneficiary", "IsOldBeneficiary", "ExistingLoanBeneficiary"], ""),
              BeneficiaryFirstDate: pick(app, ["BeneficiaryFirstDate", "FirstApplicationDate"], ""),
            };
          });

          // Build beneficiary historical first-date inside current fetched dataset
          // so old/new split remains available even when backend doesn't send it.
          const firstDateByBeneficiary = {};
          all.forEach((row) => {
            const key = String(row.UserID || row.AadhaarNo || row.Mobile || row.LoanApplicationNo || "").trim();
            const dt = this.toDateSafe(row.ApplicationDate);
            if (!key || !dt) return;
            if (!firstDateByBeneficiary[key] || dt < firstDateByBeneficiary[key]) {
              firstDateByBeneficiary[key] = dt;
            }
          });
          all = all.map((row) => {
            const key = String(row.UserID || row.AadhaarNo || row.Mobile || row.LoanApplicationNo || "").trim();
            if (!row.BeneficiaryFirstDate && key && firstDateByBeneficiary[key]) {
              row.BeneficiaryFirstDate = firstDateByBeneficiary[key];
            }
            return row;
          });

          if (["claims", "claim_applicant_wise"].indexOf(selectedType) !== -1 && Number(this.selectedLoanType || 0) > 0) {
            const selectedLoanType = Number(this.selectedLoanType);
            all = all.filter((row) => this.getClaimLoanType(row) === selectedLoanType);
          }

          this.buildOfficeOptionsFromRows(all);

          let filteredRows = this.applyReportFilters(all);
          const claimLoanTypeFilter = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(selectedType) !== -1 && Number(this.selectedLoanType || 0) > 0;
          if (claimLoanTypeFilter) {
            const selectedLoanType = Number(this.selectedLoanType);
            filteredRows = filteredRows.filter((row) => this.getClaimLoanType(row) === selectedLoanType);
          }
          // Mirror Consolidation Summary paid-claim logic: strict paid-date range on the
          // real paid date, same dedup key and positive paid-amount guard so the claim
          // count/total match the summary report.
          if (["claims", "claim_applicant_wise"].indexOf(selectedType) !== -1) {
            const claimStart = this.toDateFlexible(this.dateFrom);
            const claimEnd = this.toDateFlexible(this.dateTo);
            if (claimEnd) claimEnd.setHours(23, 59, 59, 999);
            const seenClaims = {};
            filteredRows = filteredRows.filter((row) => {
              const paymentDate = this.toDateFlexible(this.getClaimPaidDate(row));
              if (!paymentDate || (claimStart && paymentDate < claimStart) || (claimEnd && paymentDate > claimEnd)) return false;
              const claimKey = this.getClaimDedupKey(row);
              if (claimKey && seenClaims[claimKey]) return false;
              if (claimKey) seenClaims[claimKey] = true;
              return this.getClaimPaidAmount(row) > 0;
            });
          }
          filteredRows = this.enforceSpecializedCompanyRowGuard(filteredRows, selectedCompany);
          if (selectedType === "loi" || selectedType === "sanction_letters") {
            filteredRows = filteredRows.filter((row) => {
              const hasLoiNumber = String(row.LOINumber || "").trim() !== "";
              const hasLoiDate = !!this.toDateFlexible(row.LOIDate || row.IssueDate || row.SanctionDate || "");
              const hasBank = String(row.BankName || row.BranchName || row.LoanBranch || "").trim() !== "";
              const hasLoiStatusSignal = this.shouldCountAsLoi(row);
              return hasLoiNumber || hasLoiDate || hasBank || hasLoiStatusSignal;
            });
          }
          filteredRows = this.applyNotKnownFallback(filteredRows);
          const groupedSourceRows = selectedType === "financial_year_wise"
            ? filteredRows.map((row) => Object.assign({}, row, { ClaimAmount: 0, PaidAmt: 0 }))
            : filteredRows;
          const displayRows = this.isGroupedReportType(selectedType)
            ? this.buildGroupedRows(groupedSourceRows, selectedType)
            : filteredRows;
          if (selectedType === "financial_year_wise") {
            this.reportData = this.mergeFinancialYearClaimTotals(displayRows, claimRows);
          }
          this.reportBaseRows = filteredRows;
          this.tableFields = this.withReportDateFormatters(this.getTableFieldsForReportType(selectedType));
          if (selectedType !== "financial_year_wise") {
            this.reportData = displayRows;
          }
          this.totalRows  = selectedType === "financial_year_wise" ? this.reportData.length : displayRows.length;
          this.loiStatusRows = filteredRows.map((row, idx) => ({
            SrNo: idx + 1,
            LoanApplicationNo: row.LoanApplicationNo || "",
            ApplicantName: row.ApplicantName || row.FullName || [row.FirstName, row.MiddleName, row.LastName].filter(Boolean).join(" "),
            DistrictName: row.DistrictName || row.District || "",
            OfficeName: row.OfficeName || row.BranchName || row.LoanBranch || "",
            SchemeType: this.normalizeSchemeName(row.SchemeType || row.SchemeName || row.LoanTypeName || ""),
            LOINumber: row.LOINumber || "",
            LOIDate: row.LOIDate || "",
            Status: this.shouldCountAsLoi(row) ? "Generated" : "Pending",
            ApplicationID: row.ApplicationID || row.LoanApplicationID || "",
            UserID: row.UserID || "",
          }));
          this.loiStatusSummary = {
            generated: this.loiStatusRows.filter((r) => r.Status === "Generated").length,
            pending: this.loiStatusRows.filter((r) => r.Status === "Pending").length,
            total: this.loiStatusRows.length,
          };
          this.loiSummary = {
            count: filteredRows.filter((r) => this.shouldCountAsLoi(r)).length,
            amount: filteredRows.reduce((sum, r) => sum + (parseFloat(r.AmountApproved) || 0), 0),
          };
          this.prepareStats(filteredRows);

          if (selectedType === "caste_wise") {
            this.fetchStatsFromBackend();
          }

          // Keep single-scheme summary row strictly aligned with filtered totals.
          if (Array.isArray(this.schemeDetailRows) && this.schemeDetailRows.length === 1) {
            this.schemeDetailRows = this.schemeDetailRows.map((row) => ({
              ...row,
              Applications: this.totalApplications,
              LOIIssued: this.totalLOI,
              Claims: this.totalClaim,
              DemandedAmount: parseFloat(this.sumAmountDemanded) || 0,
              ApprovedAmount: parseFloat(this.sumAmountApproved) || 0,
              PaidAmount: parseFloat(this.sumPaidAmt) || 0,
            }));
          }

          // Keep top-level scheme-wise cards aligned with LOI overlay when backend MIS APIs are unavailable.
          if (Array.isArray(this.stats.schemeWise) && this.stats.schemeWise.length === 1) {
            this.stats.claimSplit = this.stats.claimSplit || { oldCount: 0, newCount: 0, oldAmount: 0, newAmount: 0 };
          }
          this.searched  = true;
          this.isLoading = false;
          this.currentPage = 1;

          this.fetchGenderStatsFromBackend();

          // Run enrichment/background calls after primary report render.
          const locationReportTypes = ["applications", "loi", "sanction_letters", "disbursements", "claims"];
          const shouldRunHeavyEnrichment = locationReportTypes.indexOf(selectedType) !== -1
            && filteredRows.length <= this.maxProfileEnrichmentRows;

          if (shouldRunHeavyEnrichment) {
            this.enrichGenderStatsFromUserDetails(filteredRows)
              .finally(() => {
                if (!this.shouldSkipCompanyStatsApi()) {
                  this.fetchStatsFromBackend();
                }
              });
            this.enrichLocationFieldsFromUserDetails(filteredRows);
          }

          const shouldEnrichClaims = ["claims", "claim_applicant_wise", "consolidation_summary"].indexOf(selectedType) !== -1;
          if (shouldEnrichClaims || filteredRows.length <= this.maxProfileEnrichmentRows) {
            this.enrichGenderStatsFromUserDetails(filteredRows, { refreshGenderGroupedReport: selectedType === "gender_wise" });
            if (!this.isGroupedReportType(selectedType)) {
              this.enrichLendingFieldsFromUserDetails(filteredRows);
            }
          }
        })
        .catch((err) => {
          if (requestId !== this.reportRequestId) {
            return;
          }
          logger.error("API Error:", err);
          this.searched  = true;
          this.isLoading = false;
          this.beneficiaryReportRows = [];
        });
    },

    onFiltered(filteredItems) {
      this.totalRows  = filteredItems.length;
      this.currentPage = 1;
    },

    exportToExcel() {
      if (this.reportData.length === 0) return;

      const companyName = this.selectedCompany ? this.selectedCompany.name : (String(this.selectedReportType).toLowerCase() === "company_wise" ? "CompanyWiseReport" : "SubCompany");
      const range = this.getSelectedDateRange();
      const dateLabel = range ? `${range.startText}_to_${range.endText}` : (this.selectedYear || "");
      const filename = companyName + "_" + dateLabel + ".xlsx";
      const selectedDistrict = this.getSelectedDistrictName() || "All";
      const selectedOffice = this.selectedOffice || "All";
      const selectedSchemeObj = (this.loanTypeOptions || []).find((x) => Number(x.value) === Number(this.selectedLoanType));
      const selectedLoanTypeText = selectedSchemeObj ? selectedSchemeObj.text : "All";
      const selectedSchemeText = this.selectedSchemeName && this.selectedSchemeName !== "All"
        ? this.selectedSchemeName
        : selectedLoanTypeText;

      const selectedType = String(this.selectedReportType || "applications").toLowerCase();
      // Export the same normalized rows currently rendered in the report table.
      const selectedLoanTypeForExport = Number(this.selectedLoanType || 0);
      const sourceRows = ["claims", "claim_applicant_wise"].indexOf(selectedType) !== -1 && selectedLoanTypeForExport > 0
        ? (this.reportData || []).filter((row) => this.getClaimLoanType(row) === selectedLoanTypeForExport)
        : this.reportData;
      const exportFields = (this.tableFields || []).filter((field) => field && field.key && field.key !== "Sr");
      const exportKeys = exportFields.map((field) => String(field.key));
      const exportHeaders = ["Sr. No."].concat(exportFields.map((field) => field.label || field.key));
      const exportLabelByKey = {};
      exportFields.forEach((field) => {
        exportLabelByKey[String(field.key)] = field.label || field.key;
      });
      const exportData = (sourceRows || []).map((row, idx) => {
        const exportRow = { "Sr. No.": idx + 1 };
        exportKeys.forEach((key) => {
          const value = row[key];
          exportRow[exportLabelByKey[key]] = value === undefined || value === null
            ? ""
            : this.formatReportDateField(typeof value === "object" ? JSON.stringify(value) : value, key);
        });
        return exportRow;
      });

      const wb = XLSX.utils.book_new();

      // Detail data sheet
      const ws_data = [
        ["MAHARASHTRA RAJYA ITAR MAGASVARGIYA VITTA ANI VIKAS MAHAMANDAL LTD."],
        [companyName + " — Date Range: " + (range ? `${range.startText} to ${range.endText}` : "All")],
        ["Scheme: " + selectedSchemeText + " | District: " + selectedDistrict + " | Office: " + selectedOffice],
      ];
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      // Merge title rows across columns
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: Math.max(exportHeaders.length - 1, 1) } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: Math.max(exportHeaders.length - 1, 1) } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: Math.max(exportHeaders.length - 1, 1) } },
      ];
      // Append data rows starting at row 5 (A5)
      XLSX.utils.sheet_add_json(ws, exportData, {
        header: exportHeaders,
        skipHeader: false,
        origin: "A5"
      });

      // Add totals row at the end of data
      const dataEndRow = exportData.length + 5; // 3 title rows + 1 header row + data rows
      const totalByKey = {
        AmountDemanded: this.sumAmountDemanded,
        AmountApproved: this.sumAmountApproved,
        Demand: this.sumDemand,
        ClaimAmount: this.sumClaimAmount,
        PaidAmt: this.sumPaidAmt,
      };
      const totalsRow = exportHeaders.map((header, index) => {
        if (index === 0) return "TOTAL";
        const field = exportFields[index - 1];
        const key = field && String(field.key);
        return totalByKey[key] === undefined ? "" : totalByKey[key];
      });
      XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: "A" + (dataEndRow + 1) });

      XLSX.utils.book_append_sheet(wb, ws, "Report");

      // ===== Summary Sheet =====
      const summaryData = [
        ["MAHARASHTRA RAJYA ITAR MAGASVARGIYA VITTA ANI VIKAS MAHAMANDAL LTD."],
        [companyName + " — Date Range: " + (range ? `${range.startText} to ${range.endText}` : "All") + " — SUMMARY"],
        ["Scheme: " + selectedSchemeText + " | District: " + selectedDistrict + " | Office: " + selectedOffice],
        [],
        ["Description", "Count / Amount"],
        ["Total Applications", this.totalApplications],
        ["Total LOI Issued", this.totalLOI],
        ["Total Amount Demanded", parseFloat(this.sumAmountDemanded)],
        ["Total Amount Approved", parseFloat(this.sumAmountApproved)],
      ];
      if (this.showClaimMetrics || this.showFinancialYearPaidMetrics) {
        summaryData.push(["Total Claims", this.totalClaim]);
        summaryData.push(["Total Paid Amount", parseFloat(this.sumPaidAmt)]);
      }
      const ws2 = XLSX.utils.aoa_to_sheet(summaryData);
      ws2["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: 1 } },
      ];
      ws2["!cols"] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Summary");

      // ===== Statistics Sheet (MD instruction coverage) =====
      const statRows = [];
      const pushRows = (title, rows) => {
        statRows.push([title, "Count"]);
        if (!rows || rows.length === 0) {
          statRows.push(["(No Data)", 0]);
        } else {
          rows.forEach((r) => {
            statRows.push([r.label, r.count]);
          });
        }
        statRows.push(["", ""]);
      };

      statRows.push(["Statistical Details as per MD Instructions", ""]);
      statRows.push(["Company", companyName]);
      statRows.push(["Date Range", range ? `${range.startText} to ${range.endText}` : "All"]);
      statRows.push(["Scheme", selectedSchemeText]);
      statRows.push(["District", selectedDistrict]);
      statRows.push(["Office", selectedOffice]);
      statRows.push(["", ""]);

      pushRows("Scheme-wise", this.stats.schemeWise);
      pushRows("Application Status-wise", this.stats.applicationStatusWise);
      pushRows("Year-wise", this.stats.yearWise);
      pushRows("Caste-wise", this.stats.casteWise);
      pushRows("Gender-wise", this.stats.genderWise);
      pushRows("Region-wise", this.stats.districtWise);

      statRows.push(["Old Beneficiary", this.stats.beneficiarySplit.oldCount]);
      statRows.push(["New Beneficiary", this.stats.beneficiarySplit.newCount]);
      statRows.push(["Old Claims", this.stats.claimSplit.oldCount]);
      statRows.push(["New Claims", this.stats.claimSplit.newCount]);
      statRows.push(["Old Claims Amount", Number(this.stats.claimSplit.oldAmount || 0).toFixed(2)]);
      statRows.push(["New Claims Amount", Number(this.stats.claimSplit.newAmount || 0).toFixed(2)]);

      const ws3 = XLSX.utils.aoa_to_sheet(statRows);
      ws3["!cols"] = [{ wch: 45 }, { wch: 20 }];
      ws3["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }];
      XLSX.utils.book_append_sheet(wb, ws3, "Statistics");

      const loiStatusRows = (this.loiStatusRows || []).map((r) => ({
        "Sr. No.": r.SrNo,
        "Application No": r.LoanApplicationNo || "",
        "Applicant": r.ApplicantName || "",
        "District": r.DistrictName || "",
        "Office": r.OfficeName || "",
        "Scheme": r.SchemeType || "",
        "LOI No.": r.LOINumber || "",
        "LOI Date": r.LOIDate || "",
        "Status": r.Status || "",
      }));
      const ws5 = XLSX.utils.json_to_sheet(loiStatusRows.length ? loiStatusRows : [{ "Sr. No.": "", "Application No": "No Data" }]);
      XLSX.utils.book_append_sheet(wb, ws5, "LOI Status");

      XLSX.writeFile(wb, filename);
    },
    openLoiVerification(row) {
      if (!row || !row.ApplicationID) return;
      this.$router.push({
        name: "VerifyLOI",
        params: {
          ApplicationID: row.ApplicationID,
          LOINumber: row.LOINumber || "0",
        },
        query: {
          UserID: row.UserID || "",
        },
      });
    },
  },

  mounted() {
    this.buildFinancialYears();
    this.initDateFilters();
    this.detectTenantAndFilterCompanies();
    this.applyCompanyStartDateToFilter();
    this.loadSchemeNames();
    this.loadStatusOptionsByReportType();
    this.loadBankOptions();
    this.loadCasteOptions();
    this.loadDistrictOptions().then(() => {
      this.prefetchOfficeOptions();
    });
  },
};
</script>

<style scoped>
.master-report-page {
  --mr-accent: #1e5aa5;
  --mr-accent-dark: #133a6d;
  --mr-border: #dce6f5;
  --mr-soft-bg: #f5f8fe;
}

.dash-heading {
  font-size: 20px;
  font-weight: 700;
  color: var(--mr-accent-dark);
  padding: 6px 0 2px;
  letter-spacing: 0.2px;
}

.report-filter-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.06);
  background: #ffffff;
  overflow: visible;
}

.filter-card-header {
  padding: 0.85rem 1.25rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

.filter-group {
  margin-bottom: 12px;
}

.filter-group label {
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.modern-input {
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  height: 36px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.modern-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

/* Modern Multi-Select Dropdown styling */
.modern-filter-dropdown /deep/ .btn {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  background-color: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 6px !important;
  color: #334155 !important;
  font-size: 13px !important;
  height: 36px !important;
  padding: 0.375rem 0.75rem !important;
  text-align: left !important;
  box-shadow: none !important;
}

.modern-filter-dropdown /deep/ .btn:hover,
.modern-filter-dropdown /deep/ .btn:focus {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12) !important;
}

.filter-display-val {
  font-size: 13px;
  color: #334155;
  font-weight: 500;
}

.chevron-icon {
  font-size: 11px;
}

.pill-count {
  font-size: 10.5px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: 600;
  background-color: #2563eb;
}

.modern-filter-menu {
  min-width: 290px;
  max-width: 350px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  z-index: 1050;
}

.search-prep {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  padding: 0 8px;
}

.filter-search-input {
  font-size: 12.5px;
  height: 32px;
  border-color: #cbd5e1;
}

.filter-search-input:focus {
  border-color: #3b82f6;
  box-shadow: none;
}

.btn-clear-search {
  height: 32px;
  padding: 0 8px;
  border-color: #cbd5e1;
  color: #64748b;
}

.filter-quick-bar {
  border-bottom: 1px dashed #e2e8f0;
  padding-bottom: 6px;
}

.filter-dropdown-scroll {
  max-height: 200px;
  overflow-y: auto;
  padding: 2px;
}

.filter-dropdown-scroll::-webkit-scrollbar {
  width: 5px;
}
.filter-dropdown-scroll::-webkit-scrollbar-track {
  background: #f8fafc;
}
.filter-dropdown-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.filter-item-row {
  padding: 5px 8px;
  border-radius: 4px;
  transition: background 0.12s;
  cursor: pointer;
  margin-bottom: 2px;
}

.filter-item-row:hover {
  background: #f1f5f9;
}

.filter-item-row.row-active {
  background: #eff6ff;
}

.filter-item-text {
  font-size: 12.5px;
  color: #1e293b;
  user-select: none;
  margin-left: 4px;
}

/* Action Bar */
.filter-action-bar {
  border-top: 1px solid #e2e8f0;
}

.action-btn-search {
  background: linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%);
  border: none;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 6px 18px;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}

.action-btn-search:hover:not(:disabled) {
  background: linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%);
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.35);
}

.action-btn-reset {
  font-weight: 600;
  border-radius: 6px;
  padding: 6px 14px;
  border-color: #cbd5e1;
  color: #475569;
}

.action-btn-reset:hover {
  background-color: #f1f5f9;
  color: #1e293b;
  border-color: #94a3b8;
}

.master-report-page .card {
  border-radius: 10px;
}

.master-report-page .table th {
  white-space: nowrap;
}

.stat-title {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #2e4b90;
}

.stat-row {
  font-size: 12px;
  line-height: 1.5;
}

.report-stats-card {
  border: 1px solid #d9e5f8;
  background: linear-gradient(135deg, #f8fbff 0%, #f4f8ff 100%);
  box-shadow: 0 10px 22px rgba(19, 58, 109, 0.08);
}

.stats-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #dce6f5;
  padding-bottom: 10px;
}

.stats-title {
  font-size: 16px;
  font-weight: 700;
  color: #143d72;
}

.stats-subtitle {
  font-size: 12px;
  color: #4f6282;
  margin-top: 2px;
}

.stats-kpis {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.kpi-chip {
  background: #fff;
  border: 1px solid #d4e1f6;
  border-radius: 8px;
  min-width: 96px;
  padding: 6px 10px;
  text-align: center;
}

.kpi-label {
  font-size: 11px;
  color: #557096;
  line-height: 1.2;
}

.kpi-value {
  font-size: 16px;
  font-weight: 700;
  color: #173f75;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
}

.stat-panel {
  background: linear-gradient(180deg, #ffffff 0%, #f6fbff 100%);
  border: 1px solid #d7e8fb;
  border-radius: 10px;
  padding: 12px;
  box-shadow: 0 3px 12px rgba(21, 68, 122, 0.08);
}

.stat-panel-title {
  font-size: 13px;
  font-weight: 700;
  color: #204d86;
  margin-bottom: 8px;
}

.stat-row2 {
  margin-bottom: 6px;
}

.stat-row2-head {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  line-height: 1.3;
}

.stat-row2-label {
  color: #2f4463;
  max-width: 78%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-row2-value {
  color: #1f3f68;
  font-weight: 700;
}

.stat-row2-bar {
  height: 8px;
  border-radius: 999px;
  background: #e7f0fc;
  margin-top: 4px;
  overflow: hidden;
}

.stat-row2-bar span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #1268bd, #53b0f2);
  animation: growBar 420ms ease-out;
}

@keyframes growBar {
  from {
    width: 0;
  }
}

.simple-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px dashed #e4ecfa;
}

.simple-row:last-child {
  border-bottom: none;
}

@media (max-width: 768px) {
  .stats-header {
    flex-direction: column;
  }

  .stats-kpis {
    width: 100%;
  }

  .kpi-chip {
    flex: 1;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AdminReports/SubCompanyReport.vue